import { startNotificationPrefetch } from '../components/NotificationBell';
import { formatHM } from '../utils/formatHours';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { wantsReturnToProfile, clearReturnToProfile } from '../utils/returnToProfile';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import nlLocale from '@fullcalendar/core/locales/nl';
import frLocale from '@fullcalendar/core/locales/fr';
import SmartStaysLogo from '../components/SmartStaysLogo';
import PropertyCalendar from '../components/PropertyCalendar';
import PropertyTimeline from '../components/PropertyTimeline';
import CleaningInfoModal from '../components/CleaningInfoModal';
import FilteredTimeline from '../components/FilteredTimeline';
import NotificationBell from '../components/NotificationBell';
import ProfileMenu from '../components/ProfileMenu';
import Avatar from '../components/Avatar';
import { isNativeApp, useNativeShell } from '../utils/nativeApp';
import NativeScreenTitle from '../components/NativeScreenTitle';
import API_URL from '../config';
import { openCleaningPdf } from '../utils/openCleaningPdf';
import { getAuthToken, getAuthUser, isImpersonating, stopImpersonating } from '../utils/authSession';
import { validateRequiredFields, clearFieldInvalid } from '../utils/formValidation';
import './Admin.css';
import { sortedNames, compareNames } from '../utils/sortNames';
import useMonthSwipe from '../utils/useMonthSwipe';
import useStableMonthHeight from '../utils/useStableMonthHeight';
import { toEmailInput, emailInputProps } from '../utils/emailInput';

// Kleine, herbruikbare icoontjes voor de "Acties"-kolom (wijzigen/verwijderen)
// - vervangen de vroegere tekstlinks door compacte symbooltjes, zelfde
// pictogramstijl (dunne lijnen, currentColor) als de lege-staat-iconen
// verderop in dit bestand.
const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m3 0-.8 12.1A2 2 0 0 1 15.2 21H8.8a2 2 0 0 1-2-1.9L6 7h12ZM10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Herstel-icoontje (gebogen pijl) voor "Heractiveren" - enkel getoond bij
// gebruikers die zelf hun account-verwijdering aanvroegen (deletionRequestedAt),
// zodat de admin die aanvraag kan afwijzen en het account terug bruikbaar
// maken i.p.v. het te verwijderen.
const RestoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12a9 9 0 1 0 3-6.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 4v5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Kalender-icoontje voor "kalender openen" - vervangt de vroegere tekstknop.
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 9H21M8 2V6M16 2V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// Tandwiel-icoontje voor "Standaardinstellingen toepassen" (Nuki LED/autolock
// defaults) - vervangt de vroegere tekstknop, tussen wijzigen/verwijderen.
const GearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.14.6.6 1.08 1.51 1.08H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
  </svg>
);

// Pijltje voor de uitklap-knop bij Te factureren/Uitbetaling (zie
// payout-group/invoicing-group hieronder) - draait 180° via CSS zodra de
// groep opengeklapt staat.
const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Vaste schoonmaakprijs per type/grootte woning - moet exact overeenkomen met
// de types/prijzen op de publieke Pricing-pagina, "Schoonmaak"-tab
// (pricing.cleaningTable, zie Pricing.js "cleaningRows"), want dat is de
// enige juiste bron voor welke woningtypes bestaan. "type5" (Overige) heeft
// bewust geen vaste prijs (op aanvraag), zie hasCustomPrice-afhandeling.
const PROPERTY_TYPE_PRICES = {
  hotelRoom: 45,
  type2: 70,
  type3: 95,
  type4: 120,
};

// Vaste kleur per dienst, dezelfde themakleuren als de tabbladen op de
// publieke Aanbod-pagina (zie Pricing.js "TABS") - zo blijft "Toegangsbeheer
// = groen", "Schoonmaak = blauw", "Automation = donkerblauw" overal
// consistent. Setup krijgt een apart amberkleurtje (geen eigen tabkleur
// nodig, want Setup's tabkleur (#1b5e20) valt te dicht bij Toegangsbeheer).
const SERVICE_COLORS = {
  setup: '#f59e0b',
  access: '#43a047',
  cleaning: '#1e88e5',
  automation: '#0d47a1',
};

// Bouwt een CSS conic-gradient-string voor een simpel taartdiagram, puur uit
// {value,color}-segmenten - geen SVG-arc-wiskunde of externe chart-library
// nodig. Segmenten met waarde 0 (of kleiner) worden overgeslagen zodat ze
// geen onzichtbaar "leeg" plakje innemen. total <= 0 (nog geen omzet deze
// maand) geeft gewoon een effen lichtgrijze cirkel terug.
const buildConicGradient = (segments) => {
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  const withValue = segments.filter((s) => s.value > 0);
  if (total <= 0 || withValue.length === 0) return '#e2e8f0';
  let cursor = 0;
  const stops = withValue.map((s) => {
    const start = (cursor / total) * 360;
    cursor += s.value;
    const end = (cursor / total) * 360;
    return `${s.color} ${start}deg ${end}deg`;
  });
  return `conic-gradient(${stops.join(', ')})`;
};

// Genereert n visueel goed uit elkaar liggende kleuren voor de top-20-
// klanten-taart (waar, anders dan bij de 4 vaste diensten hierboven, het
// aantal segmenten en welke klant welk segment krijgt allebei variabel zijn).
// De "golden angle" (~137.5°) zorgt ervoor dat opeenvolgende tinten elkaar
// nooit te dicht benaderen, ook niet bij 20 segmenten.
const goldenAngleColor = (index) => `hsl(${Math.round((index * 137.508) % 360)}, 62%, 50%)`;

// Simpel, herbruikbaar donut-diagram (CSS conic-gradient + witte cirkel in
// het midden voor het "gat") met legende ernaast - gebruikt voor zowel de
// omzet-per-dienst- als de top-20-klanten-taart hieronder op het admin-
// Dashboard-tabblad. "totalLabel" verschijnt in het midden van de donut.
const DonutChart = ({ segments, totalLabel, formatValue, emptyText }) => {
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  return (
    <div className="donut-chart-row">
      <div className="donut-chart" style={{ background: buildConicGradient(segments) }}>
        <div className="donut-chart-hole">
          <span className="donut-chart-hole-value">{formatValue(total)}</span>
          {totalLabel && <span className="donut-chart-hole-label">{totalLabel}</span>}
        </div>
      </div>
      <div className="donut-chart-legend">
        {total <= 0 ? (
          <p className="donut-chart-empty">{emptyText}</p>
        ) : (
          segments.filter((s) => s.value > 0).map((s) => (
            <div className="donut-chart-legend-row" key={s.key}>
              <span className="donut-chart-legend-dot" style={{ background: s.color }}></span>
              <span className="donut-chart-legend-label">{s.label}</span>
              <span className="donut-chart-legend-value">{formatValue(s.value)}</span>
              <span className="donut-chart-legend-pct">{Math.round((s.value / total) * 100)}%</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Automatische boekingen komen binnen via 1 van 3 aanvinkbare bronnen:
// Guesty (iCal-link en/of API-listing-id), Hospitable (iCal-link) of
// Rechtstreeks (losse Airbnb-/Booking.com-iCal-links, elk optioneel).
// Let op bij "Rechtstreeks": die feeds van Airbnb/Booking.com zelf bevatten
// geen e-mailadres van de gast, waardoor de automatische toegangscode-mail
// NIET verstuurd kan worden voor boekingen via deze weg (zie ook de
// waarschuwing die hieronder bij die optie getoond wordt). Hospitable synct
// dezelfde kalenders wél mét e-mailadres en is daarom de aanbevolen weg -
// "Rechtstreeks" is enkel voor wie (nog) geen Hospitable-koppeling heeft.
// De 3 bronnen sluiten elkaar wederzijds uit (zie ook
// hasConflictingIcalSources in backend/index.js) - dat wordt hieronder
// afgedwongen door bij het aanvinken van de ene bron de andere meteen uit
// te vinken en leeg te maken.
const deriveSourceCheckboxes = (p) => ({
  useGuesty: Boolean(p?.guestyIcalUrl || p?.guestyListingId),
  useHospitable: Boolean(p?.hospitableIcalUrl),
  useDirect: Boolean(p?.airbnbIcalUrl || p?.bookingIcalUrl),
  useSheet: Boolean(p?.bookingSheetId),
});

const withGuestyToggle = (current, checked) => ({
  ...current,
  useGuesty: checked,
  useHospitable: checked ? false : current.useHospitable,
  useDirect: checked ? false : current.useDirect,
  useSheet: checked ? false : current.useSheet,
  hospitableIcalUrl: checked ? '' : current.hospitableIcalUrl,
  airbnbIcalUrl: checked ? '' : current.airbnbIcalUrl,
  bookingIcalUrl: checked ? '' : current.bookingIcalUrl,
  bookingSheetId: checked ? '' : current.bookingSheetId,
  guestyIcalUrl: checked ? current.guestyIcalUrl : '',
  guestyListingId: checked ? current.guestyListingId : '',
});

const withHospitableToggle = (current, checked) => ({
  ...current,
  useHospitable: checked,
  useGuesty: checked ? false : current.useGuesty,
  useDirect: checked ? false : current.useDirect,
  useSheet: checked ? false : current.useSheet,
  guestyIcalUrl: checked ? '' : current.guestyIcalUrl,
  guestyListingId: checked ? '' : current.guestyListingId,
  airbnbIcalUrl: checked ? '' : current.airbnbIcalUrl,
  bookingIcalUrl: checked ? '' : current.bookingIcalUrl,
  bookingSheetId: checked ? '' : current.bookingSheetId,
  hospitableIcalUrl: checked ? current.hospitableIcalUrl : '',
});

const withDirectToggle = (current, checked) => ({
  ...current,
  useDirect: checked,
  useGuesty: checked ? false : current.useGuesty,
  useHospitable: checked ? false : current.useHospitable,
  useSheet: checked ? false : current.useSheet,
  guestyIcalUrl: checked ? '' : current.guestyIcalUrl,
  guestyListingId: checked ? '' : current.guestyListingId,
  hospitableIcalUrl: checked ? '' : current.hospitableIcalUrl,
  bookingSheetId: checked ? '' : current.bookingSheetId,
  airbnbIcalUrl: checked ? current.airbnbIcalUrl : '',
  bookingIcalUrl: checked ? current.bookingIcalUrl : '',
});

// Vierde, onafhankelijke boekingsbron naast Guesty/Hospitable/Rechtstreeks:
// voor klanten die hun boekingen handmatig bijhouden in een Google Sheet
// (FROM/TO/STATUS-kolommen) i.p.v. via een platform met iCal-export - zie
// googleSheetsService.js en syncBookingSheet() in backend/index.js. Sluit,
// net als de 3 andere bronnen, de anderen uit bij het aanvinken.
const withSheetToggle = (current, checked) => ({
  ...current,
  useSheet: checked,
  useGuesty: checked ? false : current.useGuesty,
  useHospitable: checked ? false : current.useHospitable,
  useDirect: checked ? false : current.useDirect,
  guestyIcalUrl: checked ? '' : current.guestyIcalUrl,
  guestyListingId: checked ? '' : current.guestyListingId,
  hospitableIcalUrl: checked ? '' : current.hospitableIcalUrl,
  airbnbIcalUrl: checked ? '' : current.airbnbIcalUrl,
  bookingIcalUrl: checked ? '' : current.bookingIcalUrl,
  bookingSheetId: checked ? current.bookingSheetId : '',
});

// Herbruikbare editor voor de lijst datumbereiken van een "PARTIAL"-woning
// (occasionalCleaningRanges) - gebruikt in zowel de "Woning toevoegen"- als
// de "Woning bewerken"-modal. "ranges" is een array van {start, end}
// ('YYYY-MM-DD' strings); "end" mag leeg blijven bij het toevoegen als het
// om precies 1 dag gaat, en wordt dan hier gelijkgesteld aan "start" - de
// invoervelden blijven na het toevoegen gewoon staan zodat meteen een
// volgend bereik ingevoerd kan worden.
// Adres opgedeeld in straat, nummer, postcode en gemeente. In de database blijft het één tekstveld
// ("Straat 12, 9000 Gent"): dit onderdeel splitst de bestaande waarde bij het openen en voegt ze samen bij elke wijziging.
function parseAddress(value) {
  const raw = (value || '').trim();
  const out = { street: '', number: '', postcode: '', city: '' };
  if (!raw) return out;
  const parts = raw.split(',').map((x) => x.trim()).filter(Boolean);
  let left = parts[0] || '';
  let rest = '';
  if (parts.length >= 3) {
    out.street = parts[0];
    out.number = parts[1];
    rest = parts.slice(2).join(' ');
  } else {
    rest = parts[1] || '';
    const m = left.match(/^(.*?)[\s]+(\d+\s?[A-Za-z]?(?:\s*[/-]\s*\w+)?(?:\s+bus\s*\w+)?)$/i);
    if (m) { out.street = m[1].trim(); out.number = m[2].trim(); } else { out.street = left; }
  }
  const pm = rest.match(/^(\d{4,5})\s*(.*)$/);
  if (pm) { out.postcode = pm[1]; out.city = pm[2].trim(); } else { out.city = rest; }
  return out;
}

function composeAddress({ street, number, postcode, city }) {
  const line1 = [street, number].map((x) => (x || '').trim()).filter(Boolean).join(' ');
  const line2 = [postcode, city].map((x) => (x || '').trim()).filter(Boolean).join(' ');
  return [line1, line2].filter(Boolean).join(', ');
}

function AddressFields({ value, onChange, t }) {
  const [parts, setParts] = useState(() => parseAddress(value));
  const update = (key, v) => {
    const next = { ...parts, [key]: v };
    setParts(next);
    onChange(composeAddress(next));
  };
  return (
    <div className="address-fields">
      <label className="address-field address-field-street">
        <span>{t('admin.addressStreet')}</span>
        <input type="text" value={parts.street} onChange={(e) => update('street', e.target.value)} />
      </label>
      <label className="address-field address-field-number">
        <span>{t('admin.addressNumber')}</span>
        <input type="text" value={parts.number} onChange={(e) => update('number', e.target.value)} />
      </label>
      <label className="address-field address-field-postcode">
        <span>{t('admin.addressPostcode')}</span>
        <input type="text" inputMode="numeric" value={parts.postcode} onChange={(e) => update('postcode', e.target.value)} />
      </label>
      <label className="address-field address-field-city">
        <span>{t('admin.addressCity')}</span>
        <input type="text" value={parts.city} onChange={(e) => update('city', e.target.value)} />
      </label>
    </div>
  );
}

function OccasionalRangesEditor({ ranges, onChange, t }) {
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');

  const addRange = () => {
    if (!draftStart) return;
    const start = draftStart;
    const end = draftEnd || draftStart;
    if (new Date(start) > new Date(end)) return;
    onChange([...(ranges || []), { start, end }]);
    setDraftStart('');
    setDraftEnd('');
  };

  const removeRange = (idx) => {
    onChange((ranges || []).filter((_, i) => i !== idx));
  };

  return (
    <div className="occasional-ranges-editor">
      {ranges && ranges.length > 0 && (
        <div className="occasional-ranges-list">
          {ranges.map((r, idx) => (
            <div key={idx} className="occasional-range-chip">
              <span>{r.start === r.end ? r.start : `${r.start} → ${r.end}`}</span>
              <button type="button" onClick={() => removeRange(idx)}>×</button>
            </div>
          ))}
        </div>
      )}
      <div className="date-range-inputs">
        <input type="date" value={draftStart} onChange={(e) => setDraftStart(e.target.value)} />
        <span>{t('admin.occasionalRangeUntil')}</span>
        <input type="date" value={draftEnd} onChange={(e) => setDraftEnd(e.target.value)} />
        <button type="button" className="occasional-range-add-btn" onClick={addRange}>
          {t('admin.occasionalRangeAdd')}
        </button>
      </div>
      <p className="form-hint">{t('admin.occasionalRangeSingleDayHint')}</p>
    </div>
  );
}

function Admin() {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [unscheduledBookings, setUnscheduledBookings] = useState([]);
  const [filteredUnscheduledBookings, setFilteredUnscheduledBookings] = useState([]);
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [filteredScheduledTasks, setFilteredScheduledTasks] = useState([]);
  // Filter bij "Ingepland": 'all' (standaard) toont alles, 'open' toont enkel
  // taken die nog geen bevestigde schoonmaker hebben (OPEN/PENDING_CONFIRMATION/
  // WITHDRAWN - zelfde groepering als businessCalendarDayStats hieronder),
  // 'assigned' toont enkel taken met een bevestigde schoonmaker.
  const [scheduledStatusFilter, setScheduledStatusFilter] = useState('all');
  // Per-taak override om een rij in "Ingepland" open/dicht te klappen. Zonder
  // override valt dit terug op een slim standaardgedrag (zie isScheduledTaskExpanded):
  // taken die nog actie vragen (OPEN/PENDING_CONFIRMATION) staan standaard open,
  // volledig toegewezen taken staan standaard dicht - zo blijft het overzicht
  // per dag compact en overzichtelijk ("boekhoudstijl") in plaats van bombastisch.
  const [scheduledTaskExpandOverrides, setScheduledTaskExpandOverrides] = useState({});
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  // Actieve navigatietab bovenaan de "accommodatie bewerken"-modal - zelfde
  // 4 categorieën als waar de admin de velden nu op verwacht: Algemeen,
  // Toegangsbeheer, Schoonmaak, Automation. Reset telkens naar 'general' bij
  // het openen van de modal (zie openEditPropertyModal hieronder), zodat je
  // niet per ongeluk op de tab van de vorige woning blijft staan.
  const [editPropertyTab, setEditPropertyTab] = useState('general');
  // Bewerken van een ingepland schoonmaakmoment (datum/uur, notities,
  // toegangsinfo, verwacht aantal schoonmakers) - los van de al bestaande
  // inline overschrijf-cellen (uren/bedrag) bij Uitbetaling/Facturatie, zie
  // openEditTaskModal/handleEditTask hieronder.
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  // Volledig losstaande schoonmaak toevoegen vanuit "In te plannen" (zonder
  // gekoppelde onscheduled booking) - zelfde idee als de "+ Schoonmaak
  // toevoegen"-knop in PropertyCalendar.js, maar dan met een eigen
  // woning-keuze omdat er hier geen vaste propertyId-context is. Bewust
  // eigen, onafhankelijke state (niet het approveTimes/approveAmounts-
  // patroon hergebruiken) - dat patroon is gekoppeld aan één bestaande
  // onscheduled booking per woning, dit hier niet.
  const [showAddCleaningModal, setShowAddCleaningModal] = useState(false);
  const [addingCleaning, setAddingCleaning] = useState(false);
  const [newCleaning, setNewCleaning] = useState({
    propertyId: '', date: '', startTime: '', endTime: '', cleaningServiceId: '', notes: '', access: '', cleanerCount: '',
  });
  const [schedulingProperty, setSchedulingProperty] = useState(null);
  // Van/tot-uur die de admin bij "In te plannen" kan aanpassen vóór het
  // klikken op "Goedkeuren" (zie handleApproveSchedule) - per woning
  // bijgehouden zodat je meerdere kaarten na elkaar kan afwerken zonder dat
  // ze elkaars invoer overschrijven. Standaard leeg: getApproveTime hieronder
  // valt dan terug op de woning z'n eigen cleaningWindowStart/End.
  const [approveTimes, setApproveTimes] = useState({});
  // Zelfde principe als approveTimes hierboven, maar dan voor de dag: stond
  // voorheen vast op de dag van de uitcheck (checkoutDate in
  // handleApproveSchedule), waardoor je bij "In te plannen" enkel het uur
  // kon aanpassen en niet de dag zelf. Standaard leeg: getApproveDate
  // hieronder valt dan terug op de uitcheckdag.
  const [approveDates, setApproveDates] = useState({});
  // Zelfde principe als approveTimes/approveDates hierboven, maar voor de 2
  // optionele bedragen (klantprijs, loon schoonmaker) die de admin al bij
  // "In te plannen" kan vastzetten - per woning { invoicePrice, payoutAmount }.
  const [approveAmounts, setApproveAmounts] = useState({});
  const [approvingPropertyId, setApprovingPropertyId] = useState(null);
  // Welke kalenderweergave getoond wordt zodra je op "Inboeken"/"Open" van een
  // woning klikt vanuit "In te plannen": standaard de multi-kalender (alle
  // woningen), met de optie om terug te schakelen naar de klassieke
  // maandkalender voor enkel die ene woning.
  const [scheduleView, setScheduleView] = useState('multi');
  // Onthoud de laatst actieve tab in localStorage zodat een refresh je niet
  // terugstuurt naar "In te plannen" (dit was een storend gedrag voor de admin).
  // Uitzondering: bij het (opnieuw) openen van het beheerpaneel in een nieuwe
  // browser-/app-sessie willen we wél altijd standaard op Boekhouding > In te
  // plannen terechtkomen. sessionStorage (i.p.v. een vlag die enkel Login.js
  // zet) is hiervoor de juiste plek: die overleeft een gewone refresh binnen
  // hetzelfde tabblad (zodat een refresh nog steeds je tab onthoudt), maar
  // wordt geleegd zodra het tabblad/de app-webview volledig gesloten en
  // opnieuw geopend wordt - ook wanneer de admin ingelogd blijft (token nog
  // geldig) en dus nooit het eigenlijke inlogformulier doorloopt.
  const VALID_ADMIN_TABS = ['to-schedule', 'scheduled', 'completed', 'payout', 'invoicing', 'users', 'properties', 'newsletter', 'demo'];
  const [activeTab, setActiveTabState] = useState(() => {
    if (sessionStorage.getItem('adminTabInitialized') !== 'true') {
      sessionStorage.setItem('adminTabInitialized', 'true');
      localStorage.setItem('adminActiveTab', 'to-schedule');
      return 'to-schedule';
    }
    const stored = localStorage.getItem('adminActiveTab');
    return VALID_ADMIN_TABS.includes(stored) ? stored : 'to-schedule';
  });
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    localStorage.setItem('adminActiveTab', tab);
  };
  // Uitklapbaar hamburgermenu voor de navbar op smallere schermen - vervangt
  // daar de rij tekstlinks (Kalender/Dashboard/Boekhouding/.../Demo) die
  // anders lelijk naar een 2de regel wrapte. Zelfde patroon als
  // Dashboard.js's mobileMenuOpen (dash-nav-toggle/dash-mobile-menu), maar
  // met eigen "admin-"-klassenamen om botsing met die andere, ongescoped
  // CSS-klassen te vermijden.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Enkel in de echte iOS/Android-app: onderste tabbalk met icoontjes i.p.v. het
  // hamburgermenu, en meldingen + profiel als eigen scherm (net als bij de
  // verhuurder). De website (ook op een telefoon) blijft ongewijzigd.
  const isNative = isNativeApp();
  useNativeShell(isNative);
  const [nativeScreen, setNativeScreen] = useState(() => (wantsReturnToProfile() ? 'profile' : null));
  useEffect(() => { clearReturnToProfile(); }, []); // null | 'notifications' | 'profile'
  const [nativeUnread, setNativeUnread] = useState(0);
  useEffect(() => startNotificationPrefetch(), []);

  useEffect(() => {
    const reset = () => setNativeUnread(0);
    window.addEventListener('smartstays:notifications-read', reset);
    return () => window.removeEventListener('smartstays:notifications-read', reset);
  }, []);
  useEffect(() => {
    if (!isNative) return undefined;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/notifications/unread-count`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        setNativeUnread(res.data.count || 0);
      } catch (e) { /* stil */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [isNative, nativeScreen]);
  // Woning waarvoor de multi-kalender vanuit de "Woningen"-tab geopend werd
  // (geen aparte navigatie-tab/navbar-knop meer - enkel bereikbaar via een
  // klik op een woning, of via de "Inboeken"-flow hierboven). null = de
  // gewone woningenlijst tonen.
  const [calendarViewProperty, setCalendarViewProperty] = useState(null);
  // 'single' = enkel de aangeklikte woning, 'owner' = alle woningen van
  // dezelfde eigenaar (klant) als die woning.
  const [calendarViewScope, setCalendarViewScope] = useState('single');
  // Schoonmaker waarvan de admin vanuit "Gebruikers" de persoonlijke kalender
  // wil raadplegen (zelfde kalenderblokjes/kleuren als de schoonmaker zelf
  // ziet in zijn/haar dashboard) - null = de gewone gebruikerslijst tonen.
  const [calendarViewCleaner, setCalendarViewCleaner] = useState(null);
  // Aangeklikte dag ('YYYY-MM-DD') in de per-schoonmaker kalender hieronder -
  // de dagcellen zelf tonen enkel het aantal taken, het overzicht van de
  // effectieve taken op die dag verschijnt pas na het aanklikken.
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(null);
  // Aangeklikte dag ('YYYY-MM-DD') in de nieuwe bedrijfsbrede "Kalender"-tab
  // (activeTab === 'calendar') - los van "calendarSelectedDate" hierboven, dat
  // enkel voor de per-schoonmaker kalender is.
  const [businessCalendarSelectedDate, setBusinessCalendarSelectedDate] = useState(null);
  // Schoonmaakmoment waarvan het infovenster openstaat (maandoverzicht).
  const [monthCleaningInfo, setMonthCleaningInfo] = useState(null);
  // 'month' = de bestaande FullCalendar-maandweergave met badges per dag,
  // 'multi' = de nieuwe doorzoekbare multikalender (zie businessMultiCalendar*
  // hieronder) die alle woningen als tijdlijn naast mekaar toont, zoals bij
  // Guesty - maar dan bedrijfsbreed i.p.v. per eigenaar.
  const [businessCalendarView, setBusinessCalendarView] = useState('month');
  // "In te plannen" in de app: elke boeking is een compacte kaart; het formulier
  // (datum, uren, dienst, notitie, knoppen) klapt pas open na een tik.
  const [openScheduleBookingId, setOpenScheduleBookingId] = useState(null);
  // Zoekterm voor de woningkiezer bij de multikalender (zie hieronder) - puur
  // lokaal, filtert enkel de kiezer zelf, niet de tijdlijn.
  const [businessMultiCalendarSearch, setBusinessMultiCalendarSearch] = useState('');
  // Id's van woningen die de admin uit de multikalender verwijderde (1 klik
  // op het kruisje) - standaard leeg, dus standaard staan ALLE woningen aan.
  // Een Set van verwijderde id's i.p.v. een lijst van zichtbare id's, zodat
  // een nieuw toegevoegde woning (net aangemaakt, of nog niet geladen bij het
  // eerste render) automatisch zichtbaar is zonder deze state opnieuw te
  // moeten initialiseren.
  const [businessMultiCalendarHiddenIds, setBusinessMultiCalendarHiddenIds] = useState(() => new Set());
  const toggleBusinessMultiCalendarProperty = (propertyId) => {
    setBusinessMultiCalendarHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(propertyId)) next.delete(propertyId);
      else next.add(propertyId);
      return next;
    });
  };
  // Instellingen voor de "Demo"-tab: hiermee stel je in welke abonnementen
  // een prospect zogezegd heeft, en /demo (los tabblad, geen login nodig)
  // toont dan een volledig interactieve maar volledig nep/lokale kopie van
  // het verhuurder-dashboard met die instellingen - handig om tijdens een
  // verkoopgesprek te tonen zonder een echt account te moeten aanmaken.
  const [demoConfig, setDemoConfig] = useState({ access: true, automation: true, tier: 'FULL', count: 2, device: 'desktop' });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  // Rol-filter bovenaan de gebruikerstabel: 'ALL' toont iedereen, anders enkel
  // die ene rol (klanten/verhuurders, schoonmakers of admins).
  const [userRoleFilter, setUserRoleFilter] = useState('ALL');
  // Filter op aanmaakdatum van het account: 'ALL', aantal dagen ('7', '30', '90', '365') of 'CUSTOM' (van/tot).
  const [userCreatedFilter, setUserCreatedFilter] = useState('ALL');
  const [userCreatedFrom, setUserCreatedFrom] = useState('');
  const [userCreatedTo, setUserCreatedTo] = useState('');
  // Mobiele app: welke gebruiker (kaartje) momenteel uitgeklapt is.
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [expandedPropertyId, setExpandedPropertyId] = useState(null);
  // Filter boven de accommodatielijst (zoals de rolfilter bij gebruikers): ALL | CLEANING | AUTOMATION | ACCESS
  const [propertyFilter, setPropertyFilter] = useState('ALL');
  // Aparte sorteerstatus voor de Woningen-tabel (los van die van Gebruikers
  // hierboven, want andere kolommen). key: null = geen sortering (standaard,
  // "ontfilterd").
  const [propertySortConfig, setPropertySortConfig] = useState({ key: null, direction: 'asc' });
  const [message, setMessage] = useState({ text: '', type: '' });
  // Elke melding verdwijnt vanzelf (in de app staat ze als balk boven de tabbalk, dus ze
  // mag nooit blijven hangen en de knoppen eronder blokkeren).
  useEffect(() => {
    if (!message.text) return undefined;
    const id = setTimeout(() => setMessage({ text: '', type: '' }), 4500);
    return () => clearTimeout(id);
  }, [message]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Generiek bevestigingsmodaltje i.p.v. window.confirm() - askConfirm() vult
  // confirmState.message/onConfirm, zie de JSX + helper verderop. Losstaand
  // van deleteConfirm hierboven (dat blijft specifiek voor het verwijderen
  // van gebruikers, met eigen "kan niet ongedaan gemaakt worden"-tekst).
  const [confirmState, setConfirmState] = useState(null);
  const askConfirm = (msg, onConfirm) => setConfirmState({ message: msg, onConfirm });
  const [newUser, setNewUser] = useState({ email: '', role: 'VERHUURDER' });
  const [editingUser, setEditingUser] = useState(null);
  const businessCalRef = useRef(null);
  const cleanerCalRef = useRef(null);
  // Maandoverzicht (admin): zelfde soepele veeg-animatie als de kalender van de schoonmaker
  // (utils/useMonthSwipe.js) + "⋮"-menu om de kalender aan je telefoon-agenda toe te voegen.
  const [businessCalMonth, setBusinessCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const shiftMonth = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
  const handleBusinessCalDatesSet = (arg) => {
    const d = arg.view.currentStart;
    setBusinessCalMonth((prev) => (prev.getFullYear() === d.getFullYear() && prev.getMonth() === d.getMonth() ? prev : new Date(d.getFullYear(), d.getMonth(), 1)));
  };
  const businessSwipe = useMonthSwipe({
    monthKey: `${businessCalMonth.getFullYear()}-${businessCalMonth.getMonth()}`,
    onNext: () => businessCalRef.current?.getApi().next(),
    onPrev: () => businessCalRef.current?.getApi().prev(),
  });
  // Kaart niet even lager laten worden tijdens het bladeren (anders flikkert alles
  // eronder en springt de pagina omhoog).
  useStableMonthHeight(businessSwipe.trackRef, [businessCalMonth, activeTab, businessCalendarView]);
  const [showBusinessCalMenu, setShowBusinessCalMenu] = useState(false);
  const businessCalMenuRef = useRef(null);
  const [calendarFeed, setCalendarFeed] = useState(null);
  const [showCalendarAddConfirm, setShowCalendarAddConfirm] = useState(false);
  const [editingUserTab, setEditingUserTab] = useState('details');
  const [userPropertySearch, setUserPropertySearch] = useState('');
  const [newProperty, setNewProperty] = useState({
    name: '',
    address: '',
    ownerId: '',
    airbnbIcalUrl: '',
    bookingIcalUrl: '',
    vrboIcalUrl: '',
    guestyIcalUrl: '',
    guestyListingId: '',
    hospitableIcalUrl: '',
    bookingSheetId: '',
    nukiSmartlockId: '',
    smartlockBrand: '',
    nukiApiToken: '',
    cleaningTier: 'FULL',
    occasionalCleaningRanges: [],
    propertyType: '',
    fixedCleaningPrice: '',
    automationMonthlyPrice: '',
    cleaningHours: '',
    cleanerHourlyRate: '',
    cleaningWindowStart: '',
    cleaningWindowEnd: '',
    cleaningHoursNote: '',
    useGuesty: false,
    useHospitable: false,
    useDirect: false,
    useSheet: false
  });
  const [editingProperty, setEditingProperty] = useState(null);
  // Schoonmaakinstructies-PDF per woning (verving de vroegere checklist):
  // geen apart modaltje meer, gewoon een sectie onderaan het "woning
  // bewerken"-formulier hieronder (bij de iCal-velden) - werkt op editingProperty.
  const [cleaningPdfUploading, setCleaningPdfUploading] = useState(false);
  const [cleaningPdfError, setCleaningPdfError] = useState('');
  // Woningfoto (i.p.v. het generieke huis-icoontje) - zelfde patroon als de
  // schoonmaak-PDF hierboven, werkt ook op editingProperty.
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [smartlockStatuses, setSmartlockStatuses] = useState({});
  const [applyingLockDefaultsId, setApplyingLockDefaultsId] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [filteredUsersList, setFilteredUsersList] = useState([]);
  // Aparte zoekterm voor de verhuurder-zoekbalk in de "woning bewerken"-modal
  // (los van userSearchTerm hierboven, dat is voor de "nieuwe woning"-modal -
  // anders overschrijven de twee modals elkaars zoekveld).
  const [editOwnerSearchTerm, setEditOwnerSearchTerm] = useState('');
  // Zoekterm per open taak voor "Handmatig toewijzen" (schoonmaker zoeken op
  // naam/email i.p.v. een lange <select>-lijst) - key is task.id, want er
  // kunnen meerdere open taken tegelijk op het scherm staan, elk met een
  // eigen onafhankelijk zoekveld.
  const [manualAssignSearch, setManualAssignSearch] = useState({});
  // Tijdelijke invoerwaarden voor de in-/uitklok-correctievelden in het
  // uitgeklapte paneel van een "Ingepland"-taak - per taak EN per schoonmaker-
  // plaats ('' = hoofd, '2'/'3' = co-schoonmaker), zodat elke toegewezen
  // schoonmaker apart gecorrigeerd kan worden. Sleutel: "${taskId}_${slot}".
  // Zie getClockTimeDraft/setClockTimeDraftField/handleSaveClockTimes verderop.
  const [clockTimeDrafts, setClockTimeDrafts] = useState({});
  const [savingClockTimeKey, setSavingClockTimeKey] = useState(null);
  // Welke rij ("${taskId}_${cleanerId}") in de Uitbetaling-tab momenteel
  // uitgeklapt staat om de in-/uitkloktijd van die schoonmaker recht te
  // zetten - ook nog nodig/mogelijk nadat de taak al COMPLETED/INVOICED is.
  const [payoutEditingRowKey, setPayoutEditingRowKey] = useState(null);
  // Handmatige correcties (uren/uitbetalingsbedrag bij "Uitbetaling",
  // factuurprijs bij "Te factureren") - los van "clockTimeDrafts" hierboven
  // (dat blijft specifiek voor de in-/uitkloktijden). Sleutel voor beide
  // hieronder: "${taskId}_${field}" met field 'hours'/'amount'/'price'. Zie
  // getOverrideDraft/setOverrideDraftField/handleSaveTaskOverride verderop.
  const [taskOverrideDrafts, setTaskOverrideDrafts] = useState({});
  const [savingOverrideKey, setSavingOverrideKey] = useState(null);
  const [editingOverrideKey, setEditingOverrideKey] = useState(null);
  // Zoekveld voor het toevoegen van een 2de/3de schoonmaker (co-schoonmaker)
  // aan een reeds toegewezen taak - los van "manualAssignSearch" hierboven,
  // dat enkel voor de eerste/hoofd-toewijzing op een nog open taak is.
  const [coCleanerSearch, setCoCleanerSearch] = useState({});
  // Zoekveld om een schoonmaker toe te voegen aan de vaste voorkeursvolgorde
  // van de woning die net in de "Woning bewerken"-modal open staat (zie
  // editingProperty.defaultCleanerIds hieronder) - één gedeeld veld volstaat
  // want er staat maar één zo'n modal tegelijk open.
  const [defaultCleanerSearch, setDefaultCleanerSearch] = useState('');
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [filteredNewsletterSubscribers, setFilteredNewsletterSubscribers] = useState([]);
  // Welke categorieën aangevinkt staan in de "Mailing"-tab (zie verderop) -
  // bepaalt welke groepen mee-exporteren zodra op "Exporteren" geklikt wordt.
  // Nieuwsbrief staat standaard aan (bestaand gedrag), de 2 nieuwe categorieën
  // (schoonmakers/verhuurders) staan standaard uit zodat je bewust moet kiezen.
  const [mailingGroups, setMailingGroups] = useState({ newsletter: true, cleaners: false, landlords: false });
  const [payoutSubmittingId, setPayoutSubmittingId] = useState(null);
  const [invoiceOwnerSubmittingId, setInvoiceOwnerSubmittingId] = useState(null);
  // Kwartaalfacturen van het Toegangsbeheer-abonnement (Te factureren-tab).
  const [subscriptionInvoices, setSubscriptionInvoices] = useState([]);
  const [subscriptionInvoiceBusyId, setSubscriptionInvoiceBusyId] = useState(null);
  // Welke Te factureren/Uitbetaling-groepen opengeklapt staan (bijgehouden
  // per cleanerId/ownerId) - standaard dichtgeklapt: toont dan enkel naam +
  // mailadres, pas na klikken zie je het totaal/de actieknop/de takentabel.
  const [expandedPayoutGroups, setExpandedPayoutGroups] = useState({});
  const [expandedInvoiceGroups, setExpandedInvoiceGroups] = useState({});
  // Handmatig ingevoerde Setup-omzet per maand ("YYYY-MM" -> bedrag), enkel
  // voor het "Omzet deze maand"-stukje op het Dashboard-tabblad - Setup wordt,
  // anders dan Toegangsbeheer/Automation/Schoonmaak, nergens automatisch
  // getrackt (eenmalige, op-maat opstartkost per klant), dus vult de admin
  // hier zelf 1 totaalbedrag voor de huidige maand in (zie ook
  // MonthlySetupRevenue in schema.prisma).
  const [monthlySetupRevenue, setMonthlySetupRevenue] = useState({});
  const [setupRevenueInput, setSetupRevenueInput] = useState('');
  // Periodefilter van het Dashboard-tabblad: Alles / Maand / Periode.
  const [statsDateMode, setStatsDateMode] = useState('month');
  const [statsMonth, setStatsMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; });
  const [statsRangeStart, setStatsRangeStart] = useState('');
  const [statsRangeEnd, setStatsRangeEnd] = useState('');
  const [setupRevenueSaving, setSetupRevenueSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAuthToken();
    const userData = getAuthUser();

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'ADMIN') {
      if (parsedUser.role === 'SCHOONMAKER') {
        navigate('/cleaner');
      } else {
        navigate('/dashboard');
      }
      return;
    }

    setUser(parsedUser);
    fetchUsers();
    fetchProperties();
    fetchUsersList();
    fetchSubscriptionInvoices();
    fetchUnscheduledBookings();
    fetchScheduledTasks();
    fetchNewsletterSubscribers();
    document.title = 'SmartStays - Admin Panel';
  }, [navigate]);

  useEffect(() => {
    let result = [...users];
    if (userRoleFilter !== 'ALL') {
      result = result.filter(u => u.role === userRoleFilter);
    }
    if (searchTerm) {
      result = result.filter(u =>
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (userCreatedFilter === 'CUSTOM') {
      const from = userCreatedFrom ? new Date(`${userCreatedFrom}T00:00:00`) : null;
      const to = userCreatedTo ? new Date(`${userCreatedTo}T23:59:59.999`) : null;
      result = result.filter((u) => {
        const created = new Date(u.createdAt);
        return (!from || created >= from) && (!to || created <= to);
      });
    } else if (userCreatedFilter !== 'ALL') {
      const since = Date.now() - Number(userCreatedFilter) * 24 * 60 * 60 * 1000;
      result = result.filter((u) => new Date(u.createdAt).getTime() >= since);
    }
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    result.sort((a, b) => {
      if (sortConfig.key === 'createdAt') {
        return (new Date(a.createdAt) - new Date(b.createdAt)) * dir;
      }
      if (sortConfig.key === 'name' || sortConfig.key === 'email') {
        const aVal = String((sortConfig.key === 'name' ? a.name || a.email : a.email) || '');
        const bVal = String((sortConfig.key === 'name' ? b.name || b.email : b.email) || '');
        return aVal.localeCompare(bVal, undefined, { sensitivity: 'base' }) * dir;
      }
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal < bVal) return -dir;
      if (aVal > bVal) return dir;
      return 0;
    });
    setFilteredUsers(result);
  }, [users, searchTerm, sortConfig, userRoleFilter, userCreatedFilter, userCreatedFrom, userCreatedTo]);

  const formatUserCreated = (value) => {
    if (!value) return '-';
    const lang = String(i18n.language || 'nl').slice(0, 2);
    const locale = lang === 'en' ? 'en-GB' : lang === 'fr' ? 'fr-BE' : 'nl-BE';
    return new Date(value).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  useEffect(() => {
    let result = [...properties];
    if (propertyFilter === 'CLEANING') {
      // Schoonmaak: accommodaties waar schoonmaak voor is ingesteld (niet "geen schoonmaak").
      result = result.filter((p) => p.cleaningTier && p.cleaningTier !== 'NONE');
    } else if (propertyFilter === 'AUTOMATION') {
      result = result.filter((p) => p.automationEnabled);
    } else if (propertyFilter === 'ACCESS') {
      result = result.filter((p) => p.accessManagementEnabled);
    }
    if (searchTerm) {
      result = result.filter(p =>
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.owner_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (propertySortConfig.key) {
      result.sort((a, b) => {
        const aVal = getPropertySortValue(a, propertySortConfig.key);
        const bVal = getPropertySortValue(b, propertySortConfig.key);
        if (aVal < bVal) return propertySortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return propertySortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    setFilteredProperties(result);
  }, [properties, searchTerm, propertySortConfig, smartlockStatuses, propertyFilter]);

  useEffect(() => {
    let result = [...usersList];
    if (userSearchTerm) {
      result = result.filter(u =>
        u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
    }
    setFilteredUsersList(result);
  }, [usersList, userSearchTerm]);

  useEffect(() => {
    let result = [...unscheduledBookings];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(b =>
        b.property_name?.toLowerCase().includes(term) ||
        b.address?.toLowerCase().includes(term) ||
        b.owner_name?.toLowerCase().includes(term) ||
        b.guestName?.toLowerCase().includes(term)
      );
    }
    setFilteredUnscheduledBookings(result);
  }, [unscheduledBookings, searchTerm]);

  useEffect(() => {
    // "PENDING_APPROVAL" (manueel door een verhuurder toegevoegd, nog niet
    // goedgekeurd door een admin) hoort hier niet tussen - die staan apart
    // bij "In te plannen" (zie pendingApprovalTasks) tot ze goedgekeurd zijn.
    let result = scheduledTasks.filter(t => t.status !== 'COMPLETED' && t.status !== 'INVOICED' && t.status !== 'PENDING_APPROVAL');
    if (scheduledStatusFilter === 'open') {
      result = result.filter(t => ['OPEN', 'PENDING_CONFIRMATION', 'WITHDRAWN'].includes(t.status));
    } else if (scheduledStatusFilter === 'assigned') {
      result = result.filter(t => t.status === 'ASSIGNED');
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(t =>
        t.property_name?.toLowerCase().includes(term) ||
        t.address?.toLowerCase().includes(term) ||
        t.guest_name?.toLowerCase().includes(term) ||
        t.cleaner_name?.toLowerCase().includes(term)
      );
    }
    setFilteredScheduledTasks(result);
  }, [scheduledTasks, searchTerm, scheduledStatusFilter]);

  // Maandsleutel ("YYYY-MM") van een ISO-datumstring - gebruikt om de
  // Uitbetaling-geschiedenis en Te factureren-tab per maand te groeperen.
  const monthKeyOf = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  // "payoutOutstandingOnly" (de "Openstaande"-schuifknop) filtert of enkel
  // nog niet ingeboekte/uitbetaalde taken getoond worden, of ook de al
  // uitbetaalde erbij - onafhankelijk van de (altijd huidige) maand.
  const [payoutOutstandingOnly, setPayoutOutstandingOnly] = useState(true);
  // Zelfde principe als "payoutOutstandingOnly" hierboven, maar dan filtert
  // "invoiceOutstandingOnly" op klant-facturatiestatus (customerInvoicedAt)
  // i.p.v. op schoonmaker-uitbetalingsstatus.
  const [invoiceOutstandingOnly, setInvoiceOutstandingOnly] = useState(true);
  // Datumfilter, altijd zichtbaar en altijd actief wanneer ingesteld - ook
  // terwijl "Openstaande" aan staat (dan toont het dus enkel de nog
  // openstaande taken BINNEN die maand/periode i.p.v. gewoon alle
  // openstaande ooit). "mode" is 'all' (standaard: geen datumbeperking,
  // zelfde gedrag als voorheen), 'month' (1 specifieke kalendermaand, eender
  // welke uit het verleden) of 'range' (een zelf gekozen start-/einddatum).
  // Apart bijgehouden voor Uitbetaling en Te factureren, want die 2 tabs
  // kunnen onafhankelijk van elkaar doorbladerd worden.
  const [payoutDateMode, setPayoutDateMode] = useState('all');
  const [payoutSelectedMonth, setPayoutSelectedMonth] = useState(() => monthKeyOf(new Date().toISOString()));
  const [payoutRangeStart, setPayoutRangeStart] = useState('');
  const [payoutRangeEnd, setPayoutRangeEnd] = useState('');
  const [invoiceDateMode, setInvoiceDateMode] = useState('all');
  const [invoiceSelectedMonth, setInvoiceSelectedMonth] = useState(() => monthKeyOf(new Date().toISOString()));
  const [invoiceRangeStart, setInvoiceRangeStart] = useState('');
  const [invoiceRangeEnd, setInvoiceRangeEnd] = useState('');
  // Het "Dashboard"-tabblad toont enkel de lopende, huidige maand (geen
  // maandkiezer meer) - "Omzet" is dus altijd de verwachte omzet van nu.

  // Aandeel van de voorziene schoonmaakuren van 1 taak dat voor 1 individuele
  // schoonmaker telt - gedeeld door het aantal toegewezen schoonmakers op die
  // taak (2de/3de schoonmaker), net zoals "hoursShare" in groupTasksByCleaner
  // hieronder. Gebruikt om per taakrij in de Uitbetaling-tab het effectieve
  // aandeel van DIE schoonmaker te tonen i.p.v. de volle, ongedeelde uren van
  // de taak (die kloppen enkel als er maar 1 schoonmaker op stond).
  // Verdeelt over het DAADWERKELIJK toegewezen aantal schoonmakers
  // (cleanerId/2/3 effectief ingevuld) - NIET over "expected_cleaner_count"
  // (het vooraf verwachte/geconfigureerde aantal, zie EXPECTED_CLEANER_COUNT_SQL
  // in backend/index.js). Bewust zo, want deze functie wordt uitsluitend
  // gebruikt voor uitbetalingsberekeningen op AL VOLTOOIDE taken (Uitbetaling-
  // tab, Dashboard-winst) - op dat moment ligt vast wie er écht gewerkt
  // heeft, en telt enkel dat nog, niet de oorspronkelijke planning/inschatting.
  // Eerder gebruikte dit GREATEST(expected, actual) - dat beschermde wel
  // tegen te weinig delen (een 2de schoonmaker die nadien nog bijkwam terwijl
  // "expected" nog op de oude, te lage waarde stond), maar NIET tegen het
  // omgekeerde: als "expected" te HOOG bleef staan (bv. de woning verwacht
  // normaal 2 schoonmakers, maar deze ene taak werd uiteindelijk solo
  // gedaan, of een mede-schoonmaker werd achteraf terug van de taak gehaald),
  // werd er alsnog fout gedeeld en kreeg de overgebleven schoonmaker maar de
  // helft van zijn/haar effectieve uren/bedrag uitbetaald. Het daadwerkelijk
  // toegewezen aantal is hier altijd de juiste, actuele bron - nooit lager
  // dan 1.
  const getTaskCleanerCount = (task) => {
    const assignedCount = [task.cleanerId, task.cleanerId2, task.cleanerId3].filter(Boolean).length;
    return Math.max(assignedCount, 1);
  };

  // In-/uitkloktijden zijn enkel zichtbaar per schoonmaker terwijl een taak
  // nog ASSIGNED is (zie het "cleaner-info"-blok in de Ingepland-tab). Zodra
  // een taak voltooid is verdwijnt ze uit Ingepland naar Uitbetaling, en
  // zonder dit hier apart te tonen zou de admin de effectief geregistreerde
  // tijden niet meer kunnen nakijken. Geeft de in-/uitkloktijd terug van
  // specifiek de schoonmaker "cleanerId" op deze taak (hoofd of co-slot).
  const getCleanerSlotTimes = (task, cleanerId) => {
    if (task.cleanerId === cleanerId) return { in: task.cleanerClockInAt, out: task.cleanerClockOutAt };
    if (task.cleanerId2 === cleanerId) return { in: task.cleaner2ClockInAt, out: task.cleaner2ClockOutAt };
    if (task.cleanerId3 === cleanerId) return { in: task.cleaner3ClockInAt, out: task.cleaner3ClockOutAt };
    return { in: null, out: null };
  };

  // Geeft de "slot"-sleutel ('', '2' of '3') terug van deze schoonmaker op
  // deze taak - nodig om de bestaande "clock-time-edit"-helpers (oorspronke-
  // lijk gebouwd voor de Ingepland-tab) ook in de Uitbetaling-tab te kunnen
  // hergebruiken voor 1 specifieke schoonmaker per rij.
  const getCleanerSlot = (task, cleanerId) => {
    if (task.cleanerId === cleanerId) return '';
    if (task.cleanerId2 === cleanerId) return '2';
    if (task.cleanerId3 === cleanerId) return '3';
    return null;
  };

  const formatHoursShare = (hours) => {
    if (hours === null || hours === undefined || isNaN(hours)) return t('admin.hoursNotSet');
    return formatHM(hours);
  };

  // Formatteert een bedrag (bv. 42.5) als "€ 42,50" - gebruikt bij
  // "Uitbetaling" voor het bedrag per woning/taak en het totaal per
  // schoonmaker (zie groupTasksByCleaner: amountShare/totalAmount).
  const formatPayoutAmount = (amount) => {
    return (Number(amount) || 0).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR' });
  };

  // Formatteert een tijdsverschil (2 Date-achtige waarden) als "U:MM" (bv.
  // "1:03" voor 63 minuten) i.p.v. afgeronde decimale uren - voor de exacte
  // geklokte duur naast de in-/uitkloktijd in de Uitbetaling-tab, waar een
  // afgerond decimaal getal (bv. "1,1") net verwarrend/onnauwkeurig was.
  const formatDurationHM = (start, end) => {
    if (!start || !end) return null;
    const totalMinutes = Math.round((new Date(end) - new Date(start)) / 60000);
    if (isNaN(totalMinutes) || totalMinutes < 0) return null;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}:${String(m).padStart(2, '0')}`;
  };

  // Is DEZE ene schoonmaker (op dit slot: '', '2' of '3') al uitbetaald voor
  // deze taak? Gebruikt het aparte "cleaner{slot}InvoicedAt"-veld i.p.v. de
  // taak-brede "status" - zo blijft de betaalstatus van co-schoonmakers op
  // een gedeelde taak volledig onafhankelijk van elkaar (voorheen zette
  // uitbetalen voor 1 schoonmaker de hele taak op status "INVOICED", wat de
  // andere schoonmaker(s) op diezelfde taak ook meteen als betaald liet
  // tonen). Val voor taken van vóór deze aparte tracking (geen van de 3
  // velden ooit ingevuld) terug op de oude taak-brede status, zodat al
  // eerder afgehandelde uitbetalingen niet plots weer als openstaand tonen.
  const isSlotPaid = (task, slot) => {
    const field = slot === '' ? 'cleanerInvoicedAt' : `cleaner${slot}InvoicedAt`;
    if (task[field]) return true;
    const anySlotFieldSet = task.cleanerInvoicedAt || task.cleaner2InvoicedAt || task.cleaner3InvoicedAt;
    return task.status === 'INVOICED' && !anySlotFieldSet;
  };

  // Groepeert taken per schoonmaker voor de Uitbetaling-tab(s) hieronder. Een
  // taak met een 2de/3de schoonmaker (co-schoonmaker) komt bij élk van hen
  // terecht, telkens met een proportioneel aandeel (gedeeld door het aantal
  // toegewezen schoonmakers op die taak) van de "cleaningHours" van de
  // woning i.p.v. de volle uren aan iedereen toe te kennen.
  //
  // "outstandingOnly": laat enkel de taken toe die voor DIE specifieke
  // schoonmaker nog niet betaald zijn - per schoonmaker apart bepaald (zie
  // isSlotPaid hierboven), dus 1 gedeelde taak kan bij schoonmaker A wél en
  // bij schoonmaker B niet in de lijst zitten.
  const groupTasksByCleaner = (tasks, outstandingOnly = false) => {
    const groups = {};
    tasks.forEach((task) => {
      const cleanerSlots = [
        { id: task.cleanerId, name: task.cleaner_name, email: task.cleaner_email, slot: '' },
        { id: task.cleanerId2, name: task.cleaner2_name, email: task.cleaner2_email, slot: '2' },
        { id: task.cleanerId3, name: task.cleaner3_name, email: task.cleaner3_email, slot: '3' },
      ].filter((c) => c.id);
      // Normale, GEDEELDE uren per schoonmaker op deze taak (de woning z'n
      // cleaningHours, verdeeld over het aantal schoonmakers) - dit blijft de
      // standaardwaarde zolang een schoonmaker geen eigen override heeft.
      const defaultHoursShare = (Number(task.cleaning_hours) || 0) / getTaskCleanerCount(task);

      (cleanerSlots.length > 0 ? cleanerSlots : [{ id: 'onbekend', name: null, email: null, slot: '' }]).forEach((c) => {
        if (outstandingOnly && isSlotPaid(task, c.slot)) return;
        const key = c.id;
        if (!groups[key]) {
          groups[key] = {
            cleanerId: c.id === 'onbekend' ? null : c.id,
            cleanerName: c.name,
            cleanerEmail: c.email,
            tasks: [],
            totalHours: 0,
            totalAmount: 0,
          };
        }
        // Een handmatige correctie (potloodje bij Uitbetaling) geldt ENKEL
        // voor DEZE ene schoonmaker op DEZE taak (overrideCleaningHours/2/3,
        // overridePayoutAmount/2/3 - zie schema.prisma), en wordt NOOIT
        // gedeeld/herverdeeld met eventuele mede-schoonmakers op dezelfde
        // taak - elke student staat hier dus volledig los van de anderen.
        const hoursOverride = task[`overrideCleaningHours${c.slot}`];
        const hasHoursOverride = hoursOverride !== null && hoursOverride !== undefined;
        const cleanerHours = hasHoursOverride ? Number(hoursOverride) : defaultHoursShare;

        const amountOverride = task[`overridePayoutAmount${c.slot}`];
        const hasAmountOverride = amountOverride !== null && amountOverride !== undefined;
        // Zonder eigen bedrag-override: het uurtarief van de woning x het
        // NORMALE (gedeelde, niet-overschreven) uren-aandeel - bewust NIET
        // "cleanerHours" hierboven, want de uren- en bedrag-override moeten
        // volledig los van elkaar aan te passen zijn. Zonder deze scheiding
        // paste een uren-correctie automatisch ook het getoonde/berekende
        // bedrag aan (en omgekeerd), wat niet de bedoeling is - elk veld
        // wordt pas herberekend als je HET ZELF terugzet naar leeg.
        const cleanerAmount = hasAmountOverride
          ? Number(amountOverride)
          : defaultHoursShare * (Number(task.cleaner_hourly_rate) || 0);

        groups[key].tasks.push(task);
        groups[key].totalHours += cleanerHours;
        groups[key].totalAmount += cleanerAmount;
      });
    });
    // Sorteert de taken per schoonmaker op het volledige tijdstip (dag +
    // uur), niet enkel op dag - voorheen kon binnen dezelfde dag de
    // volgorde willekeurig lijken (bv. 12:00 vóór 11:12), want er werd enkel
    // impliciet gesorteerd via de volgorde waarin de taken binnenkwamen.
    Object.values(groups).forEach((group) => {
      group.tasks.sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
    });
    return Object.values(groups).sort((a, b) => (a.cleanerName || '').localeCompare(b.cleanerName || ''));
  };

  // Toetst of "dateStr" binnen het gekozen datumfilter valt - 1 specifieke
  // maand ("month", vergelijkbaar via monthKeyOf) of een zelf gekozen
  // start-/einddatum ("range", inclusief beide grenzen - de einddatum telt
  // tot en met het einde van die dag). Gedeeld door Uitbetaling en Te
  // factureren hieronder.
  const isWithinDateFilter = (dateStr, mode, selectedMonth, rangeStart, rangeEnd) => {
    if (mode === 'range') {
      const taskDate = new Date(dateStr);
      if (rangeStart && taskDate < new Date(`${rangeStart}T00:00:00`)) return false;
      if (rangeEnd && taskDate > new Date(`${rangeEnd}T23:59:59.999`)) return false;
      return true;
    }
    return monthKeyOf(dateStr) === selectedMonth;
  };

  // Uitbetaling-tab: alle voltooide taken (status COMPLETED of INVOICED).
  // De "Openstaande"-schuifknop is puur een filter erbovenop: aan = enkel
  // nog niet ingeboekte/uitbetaalde taken (status COMPLETED) tonen, uit =
  // ook de al uitbetaalde (INVOICED) taken meetonen.
  const payoutBaseTasks = useMemo(() => {
    let tasks = scheduledTasks.filter((task) => {
      if (task.status !== 'COMPLETED' && task.status !== 'INVOICED') return false;
      // Het datumfilter (Maand/Periode) geldt altijd zodra actief ("all" =
      // standaard, geen beperking) - ook terwijl "Openstaande" aan staat, zo
      // kan je bv. de nog openstaande taken van specifiek september opzoeken
      // i.p.v. enkel "alles wat nog openstaat, ongeacht wanneer". Enkel
      // zolang er GEEN datumfilter actief is, blijven openstaande (nog niet
      // uitbetaalde) taken ongeacht de maand zichtbaar - dat was de eerdere
      // bugfix (taken mogen nooit stilzwijgend verdwijnen bij een nieuwe
      // kalendermaand) en blijft zo het standaardgedrag.
      if (payoutDateMode !== 'all' && !isWithinDateFilter(task.scheduledAt, payoutDateMode, payoutSelectedMonth, payoutRangeStart, payoutRangeEnd)) {
        return false;
      }
      return true;
    });
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      // Zoekt ook op de namen van eventuele mede-schoonmakers (slot 2/3),
      // anders verdwijnt hun eigen taak uit beeld als je op hún naam zoekt
      // terwijl de hoofd-schoonmaker (slot 1) een andere naam heeft.
      tasks = tasks.filter((task) =>
        task.property_name?.toLowerCase().includes(term) ||
        task.address?.toLowerCase().includes(term) ||
        task.guest_name?.toLowerCase().includes(term) ||
        task.cleaner_name?.toLowerCase().includes(term) ||
        task.cleaner2_name?.toLowerCase().includes(term) ||
        task.cleaner3_name?.toLowerCase().includes(term)
      );
    }
    return tasks;
  }, [scheduledTasks, searchTerm, payoutOutstandingOnly, payoutDateMode, payoutSelectedMonth, payoutRangeStart, payoutRangeEnd]);

  const payoutGroups = useMemo(() => {
    return groupTasksByCleaner(payoutBaseTasks, payoutOutstandingOnly);
  }, [payoutBaseTasks, payoutOutstandingOnly]);

  // Groepeert taken per verhuurder voor de Te factureren-tab(s) hieronder -
  // gedeeld tussen de "Openstaand"-weergave en de read-only maandgeschiedenis
  // (zelfde principe als groupTasksByCleaner hierboven, maar dan per klant).
  const groupTasksByOwner = (tasks) => {
    const groups = {};
    tasks.forEach((task) => {
      const key = task.owner_id || 'onbekend';
      if (!groups[key]) {
        groups[key] = {
          ownerId: task.owner_id,
          ownerName: task.owner_name,
          ownerEmail: task.owner_email,
          hasSubscription: task.owner_has_subscription,
          subscriptionCount: task.owner_subscription_count,
          tasks: [],
          subtotal: 0,
        };
      }
      const hasCustomPrice = task.fixed_cleaning_price !== null && task.fixed_cleaning_price !== undefined;
      const price = hasCustomPrice ? Number(task.fixed_cleaning_price) : (PROPERTY_TYPE_PRICES[task.property_type] ?? 0);
      groups[key].tasks.push({ ...task, invoicePrice: price });
      groups[key].subtotal += price;
    });
    return Object.values(groups).map((g) => {
      // Het Toegangsbeheer-abonnement staat hier NIET meer bij: dat wordt per
      // kwartaal vooraf gefactureerd (en gratis voor Automation-klanten) via
      // de aparte abonnementsfacturen onderaan in deze tab. De maandprijs
      // (€9.99 + €2.50 per extra woning) staat in backend/subscriptionBilling.js.
      const subscriptionPrice = 0;
      const allInvoiced = g.tasks.every((t) => t.customerInvoicedAt);
      const invoicedAt = allInvoiced
        ? g.tasks.reduce((latest, t) => (!latest || new Date(t.customerInvoicedAt) > new Date(latest) ? t.customerInvoicedAt : latest), null)
        : null;
      return { ...g, subscriptionPrice, total: g.subtotal + subscriptionPrice, allInvoiced, invoicedAt };
    }).sort((a, b) => (a.ownerName || '').localeCompare(b.ownerName || ''));
  };

  // Te factureren-tab: alle voltooide taken (status COMPLETED of INVOICED -
  // onafhankelijk van de uitbetalingsstatus aan de schoonmaker) van de
  // huidige kalendermaand. Zelfde principe als "payoutBaseTasks/
  // payoutOutstandingOnly" hierboven, maar dan filtert "Openstaande" hier
  // op klant-facturatiestatus (customerInvoicedAt) i.p.v. op
  // schoonmaker-uitbetalingsstatus.
  const invoiceBaseTasks = useMemo(() => {
    let tasks = scheduledTasks.filter((task) => {
      if (task.status !== 'COMPLETED' && task.status !== 'INVOICED') return false;
      // Zelfde principe als payoutBaseTasks hierboven: het datumfilter geldt
      // altijd zodra actief (ook terwijl "Openstaande" aan staat), enkel
      // zonder datumfilter ("all") blijven nog niet gefactureerde taken
      // ongeacht de maand zichtbaar.
      if (invoiceDateMode !== 'all' && !isWithinDateFilter(task.scheduledAt, invoiceDateMode, invoiceSelectedMonth, invoiceRangeStart, invoiceRangeEnd)) {
        return false;
      }
      return true;
    });
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      tasks = tasks.filter((task) =>
        task.property_name?.toLowerCase().includes(term) ||
        task.address?.toLowerCase().includes(term) ||
        task.owner_name?.toLowerCase().includes(term)
      );
    }
    return tasks;
  }, [scheduledTasks, searchTerm, invoiceOutstandingOnly, invoiceDateMode, invoiceSelectedMonth, invoiceRangeStart, invoiceRangeEnd]);

  const invoiceGroups = useMemo(() => {
    const tasks = invoiceOutstandingOnly ? invoiceBaseTasks.filter((task) => !task.customerInvoicedAt) : invoiceBaseTasks;
    return groupTasksByOwner(tasks);
  }, [invoiceBaseTasks, invoiceOutstandingOnly]);

  // Abonnementsfacturen (Toegangsbeheer, per kwartaal vooraf) in de Te
  // factureren-tab: "Openstaande" verbergt de al betaalde, de zoekbalk filtert
  // op klantnaam/-mail. Het maand-/periodefilter geldt niet voor deze
  // facturen (die lopen over een heel kwartaal).
  const visibleSubscriptionInvoices = useMemo(() => {
    let list = subscriptionInvoices;
    if (invoiceOutstandingOnly) list = list.filter((inv) => inv.status !== 'PAID');
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter((inv) => (inv.ownerName || '').toLowerCase().includes(term) || (inv.ownerEmail || '').toLowerCase().includes(term));
    }
    return list;
  }, [subscriptionInvoices, invoiceOutstandingOnly, searchTerm]);

  // Altijd het aantal klanten met effectief nog openstaande facturen, los van
  // de maand/"Openstaande"-filters hierboven - dit is wat het cijfertje op het
  // "Te factureren"-tabblad toont (zelfde rol als "outstandingPayoutGroupsCount"
  // hieronder voor het "Uitbetaling"-tabblad).
  const outstandingInvoiceGroupsCount = useMemo(() => {
    const tasks = scheduledTasks.filter((t) => (t.status === 'COMPLETED' || t.status === 'INVOICED') && !t.customerInvoicedAt);
    const subscriptionToInvoice = subscriptionInvoices.filter((inv) => inv.status === 'TO_INVOICE').length;
    return groupTasksByOwner(tasks).length + subscriptionToInvoice;
  }, [scheduledTasks, subscriptionInvoices]);

  // Altijd het aantal schoonmakers met effectief nog een openstaande
  // uitbetaling, los van de maand/"Openstaande"-filters - dit is wat het
  // cijfertje op het "Uitbetaling"-tabblad toont. Voorheen stond hier het
  // aantal COMPLETED taken (filteredCompletedTasks.length), maar 1 taak met
  // meerdere schoonmakers komt in de Uitbetaling-tabel meermaals voor (1 rij
  // per schoonmaker) en meerdere taken kunnen bij dezelfde schoonmaker
  // horen - dat cijfer klopte dus niet met "hoeveel mensen moeten
  // uitbetaald worden". Groepeert daarom net als de tabel zelf per
  // schoonmaker en telt het aantal groepen.
  const outstandingPayoutGroupsCount = useMemo(() => {
    const tasks = scheduledTasks.filter((t) => t.status === 'COMPLETED' || t.status === 'INVOICED');
    return groupTasksByCleaner(tasks, true).length;
  }, [scheduledTasks]);

  // Alle cijfers voor het nieuwe "Dashboard"-tabblad (bedrijfsoverzicht) -
  // bewust volledig client-side berekend uit de data die toch al voor de
  // rest van het beheerpaneel opgehaald wordt (users/properties/
  // scheduledTasks/newsletterSubscribers/unscheduledBookings), i.p.v. een
  // apart backend-endpoint. "Live" wordt hier ingevuld doordat dit tabblad
  // die data bij het openen en daarna elke 30s opnieuw ophaalt (zie de
  // useEffect verderop) - deze memo herrekent dan automatisch mee.
  const businessStats = useMemo(() => {
    // Periode volgens de filter bovenaan het Dashboard: alles, 1 maand of een
    // vrij gekozen periode. "selectedMonthKey" is de maand waarvoor het
    // Setup-bedrag ingevoerd wordt (gekozen maand, anders de huidige).
    const todayKey = monthKeyOf(new Date());
    let periodStart = null;
    let periodEnd = null;
    if (statsDateMode === 'month') {
      const [y, m] = statsMonth.split('-').map(Number);
      periodStart = new Date(y, m - 1, 1);
      periodEnd = new Date(y, m, 0, 23, 59, 59, 999);
    } else if (statsDateMode === 'range') {
      if (statsRangeStart) periodStart = new Date(`${statsRangeStart}T00:00:00`);
      if (statsRangeEnd) periodEnd = new Date(`${statsRangeEnd}T23:59:59.999`);
    }
    const selectedMonthKey = statsDateMode === 'month' ? statsMonth : todayKey;
    const isSelectedMonth = (dateStr) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      if (periodStart && d < periodStart) return false;
      if (periodEnd && d > periodEnd) return false;
      return true;
    };
    // Aantal maanden in de periode waarin een abonnement (vanaf startStr) liep:
    // per kalendermaand 1x het maandtarief, tot en met de huidige maand.
    const usersById = Object.fromEntries(users.map((u) => [u.id, u]));
    const monthsActive = (startStr) => {
      const start = startStr ? new Date(startStr) : null;
      const now = new Date();
      const lastCap = new Date(now.getFullYear(), now.getMonth(), 1);
      let from = periodStart ? new Date(periodStart.getFullYear(), periodStart.getMonth(), 1) : (start ? new Date(start.getFullYear(), start.getMonth(), 1) : lastCap);
      const to = periodEnd ? new Date(periodEnd.getFullYear(), periodEnd.getMonth(), 1) : lastCap;
      let count = 0;
      for (let guard = 0; from <= to && guard < 600; guard += 1) {
        const monthEnd = new Date(from.getFullYear(), from.getMonth() + 1, 0, 23, 59, 59, 999);
        if (!start || start <= monthEnd) count += 1;
        from = new Date(from.getFullYear(), from.getMonth() + 1, 1);
      }
      return count;
    };

    const verhuurders = users.filter((u) => u.role === 'VERHUURDER');
    const schoonmakers = users.filter((u) => u.role === 'SCHOONMAKER');
    // Gewoon het totale aantal, niet enkel de al geactiveerde accounts - het
    // onderscheid "actief"/"nog niet geactiveerd" is niet relevant genoeg om
    // apart te tonen op dit dashboard.
    const totalVerhuurders = verhuurders.length;
    const totalSchoonmakers = schoonmakers.length;
    const newUsersThisMonth = users.filter((u) => isSelectedMonth(u.createdAt)).length;

    const subscribedVerhuurders = verhuurders.filter((u) => u.hasSubscription);
    const automationVerhuurders = verhuurders.filter((u) => u.hasAutomation);
    // "Aantal setup deze maand" = aantal klanten waarvoor in de gekozen maand
    // voor het eerst een abonnement (toegangsbeheer en/of automation) gestart
    // is, op basis van diezelfde "subscriptionStartedAt"/"automationStartedAt"
    // velden (dus ook hier vallen oudere abonnees zonder dat veld terug op
    // hun "createdAt" als beste benadering).
    const setupsThisMonthIds = new Set();
    verhuurders.forEach((u) => {
      if (u.hasSubscription && isSelectedMonth(u.subscriptionStartedAt || u.createdAt)) setupsThisMonthIds.add(u.id);
      if (u.hasAutomation && isSelectedMonth(u.automationStartedAt || u.createdAt)) setupsThisMonthIds.add(u.id);
    });
    const setupsThisMonth = setupsThisMonthIds.size;

    // Per-klant (verhuurder) omzet-opbouw voor de top-20-klanten-taart
    // verderop: gevuld per dienst hieronder (Toegangsbeheer/Automation bij
    // het doorlopen van properties, Schoonmaak bij het doorlopen van
    // tasksThisMonth). Setup zit hier bewust NIET in - dat is 1 vrij
    // totaalbedrag per maand zonder koppeling aan een specifieke klant.
    const ownerRevenueMap = {};
    const ownerEntry = (id, name, email) => {
      if (!id) return null;
      if (!ownerRevenueMap[id]) {
        ownerRevenueMap[id] = { ownerId: id, name: name || null, email: email || null, access: 0, automation: 0, cleaning: 0, cost: 0 };
      } else {
        if (!ownerRevenueMap[id].name && name) ownerRevenueMap[id].name = name;
        if (!ownerRevenueMap[id].email && email) ownerRevenueMap[id].email = email;
      }
      return ownerRevenueMap[id];
    };

    // Toegangsbeheer-omzet: vast tarief van €9.99/maand (excl. btw) per
    // gekoppelde woning - dit is geen instelbaar veld meer sinds klanten
    // zelf hun smart lock aanschaffen (geen pakket-afhankelijke hardware-
    // prijs meer). Automation-omzet blijft wel het per-woning ingestelde
    // maandtarief (automationMonthlyPrice - zie schema.prisma), want dat
    // pakket wordt nog steeds op maat geoffreerd.
    const ACCESS_MANAGEMENT_FIXED_PRICE = 9.99;
    const tierCounts = { FULL: 0, PARTIAL: 0, NONE: 0 };
    const typeCounts = { hotelRoom: 0, type2: 0, type3: 0, type4: 0, type5: 0, unset: 0 };
    let smartlockLinked = 0;
    let accessManagedProperties = 0;
    let accessRevenue = 0;
    let automationRevenue = 0;
    properties.forEach((p) => {
      const tier = p.cleaningTier || 'FULL';
      tierCounts[tier] = (tierCounts[tier] || 0) + 1;
      const typeKey = p.propertyType && Object.prototype.hasOwnProperty.call(typeCounts, p.propertyType) ? p.propertyType : 'unset';
      typeCounts[typeKey] += 1;
      if (p.nukiSmartlockId) smartlockLinked += 1;
      if (p.accessManagementEnabled) {
        accessManagedProperties += 1;
        const ownerUser = usersById[p.ownerId] || {};
        // Automation-klanten krijgen Toegangsbeheer gratis: wel meegeteld als
        // beheerde woning, maar geen omzet.
        if (!ownerUser.hasAutomation) {
          const price = ACCESS_MANAGEMENT_FIXED_PRICE * monthsActive(ownerUser.subscriptionStartedAt || ownerUser.createdAt);
          accessRevenue += price;
          const entry = ownerEntry(p.ownerId, p.owner_name, p.owner_email);
          if (entry) entry.access += price;
        }
      }
      if (p.automationEnabled) {
        const ownerUser = usersById[p.ownerId] || {};
        const price = (Number(p.automationMonthlyPrice) || 0) * monthsActive(ownerUser.automationStartedAt || ownerUser.createdAt);
        automationRevenue += price;
        const entry = ownerEntry(p.ownerId, p.owner_name, p.owner_email);
        if (entry) entry.automation += price;
      }
    });

    const tasksThisMonth = scheduledTasks.filter((t) => isSelectedMonth(t.scheduledAt));
    const completedThisMonth = tasksThisMonth.filter((t) => t.status === 'COMPLETED' || t.status === 'INVOICED').length;
    const activeCleanerIdsThisMonth = new Set();
    tasksThisMonth.forEach((t) => {
      [t.cleanerId, t.cleanerId2, t.cleanerId3].forEach((id) => { if (id) activeCleanerIdsThisMonth.add(id); });
    });

    // Omzet uit schoonmaakopdrachten voor de gekozen maand: ALLE taken die dat
    // moment gepland staan/stonden (dus ook nog "aan te komen" open/ingeplande
    // taken, niet enkel al voltooide/gefactureerde), min de geannuleerde
    // (WITHDRAWN) - dit is de verwachte omzet uit schoonmaak voor die maand.
    // Zelfde prijsberekening (vast tarief per woning, anders standaardtarief
    // per pandtype) als "getScheduledTaskPrice" verderop in dit bestand -
    // hier bewust inline herhaald omdat die helper pas later gedefinieerd
    // wordt (na deze memo) in de bestandsvolgorde.
    let cleaningRevenue = 0;
    tasksThisMonth
      .filter((t) => t.status !== 'WITHDRAWN')
      .forEach((t) => {
        const hasCustomPrice = t.fixed_cleaning_price !== null && t.fixed_cleaning_price !== undefined;
        const price = hasCustomPrice ? Number(t.fixed_cleaning_price) : (PROPERTY_TYPE_PRICES[t.property_type] ?? 0);
        cleaningRevenue += price;
        const entry = ownerEntry(t.owner_id, t.owner_name, t.owner_email);
        if (entry) entry.cleaning += price;
      });

    // Setup: enkel dienst die we nergens automatisch kunnen aflezen (op-maat
    // opstartkost per klant, geen vaste/recurrente prijs) - de admin typt dit
    // zelf 1 totaalbedrag per maand in (zie monthlySetupRevenue state).
    const setupRevenue = Object.entries(monthlySetupRevenue).reduce((sum, [key, amount]) => {
      const [ky, km] = key.split('-').map(Number);
      const mStart = new Date(ky, km - 1, 1);
      const mEnd = new Date(ky, km, 0, 23, 59, 59, 999);
      if (periodStart && mEnd < periodStart) return sum;
      if (periodEnd && mStart > periodEnd) return sum;
      return sum + (Number(amount) || 0);
    }, 0);

    const totalRevenue = setupRevenue + accessRevenue + cleaningRevenue + automationRevenue;

    // Schoonmaker-kost voor de lopende maand: zelfde berekening als in
    // groupTasksByCleaner (per toegewezen schoonmaker apart, met een
    // eventuele eigen override die nooit gedeeld wordt met mede-
    // schoonmakers op dezelfde taak), maar hier opgeteld over ALLE
    // schoonmakers/taken van de maand i.p.v. gegroepeerd per schoonmaker -
    // dit is voorlopig de enige kost die we bijhouden (zie ook "Winst"
    // hieronder). Zelfde WITHDRAWN-filter als cleaningRevenue hierboven, en
    // telt ook nog "aan te komen" taken mee (verwachte kost, net als
    // verwachte omzet).
    let cleanerCost = 0;
    tasksThisMonth
      .filter((t) => t.status !== 'WITHDRAWN')
      .forEach((t) => {
        // Verwachte kost: ook zolang er nog (niet genoeg) schoonmakers zijn
        // toegewezen telt de kost al mee - het loon is op voorhand gekend
        // (uren x uurtarief, gedeeld over het verwachte aantal schoonmakers).
        const assignedIds = [t.cleanerId, t.cleanerId2, t.cleanerId3];
        const plannedCount = Math.max(getTaskCleanerCount(t), Number(t.expected_cleaner_count) || 1);
        const defaultHoursShare = (Number(t.cleaning_hours) || 0) / plannedCount;
        for (let idx = 0; idx < plannedCount; idx += 1) {
          const cleanerId = assignedIds[idx];
          const slot = idx === 0 ? '' : String(idx + 1);
          // Zelfde bewuste ontkoppeling als in groupTasksByCleaner: het
          // bedrag zonder eigen override gebruikt altijd defaultHoursShare.
          const amountOverride = cleanerId ? t[`overridePayoutAmount${slot}`] : null;
          const slotCost = (amountOverride !== null && amountOverride !== undefined)
            ? Number(amountOverride)
            : defaultHoursShare * (Number(t.cleaner_hourly_rate) || 0);
          cleanerCost += slotCost;
          const costEntry = ownerEntry(t.owner_id, t.owner_name, t.owner_email);
          if (costEntry) costEntry.cost += slotCost;
        }
      });

    const profit = totalRevenue - cleanerCost;

    const serviceBreakdown = [
      { key: 'setup', labelKey: 'admin.statsRevenueSetup', value: setupRevenue },
      { key: 'access', labelKey: 'admin.statsRevenueSubscriptions', value: accessRevenue },
      { key: 'cleaning', labelKey: 'admin.statsRevenueCleaning', value: cleaningRevenue },
      { key: 'automation', labelKey: 'admin.statsRevenueAutomation', value: automationRevenue },
    ];

    const topCustomers = Object.values(ownerRevenueMap)
      .map((o) => ({ ...o, total: o.access + o.automation + o.cleaning }))
      .filter((o) => o.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 20);

    // Winst van diezelfde top-20-klanten: omzet min de schoonmaker-kost van hun taken.
    const topCustomersProfit = topCustomers
      .map((o) => ({ ...o, profit: o.total - (o.cost || 0) }))
      .filter((o) => o.profit > 0)
      .sort((a, b) => b.profit - a.profit);

    const newsletterActive = newsletterSubscribers.filter((s) => !s.unsubscribedAt).length;
    const newSubscribersThisMonth = newsletterSubscribers.filter((s) => isSelectedMonth(s.createdAt)).length;

    return {
      selectedMonthKey,
      setupRevenue,
      accessRevenue,
      automationRevenue,
      cleaningRevenue,
      totalRevenue,
      cleanerCost,
      profit,
      serviceBreakdown,
      topCustomers,
      topCustomersProfit,
      totalVerhuurders,
      totalSchoonmakers,
      newUsersThisMonth,
      subscribedCount: subscribedVerhuurders.length,
      automationCount: automationVerhuurders.length,
      setupsThisMonth,
      accessManagedProperties,
      totalProperties: properties.length,
      tierCounts,
      typeCounts,
      smartlockLinked,
      tasksThisMonthCount: tasksThisMonth.length,
      completedThisMonth,
      todoThisMonth: tasksThisMonth.length - completedThisMonth,
      activeCleanersThisMonth: activeCleanerIdsThisMonth.size,
      newsletterTotal: newsletterSubscribers.length,
      newsletterActive,
      newSubscribersThisMonth,
    };
  }, [users, properties, scheduledTasks, newsletterSubscribers, monthlySetupRevenue, statsDateMode, statsMonth, statsRangeStart, statsRangeEnd]);

  // Dit tabblad toont "live" bedrijfscijfers: bij het openen meteen verversen
  // (de rest van het paneel haalt alles maar 1x op bij het inloggen, dus de
  // data kan intussen verouderd zijn) en daarna elke 30s opnieuw, zolang de
  // admin op dit tabblad blijft.
  useEffect(() => {
    if (activeTab !== 'stats') return undefined;
    fetchUsers();
    fetchProperties();
    fetchScheduledTasks();
    fetchUnscheduledBookings();
    fetchNewsletterSubscribers();
    fetchMonthlySetupRevenue();
    const interval = setInterval(() => {
      fetchUsers();
      fetchProperties();
      fetchScheduledTasks();
      fetchUnscheduledBookings();
      fetchNewsletterSubscribers();
      fetchMonthlySetupRevenue();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Vult het invoerveld voor de Setup-omzet 1 keer (zodra het al opgehaalde
  // bedrag voor deze maand binnenkomt) in, zonder verder actief getyp van de
  // admin te overschrijven bij elke 30s-verversing hierboven.
  useEffect(() => {
    setSetupRevenueInput(monthlySetupRevenue[statsMonth] !== undefined ? String(monthlySetupRevenue[statsMonth]) : '');
  }, [monthlySetupRevenue, statsMonth]);

  useEffect(() => {
    let result = [...newsletterSubscribers];
    if (searchTerm) {
      result = result.filter(s => s.email?.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    setFilteredNewsletterSubscribers(result);
  }, [newsletterSubscribers, searchTerm]);

  const fetchUsers = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Fout bij ophalen gebruikers:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        if (isImpersonating()) {
          // Enkel de impersonatie-sessie van dit tabblad beëindigen - niet de
          // echte, gedeelde sessie van de admin (localStorage) aanraken.
          stopImpersonating();
          window.location.href = '/admin';
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        }
      }
    }
  };

  const fetchProperties = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/admin/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
      // Batterijstatus meteen voor elke gekoppelde woning ophalen (i.p.v. pas
      // na een klik), zodat het percentage direct zichtbaar en sorteerbaar is.
      response.data
        .filter(p => p.nukiSmartlockId)
        .forEach(p => fetchSmartlockStatus(p.id));
    } catch (error) {
      console.error('Fout bij ophalen properties:', error);
    }
  };

  // Haalt de live batterij-/bereikbaarheidsstatus van een gekoppeld Nuki-slot
  // op. Wordt automatisch voor elke gekoppelde woning aangeroepen bij het
  // laden van de Woningen-tabel (zie fetchProperties), zodat het percentage
  // meteen zichtbaar is en er meteen op gesorteerd kan worden.
  const fetchSmartlockStatus = async (propertyId) => {
    setSmartlockStatuses(prev => ({ ...prev, [propertyId]: { loading: true } }));
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/properties/${propertyId}/smartlock-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmartlockStatuses(prev => ({ ...prev, [propertyId]: response.data }));
    } catch (error) {
      console.error('Fout bij ophalen smartlock-status:', error);
      setSmartlockStatuses(prev => ({ ...prev, [propertyId]: { error: true } }));
    }
  };

  const fetchUsersList = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const verhuurders = response.data.filter(u => u.role === 'VERHUURDER');
      setUsersList(verhuurders);
      setFilteredUsersList(verhuurders);
    } catch (error) {
      console.error('Fout bij ophalen gebruikers:', error);
    }
  };

  const fetchSubscriptionInvoices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscription-invoices`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setSubscriptionInvoices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Fout bij ophalen abonnementsfacturen:', error);
    }
  };

  // Abonnementsfactuur: eerst "gefactureerd" (factuur verstuurd), daarna
  // "betaald". Het nieuwe kwartaal gaat pas in als de factuur betaald is.
  const handleSubscriptionInvoiceAction = async (invoice, action) => {
    setSubscriptionInvoiceBusyId(invoice.id);
    try {
      await axios.post(`${API_URL}/api/subscription-invoices/${invoice.id}/${action}`, {}, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      await fetchSubscriptionInvoices();
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.subscriptionInvoiceError'), type: 'error' });
    } finally {
      setSubscriptionInvoiceBusyId(null);
    }
  };

  const fetchUnscheduledBookings = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/unscheduled-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnscheduledBookings(response.data);
    } catch (error) {
      console.error('Fout bij ophalen niet-ingeplande boekingen:', error);
    }
  };

  // Abonnementslink (webcal://) met ALLE schoonmaakmomenten, voor de agenda-app op je telefoon
  // - zie /api/users/me/calendar-feed (voor admins: alle ingeplande schoonmaak, niet enkel de eigen).
  const fetchCalendarFeed = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/users/me/calendar-feed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarFeed(response.data);
      return response.data;
    } catch (error) {
      setMessage({ text: t('cleaner.calendarFeedError'), type: 'error' });
      return null;
    }
  };

  const handleCopyCalendarLink = async () => {
    const feed = calendarFeed || await fetchCalendarFeed();
    if (!feed) return;
    try {
      await navigator.clipboard.writeText(feed.httpsUrl);
      setMessage({ text: t('cleaner.calendarLinkCopied'), type: 'success' });
    } catch (error) {
      setMessage({ text: t('cleaner.calendarCopyError'), type: 'error' });
    }
  };

  const handleAddToCalendar = async () => {
    const feed = calendarFeed || await fetchCalendarFeed();
    if (feed) setShowCalendarAddConfirm(true);
  };

  // Sluit het ⋮-menu bij een klik erbuiten.
  useEffect(() => {
    if (!showBusinessCalMenu) return undefined;
    const handleClickOutside = (e) => {
      if (businessCalMenuRef.current && !businessCalMenuRef.current.contains(e.target)) {
        setShowBusinessCalMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBusinessCalMenu]);

  const fetchScheduledTasks = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/cleaning-tasks?withDetails=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScheduledTasks(response.data);
    } catch (error) {
      console.error('Fout bij ophalen ingeplande taken:', error);
    }
  };

  const fetchNewsletterSubscribers = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/admin/newsletter-subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewsletterSubscribers(response.data);
    } catch (error) {
      console.error('Fout bij ophalen nieuwsbrief-inschrijvingen:', error);
    }
  };

  const fetchMonthlySetupRevenue = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/admin/monthly-setup-revenue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const byMonth = Object.fromEntries(response.data.map((row) => [row.monthKey, Number(row.amount)]));
      setMonthlySetupRevenue(byMonth);
    } catch (error) {
      console.error('Fout bij ophalen Setup-omzet:', error);
    }
  };

  // Slaat het ingevoerde Setup-omzetbedrag voor de huidige maand op. Los van
  // de andere 3 diensten (die automatisch berekend worden), dit is het enige
  // stukje van "Omzet deze maand" dat de admin zelf moet intypen.
  const handleSaveSetupRevenue = async () => {
    const monthKey = statsMonth;
    const parsed = Number(setupRevenueInput);
    if (Number.isNaN(parsed) || parsed < 0) return;
    setSetupRevenueSaving(true);
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/api/admin/monthly-setup-revenue/${monthKey}`, { amount: parsed }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthlySetupRevenue((prev) => ({ ...prev, [monthKey]: parsed }));
    } catch (error) {
      console.error('Fout bij opslaan Setup-omzet:', error);
    } finally {
      setSetupRevenueSaving(false);
    }
  };

  const toggleMailingGroup = (group) => {
    setMailingGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  // Exporteert de mailadressen van enkel de aangevinkte categorieën
  // (nieuwsbrief-inschrijvers/schoonmakers/verhuurders) als 1 CSV - vervangt
  // de vorige, enkel-op-nieuwsbrief-gerichte export.
  const handleExportMailing = async () => {
    const selectedGroups = Object.entries(mailingGroups).filter(([, checked]) => checked).map(([group]) => group);
    if (selectedGroups.length === 0) {
      setMessage({ text: t('admin.mailingNoGroupsSelected'), type: 'error' });
      return;
    }
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/admin/mailing-export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { groups: selectedGroups.join(',') },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'mailing-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({ text: t('admin.newsletterExportError'), type: 'error' });
    }
  };

  const togglePayoutGroup = (cleanerId) => {
    setExpandedPayoutGroups((prev) => ({ ...prev, [cleanerId]: !prev[cleanerId] }));
  };

  const toggleInvoiceGroup = (ownerId) => {
    setExpandedInvoiceGroups((prev) => ({ ...prev, [ownerId]: !prev[ownerId] }));
  };

  const openPropertySchedule = (property) => {
    setSchedulingProperty(property);
    setScheduleView('multi');
  };

  const closePropertySchedule = () => {
    setSchedulingProperty(null);
    fetchUnscheduledBookings();
    fetchScheduledTasks();
  };

  // Leest het Van/Tot-uur dat de admin aan het intikken is bij "In te
  // plannen" - zolang die nog niets zelf aanpaste, valt dit terug op het
  // venster van de gekozen schoonmaakdienst (PropertyCleaningService, zie
  // dropdown verderop), en pas daarna op de legacy vlakke velden van de
  // woning zelf (cleaningWindowStart/End, van vóór de diensten-feature).
  // "draftKey" is de sleutel waaronder de invoer zelf bewaard wordt (per
  // boeking uniek, dus booking.id - zo heeft elke boeking haar eigen
  // onafhankelijke invoervelden, ook als dezelfde woning meerdere boekingen
  // tegelijk te plannen heeft); "propertyId" is enkel nodig om de juiste
  // woning/dienst op te zoeken voor de fallback-waarden. "booking" wordt niet
  // meer gebruikt voor de tijden zelf (Van/Tot moeten het ingestelde
  // schoonmaakdienst-venster volgen, niet de eigen uitcheck-/volgende
  // inchek-tijd van de boeking) - param blijft staan voor evt. toekomstig
  // gebruik maar heeft momenteel geen effect.
  const getApproveTime = (draftKey, field, propertyId) => {
    const override = approveTimes[draftKey]?.[field];
    if (override !== undefined) return override;
    const prop = properties.find((p) => p.id === (propertyId || draftKey));
    const serviceId = approveAmounts[draftKey]?.serviceId || prop?.cleaningServices?.[0]?.id;
    const service = prop?.cleaningServices?.find((s) => s.id === serviceId);
    const serviceValue = field === 'start' ? service?.windowStart : service?.windowEnd;
    if (serviceValue) return serviceValue;
    const propValue = field === 'start' ? prop?.cleaningWindowStart : prop?.cleaningWindowEnd;
    if (propValue) return propValue;
    // Laatste redmiddel als de woning geen enkel venster heeft ingesteld (bv.
    // net via Google Sheets geïmporteerde boekingen): een algemeen standaard-
    // venster i.p.v. een leeg "--:--" veld dat de admin elke keer manueel
    // moet invullen. 11:00-15:00 sluit aan bij de standaard uitchek-/inchek-
    // conventie elders in de app (zie parseSheetDate in index.js).
    return field === 'start' ? '11:00' : '15:00';
  };

  const setApproveTime = (draftKey, field, value) => {
    setApproveTimes((prev) => ({
      ...prev,
      [draftKey]: { ...(prev[draftKey] || {}), [field]: value }
    }));
  };

  // Zelfde patroon als hierboven, maar voor de dag ('YYYY-MM-DD') i.p.v. het
  // uur - valt terug op de uitcheckdag van de boeking zolang de admin hier
  // zelf nog niets aangepast heeft.
  const getApproveDate = (propertyId, fallbackCheckOut) => {
    const override = approveDates[propertyId];
    if (override !== undefined) return override;
    return fallbackCheckOut ? toDateKey(fallbackCheckOut) : '';
  };

  const setApproveDate = (propertyId, value) => {
    setApproveDates((prev) => ({ ...prev, [propertyId]: value }));
  };

  // Zelfde per-woning-draft-patroon als hierboven, maar voor de 2 optionele
  // bedragen die de admin bij "In te plannen" nu al vast kan zetten voor
  // DEZE ene taak: de klantprijs en het loon van de (nog toe te wijzen)
  // schoonmaker - zie overrideInvoicePrice/overridePayoutAmount bij
  // handleApproveSchedule. Leeg = gewoon de normale berekening laten gelden,
  // exact zoals voorheen.
  const getApproveAmount = (propertyId, field) => approveAmounts[propertyId]?.[field] ?? '';
  const setApproveAmount = (propertyId, field, value) => {
    setApproveAmounts((prev) => ({
      ...prev,
      [propertyId]: { ...(prev[propertyId] || {}), [field]: value }
    }));
  };

  // "Goedkeuren"-knop bij "In te plannen": plant in één klik de eerstvolgende
  // (nog niet ingeplande) boeking van deze woning in, op de datum van de
  // uitcheck en het Van/Tot-uur dat hierboven ingevuld staat - zonder eerst
  // de volledige kalender te moeten openen. Gebruikt gewoon dezelfde
  // POST /api/cleaning-tasks als de normale inplan-flow via de kalender.
  const handleRejectCleaning = (bookingId) => {
    askConfirm(t('admin.confirmRejectCleaning'), async () => {
      try {
        await axios.post(`${API_URL}/api/bookings/${bookingId}/reject-cleaning`, {}, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        fetchUnscheduledBookings();
      } catch (error) {
        setMessage({ text: error.response?.data?.error || t('admin.withdrawError'), type: 'error' });
      }
    });
  };

  const handleRestoreCleaning = async (bookingId) => {
    try {
      await axios.post(`${API_URL}/api/bookings/${bookingId}/restore-cleaning`, {}, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      fetchUnscheduledBookings();
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.withdrawError'), type: 'error' });
    }
  };

  const handleApproveSchedule = async (booking) => {
    // Gesleuteld op booking.id (niet propertyId) - zo heeft elke boeking
    // haar eigen, onafhankelijke datum/uur/prijs-invoer, ook als dezelfde
    // woning meerdere boekingen tegelijk te plannen heeft.
    const start = getApproveTime(booking.id, 'start', booking.propertyId);
    const end = getApproveTime(booking.id, 'end', booking.propertyId);
    if (!start || !end) {
      setMessage({ text: t('admin.approveMissingHours'), type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
      return;
    }

    // Dag komt nu uit het datumveld dat de admin bij "In te plannen" kan
    // aanpassen (getApproveDate) i.p.v. altijd vast te staan op de
    // uitcheckdag van de boeking - valt terug op die uitcheckdag zolang er
    // niets aangepast is.
    const dateStr = getApproveDate(booking.id, booking.checkOut);
    const [dateY, dateM, dateD] = dateStr.split('-').map(Number);
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    const startDateTime = new Date(dateY, dateM - 1, dateD, startH, startM, 0, 0);
    const endDateTime = new Date(dateY, dateM - 1, dateD, endH, endM, 0, 0);

    if (endDateTime <= startDateTime) {
      setMessage({ text: t('admin.approveInvalidHours'), type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
      return;
    }

    setApprovingPropertyId(booking.id);
    try {
      const token = getAuthToken();
      const propRecord = properties.find((p) => p.id === booking.propertyId);
      const serviceIdDraft = getApproveAmount(booking.id, 'serviceId') || propRecord?.cleaningServices?.[0]?.id;
      // Een schoonmaaktype is verplicht zodra de woning er heeft (staat standaard
      // op het eerste type, zie de dropdown bij "In te plannen").
      if ((propRecord?.cleaningServices || []).length > 0 && !serviceIdDraft) {
        setMessage({ text: t('admin.cleaningServiceRequired'), type: 'error' });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
        setApprovingPropertyId(null);
        return;
      }
      // Notitie die de admin hier specifiek voor DEZE taak intikt - puur
      // optioneel, geen standaardtekst/vooringevulde waarde meer (de
      // algemene notitie van de woning wordt hier niet meer automatisch in
      // gezet, dat zorgde voor verwarrende dubbele info).
      const notesDraft = getApproveAmount(booking.id, 'notes');
      // Geen Klantprijs/Loon schoonmaker-override meer hier - dat komt al
      // automatisch uit de gekozen Schoonmaakdienst (cleaningServiceId). Nog
      // wel aanpasbaar nadien via "bewerk schoonmaak" bij Ingepland.
      await axios.post(`${API_URL}/api/cleaning-tasks`, {
        bookingId: booking.id,
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        notes: notesDraft || undefined,
        cleaningServiceId: serviceIdDraft || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: t('admin.approveSuccess', { name: booking.property_name }), type: 'success' });
      setApproveAmounts((prev) => { const next = { ...prev }; delete next[booking.id]; return next; });
      fetchUnscheduledBookings();
      fetchScheduledTasks();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.approveError'), type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    } finally {
      setApprovingPropertyId(null);
    }
  };

  // Een volledig nieuwe schoonmaak toevoegen vanuit "In te plannen", zonder
  // dat die aan een bestaande onscheduled booking hangt (bookingId blijft
  // undefined) - zelfde POST /api/cleaning-tasks als handleSaveCleaning in
  // PropertyCalendar.js, enkel met een zelf gekozen woning i.p.v. een vaste
  // propertyId-prop.
  const handleAddCleaning = async (e) => {
    e.preventDefault();
    if (!newCleaning.propertyId || !newCleaning.date || !newCleaning.startTime || !newCleaning.endTime) {
      setMessage({ text: t('admin.addCleaningMissingFields'), type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
      return;
    }
    const [y, m, d] = newCleaning.date.split('-').map(Number);
    const [startH, startM] = newCleaning.startTime.split(':').map(Number);
    const [endH, endM] = newCleaning.endTime.split(':').map(Number);
    const startDateTime = new Date(y, m - 1, d, startH, startM, 0, 0);
    const endDateTime = new Date(y, m - 1, d, endH, endM, 0, 0);
    if (endDateTime <= startDateTime) {
      setMessage({ text: t('admin.approveInvalidHours'), type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
      return;
    }

    setAddingCleaning(true);
    try {
      const token = getAuthToken();
      await axios.post(`${API_URL}/api/cleaning-tasks`, {
        propertyId: newCleaning.propertyId,
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        notes: newCleaning.notes || undefined,
        cleaningServiceId: newCleaning.cleaningServiceId || properties.find((p) => p.id === newCleaning.propertyId)?.cleaningServices?.[0]?.id || undefined,
        expectedCleanerCount: newCleaning.cleanerCount || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: t('admin.addCleaningSuccess'), type: 'success' });
      setShowAddCleaningModal(false);
      setNewCleaning({ propertyId: '', date: '', startTime: '', endTime: '', cleaningServiceId: '', notes: '', access: '', cleanerCount: '' });
      fetchUnscheduledBookings();
      fetchScheduledTasks();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.addCleaningError'), type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    } finally {
      setAddingCleaning(false);
    }
  };

  const handleConfirmCandidate = async (task) => {
    try {
      const token = getAuthToken();
      await axios.post(`${API_URL}/api/cleaning-tasks/${task.id}/assign-cleaner`, {
        cleanerId: task.cleanerId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: t('admin.cleanerConfirmedForTask', { name: task.cleaner_name }), type: 'success' });
      fetchScheduledTasks();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.confirmError'), type: 'error' });
    }
  };

  const handleManualAssign = async (taskId, cleanerId) => {
    if (!cleanerId) return;
    try {
      const token = getAuthToken();
      await axios.post(`${API_URL}/api/cleaning-tasks/${taskId}/assign-cleaner`, {
        cleanerId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: t('admin.cleanerAssigned'), type: 'success' });
      setManualAssignSearch((prev) => { const next = { ...prev }; delete next[taskId]; return next; });
      fetchScheduledTasks();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.assignError'), type: 'error' });
    }
  };

  // Wisselt een reserve in als (nieuwe) hoofd-schoonmaker voor een taak die al
  // een schoonmaker heeft - hergebruikt hetzelfde endpoint als een gewone
  // toewijzing (de backend herkent zelf dat er al een andere schoonmaker op
  // stond en trekt dan diens toegangscode in + verwittigt hem/haar).
  const handleSwapReserve = async (task, reserve) => {
    askConfirm(t('admin.confirmSwapReserve', { name: reserve.cleaner_name }), async () => {
      try {
        const token = getAuthToken();
        await axios.post(`${API_URL}/api/cleaning-tasks/${task.id}/assign-cleaner`, {
          cleanerId: reserve.cleanerId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ text: t('admin.reserveSwapped', { name: reserve.cleaner_name }), type: 'success' });
        fetchScheduledTasks();
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        setMessage({ text: error.response?.data?.error || t('admin.assignError'), type: 'error' });
      }
    });
  };

  // Voegt een 2de/3de schoonmaker toe aan een reeds toegewezen taak - vult
  // altijd beide velden mee door (i.p.v. enkel het gewijzigde), want de
  // backend-endpoint verwacht cleanerId2 én cleanerId3 samen (zie
  // PUT /api/cleaning-tasks/:id/co-cleaners), niet één losse patch.
  const handleAddCoCleaner = async (task, cleanerId) => {
    if (!cleanerId) return;
    const slot = task.cleanerId2 ? 'cleanerId3' : 'cleanerId2';
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/api/cleaning-tasks/${task.id}/co-cleaners`, {
        cleanerId2: slot === 'cleanerId2' ? cleanerId : task.cleanerId2,
        cleanerId3: slot === 'cleanerId3' ? cleanerId : task.cleanerId3,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: t('admin.coCleanerAdded'), type: 'success' });
      setCoCleanerSearch((prev) => { const next = { ...prev }; delete next[task.id]; return next; });
      fetchScheduledTasks();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.assignError'), type: 'error' });
    }
  };

  const handleRemoveCoCleaner = async (task, slot) => {
    askConfirm(t('admin.confirmRemoveCoCleaner'), async () => {
      try {
        const token = getAuthToken();
        await axios.put(`${API_URL}/api/cleaning-tasks/${task.id}/co-cleaners`, {
          cleanerId2: slot === 'cleanerId2' ? null : task.cleanerId2,
          cleanerId3: slot === 'cleanerId3' ? null : task.cleanerId3,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchScheduledTasks();
      } catch (error) {
        setMessage({ text: error.response?.data?.error || t('admin.withdrawError'), type: 'error' });
      }
    });
  };

  const handleRemoveCleanerFromTask = (task, cleanerId) => {
    askConfirm(t('admin.confirmRemoveCoCleaner'), async () => {
      try {
        const token = getAuthToken();
        await axios.post(`${API_URL}/api/cleaning-tasks/${task.id}/remove-cleaner`, { cleanerId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchScheduledTasks();
      } catch (error) {
        setMessage({ text: error.response?.data?.error || t('admin.withdrawError'), type: 'error' });
      }
    });
  };

  const handleUnassignTask = async (taskId) => {
    askConfirm(t('admin.confirmWithdrawTask'), async () => {
      try {
        const token = getAuthToken();
        await axios.post(`${API_URL}/api/cleaning-tasks/${taskId}/unassign`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ text: t('admin.taskReopened'), type: 'success' });
        fetchScheduledTasks();
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        setMessage({ text: error.response?.data?.error || t('admin.withdrawError'), type: 'error' });
      }
    });
  };

  const handleUncompleteTask = async (taskId) => {
    try {
      const token = getAuthToken();
      await axios.post(`${API_URL}/api/cleaning-tasks/${taskId}/uncomplete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: t('admin.taskReverted'), type: 'success' });
      fetchScheduledTasks();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.revertError'), type: 'error' });
    }
  };

  // Bulk: alle voltooide taken van 1 schoonmaker in 1 keer "Ingeboekt" zetten
  // (Uitbetaling-tab) - de opgetelde uren voor die schoonmaker starten
  // vervolgens weer vanaf 0, want enkel nog-niet-ingeboekte (status
  // COMPLETED) taken tellen mee in het getoonde totaal.
  const handlePayoutCleaner = async (cleanerId) => {
    askConfirm(t('admin.confirmPayout'), async () => {
      setPayoutSubmittingId(cleanerId);
      try {
        const token = getAuthToken();
        await axios.post(`${API_URL}/api/cleaning-tasks/payout/${cleanerId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ text: t('admin.payoutDone'), type: 'success' });
        fetchScheduledTasks();
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        setMessage({ text: error.response?.data?.error || t('admin.payoutError'), type: 'error' });
      } finally {
        setPayoutSubmittingId(null);
      }
    });
  };

  // Bulk: alle openstaande (nog niet aan de klant gefactureerde)
  // schoonmaakbeurten van 1 verhuurder in 1 keer als "Gefactureerd" markeren
  // (Te factureren-tab) - onafhankelijk van de uitbetalingsstatus hierboven.
  const handleInvoiceOwner = async (ownerId, ownerName) => {
    askConfirm(t('admin.confirmInvoiceOwner', { name: ownerName || t('admin.unknown') }), async () => {
      setInvoiceOwnerSubmittingId(ownerId);
      try {
        const token = getAuthToken();
        // Geen "month" meer meesturen: "Te factureren" toont bij "Openstaande"
        // nu bewust ALLE nog openstaande maanden (zie invoiceBaseTasks
        // hierboven), dus deze knop moet ook echt alles markeren wat je op
        // dat moment ziet staan - niet enkel de lopende maand.
        await axios.post(`${API_URL}/api/cleaning-tasks/invoice-owner/${ownerId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ text: t('admin.invoiceOwnerDone'), type: 'success' });
        fetchScheduledTasks();
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        setMessage({ text: error.response?.data?.error || t('admin.invoiceOwnerError'), type: 'error' });
      } finally {
        setInvoiceOwnerSubmittingId(null);
      }
    });
  };

  // Keurt een manueel door een verhuurder toegevoegde schoonmaaktaak goed
  // (status PENDING_APPROVAL -> OPEN) - wordt dan meteen zichtbaar voor
  // schoonmakers. Weigeren gebeurt gewoon via de bestaande handleDeleteTask
  // hieronder (verwijdert de taak, verhuurder kan indien nodig opnieuw
  // toevoegen).
  const [approvingTaskId, setApprovingTaskId] = useState(null);
  const handleApprovePendingTask = async (taskId) => {
    setApprovingTaskId(taskId);
    try {
      const token = getAuthToken();
      await axios.post(`${API_URL}/api/cleaning-tasks/${taskId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: t('admin.pendingApprovalApproved'), type: 'success' });
      fetchScheduledTasks();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.pendingApprovalApproveError'), type: 'error' });
    } finally {
      setApprovingTaskId(null);
    }
  };

  const handleDeleteTask = async (taskId) => {
    askConfirm(t('calendar.confirmDeleteCleaning'), async () => {
      try {
        const token = getAuthToken();
        await axios.delete(`${API_URL}/api/cleaning-tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ text: t('calendar.cleaningDeleted'), type: 'success' });
        fetchScheduledTasks();
        // De onderliggende boeking blijft bestaan (enkel het geplande
        // schoonmaakmoment zelf wordt verwijderd, zie DELETE
        // /api/cleaning-tasks/:id) en hoort dus meteen terug tussen "In te
        // plannen" te verschijnen - zonder deze refetch bleef die tab de oude
        // lijst tonen tot een handmatige paginaverversing.
        fetchUnscheduledBookings();
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        setMessage({ text: error.response?.data?.error || t('dashboard.deleteError'), type: 'error' });
      }
    });
  };

  const openEditTaskModal = (task) => {
    setEditingTask({
      ...task,
      startDateInput: toDateTimeLocalInputValue(task.scheduledAt),
      endDateInput: toDateTimeLocalInputValue(task.cleaningEnd),
      notesInput: task.notes || '',
      expectedCleanerCountInput: task.expectedCleanerCount ?? '',
      // Bewust de RUWE override-velden (task.overrideInvoicePrice/
      // overridePayoutAmount), niet de al berekende/COALESCE'de
      // task.fixed_cleaning_price - anders zou dit veld bij het openen al de
      // standaardprijs van de woning tonen alsof dat een bewuste override was,
      // en zou opslaan zonder wijziging die standaardprijs per ongeluk
      // vastpinnen op taakniveau.
      invoicePriceInput: task.overrideInvoicePrice ?? '',
      payoutAmountInput: task.overridePayoutAmount ?? '',
      // Gekozen schoonmaakdienst (PropertyCleaningService) - als de taak er
      // nog geen heeft (ouder dan de diensten-feature), meteen de eerste
      // dienst van de woning voorstellen i.p.v. leeg (zie dropdown
      // hieronder, geen "Automatisch (standaard)"-optie meer). Enkel als de
      // woning zelf geen enkele dienst heeft blijft dit leeg - dan valt het
      // bij opslaan terug op de legacy vaste velden, zie cleaningServiceId
      // resolutie in PUT /api/cleaning-tasks/:id.
      // NOOIT stilzwijgend een dienst invullen: een dienst bepaalt prijs/uitbetaling.
      cleaningServiceIdInput: task.cleaning_service_id || '',
      // Koppeling met een boeking (CalendarEvent) - leeg laten/op "" zetten
      // ontkoppelt de taak terug (wordt dan een losstaande schoonmaak zonder
      // boeking, net als via "+ Schoonmaak toevoegen"), zie de dropdown
      // hieronder in de modal.
      bookingIdInput: task.bookingId || '',
    });
    setShowEditTaskModal(true);
  };

  // Klik op een schoonmaak-balkje in de bedrijfsbrede multikalender (zie
  // PropertyTimeline's "onEditCleaning"-prop) - zoekt de volledige taak op
  // in de al geladen "scheduledTasks" (dezelfde lijst als "Ingepland", altijd
  // alle taken ongeacht status/datum voor een admin) en opent daarmee gewoon
  // dezelfde "bewerk schoonmaak"-modal als de potloodknop bij Ingepland,
  // i.p.v. het alleen-lezen infovenstertje dat PropertyTimeline anders toont.
  const handleEditCleaningFromTimeline = (cleaningId) => {
    const task = scheduledTasks.find((t) => t.id === cleaningId);
    if (task) {
      openEditTaskModal(task);
    } else {
      setMessage({ text: t('admin.taskNotFoundForEdit'), type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    }
  };

  // Standaardwaarden voor het "bewerk schoonmaak"-venster: uit de gekozen
  // schoonmaakdienst, met terugval op de vlakke woningvelden.
  const getTaskEditDefaults = (task) => {
    if (!task) return { price: '', payout: '', count: '' };
    const prop = properties.find((p) => p.id === task.propertyId);
    const svc = (prop?.cleaningServices || []).find((x) => x.id === task.cleaningServiceIdInput);
    const num = (v) => (v === null || v === undefined || v === '' || isNaN(Number(v)) ? null : Number(v));
    const price = num(svc?.price) ?? num(prop?.fixedCleaningPrice);
    const count = num(svc?.cleanerCount) ?? num(task.expected_cleaner_count) ?? num(prop?.defaultCleanerCount) ?? 1;
    const hours = num(svc?.hours) ?? num(prop?.cleaningHours);
    const rate = num(svc?.hourlyRate) ?? num(prop?.cleanerHourlyRate);
    const usedCount = num(task.expectedCleanerCountInput) ?? count;
    const payout = hours !== null && rate !== null ? Math.round((hours / Math.max(usedCount, 1)) * rate * 100) / 100 : null;
    return { price: price ?? '', payout: payout ?? '', count };
  };

  const handleEditTask = async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    if (!editingTask.startDateInput || !editingTask.endDateInput) {
      setMessage({ text: t('admin.taskEditDatesRequired'), type: 'error' });
      return;
    }
    if (new Date(editingTask.endDateInput) <= new Date(editingTask.startDateInput)) {
      setMessage({ text: t('admin.taskEditEndBeforeStart'), type: 'error' });
      return;
    }
    if (editingTask.invoicePriceInput !== '' && (isNaN(editingTask.invoicePriceInput) || Number(editingTask.invoicePriceInput) < 0)) {
      setMessage({ text: t('admin.overrideInvalidValue'), type: 'error' });
      return;
    }
    if (editingTask.payoutAmountInput !== '' && (isNaN(editingTask.payoutAmountInput) || Number(editingTask.payoutAmountInput) < 0)) {
      setMessage({ text: t('admin.overrideInvalidValue'), type: 'error' });
      return;
    }
    const editDefaults = getTaskEditDefaults(editingTask);
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/api/cleaning-tasks/${editingTask.id}`, {
        startDate: new Date(editingTask.startDateInput).toISOString(),
        endDate: new Date(editingTask.endDateInput).toISOString(),
        notes: editingTask.notesInput,
        expectedCleanerCount: (editingTask.expectedCleanerCountInput === '' || Number(editingTask.expectedCleanerCountInput) === Number(editDefaults.count)) ? '' : Number(editingTask.expectedCleanerCountInput),
        overrideInvoicePrice: (editingTask.invoicePriceInput === '' || (editDefaults.price !== '' && Number(editingTask.invoicePriceInput) === Number(editDefaults.price) )) ? null : Number(editingTask.invoicePriceInput),
        overridePayoutAmount: (editingTask.payoutAmountInput === '' || (editDefaults.payout !== '' && Number(editingTask.payoutAmountInput) === Number(editDefaults.payout) )) ? null : Number(editingTask.payoutAmountInput),
        cleaningServiceId: editingTask.cleaningServiceIdInput === '' ? null : editingTask.cleaningServiceIdInput,
        bookingId: editingTask.bookingIdInput === '' ? null : editingTask.bookingIdInput,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: t('admin.taskEditSaved'), type: 'success' });
      setShowEditTaskModal(false);
      setEditingTask(null);
      fetchScheduledTasks();
      // Ook de ongeplande-boekingen-lijst verversen: als hier net een boeking
      // gekoppeld/ontkoppeld werd, moet die meteen uit/in de "Koppel aan
      // boeking"-dropdown van ANDERE taken verdwijnen/verschijnen - zonder
      // deze refetch bleef zo'n al-gekoppelde boeking nog aanklikbaar staan
      // als optie elders, tot de volgende keer dat deze lijst toevallig ververste.
      fetchUnscheduledBookings();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.taskOverrideSaveError'), type: 'error' });
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!validateRequiredFields(e.target) || !newUser.email || !newUser.role) {
      setMessage({ text: t('admin.emailRoleRequired'), type: 'error' });
      return;
    }
    try {
      const token = getAuthToken();
      const response = await axios.post(`${API_URL}/api/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: response.data.message, type: 'success' });
      setShowUserModal(false);
      setNewUser({ email: '', role: 'VERHUURDER' });
      fetchUsers();
      fetchUsersList();
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.addError'), type: 'error' });
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    if (!validateRequiredFields(e.target)) {
      setMessage({ text: t('common.fillRequired'), type: 'error' });
      return;
    }

    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/api/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role,
        straat: editingUser.straat,
        huisnummer: editingUser.huisnummer,
        postcode: editingUser.postcode,
        stad: editingUser.stad,
        phone: editingUser.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (editingUser.role === 'SCHOONMAKER') {
        await axios.put(`${API_URL}/api/admin/users/${editingUser.id}/properties`, {
          propertyIds: editingUser.cleanerPropertyIds || [],
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setMessage({ text: t('admin.userUpdated'), type: 'success' });
      setShowEditUserModal(false);
      setEditingUser(null);
      fetchUsers();
      fetchUsersList();
      fetchProperties();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('dashboard.updateError'), type: 'error' });
    }
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    if (!validateRequiredFields(e.target) || !newProperty.name || !newProperty.ownerId) {
      setMessage({ text: t('admin.nameOwnerRequired'), type: 'error' });
      return;
    }
    try {
      const token = getAuthToken();
      const createRes = await axios.post(`${API_URL}/api/admin/properties`, newProperty, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const createdId = createRes.data?.property?.id;
      if (createdId && (newProperty.accessManagementEnabled || newProperty.automationEnabled)) {
        const others = properties.filter((p) => p.ownerId === newProperty.ownerId);
        const automationIds = others.filter((p) => p.automationEnabled).map((p) => p.id).concat(newProperty.automationEnabled ? [createdId] : []);
        const accessIds = others.filter((p) => p.accessManagementEnabled || p.automationEnabled).map((p) => p.id).concat([createdId]);
        await axios.put(`${API_URL}/api/users/${newProperty.ownerId}`, {
          hasSubscription: true,
          subscriptionPropertyCount: accessIds.length,
          hasAutomation: automationIds.length > 0,
          automationPropertyCount: automationIds.length,
        }, { headers: { Authorization: `Bearer ${token}` } });
        await axios.put(`${API_URL}/api/users/${newProperty.ownerId}/subscription-properties`, {
          accessManagementPropertyIds: accessIds,
          automationPropertyIds: automationIds,
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setMessage({ text: t('admin.propertyAdded'), type: 'success' });
      fetchUsersList();
      fetchSubscriptionInvoices();
      setShowPropertyModal(false);
      setNewProperty({
        name: '',
        address: '',
        ownerId: '',
        airbnbIcalUrl: '',
        bookingIcalUrl: '',
        vrboIcalUrl: '',
        guestyIcalUrl: '',
        guestyListingId: '',
        hospitableIcalUrl: '',
        bookingSheetId: '',
        nukiSmartlockId: '',
        smartlockBrand: '',
        nukiApiToken: '',
        cleaningTier: 'FULL',
        occasionalCleaningRanges: [],
        propertyType: '',
        fixedCleaningPrice: '',
        automationMonthlyPrice: '',
        cleaningHours: '',
        cleanerHourlyRate: '',
        cleaningWindowStart: '',
        cleaningWindowEnd: '',
        cleaningHoursNote: '',
        useGuesty: false,
        useHospitable: false,
        useDirect: false,
        useSheet: false,
        accessManagementEnabled: false,
        automationEnabled: false
      });
      setUserSearchTerm('');
      fetchProperties();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.addError'), type: 'error' });
    }
  };

  const handleEditProperty = async (e) => {
    e.preventDefault();
    if (!editingProperty) return;
    if (!validateRequiredFields(e.target) || !editingProperty.ownerId) {
      setMessage({ text: t('admin.nameOwnerRequired'), type: 'error' });
      return;
    }
    // Er bestaat geen automatische "Standaard"-dienst meer - een woning mag
    // dus ook gewoon 0 schoonmaakdiensten hebben (dan gelden de legacy
    // vlakke velden van de woning). Enkel ECHT ingevulde rijen (met naam)
    // meesturen; lege, nog niet ingevulde rijen die de admin per ongeluk
    // liet staan worden gewoon genegeerd i.p.v. de hele opslag te blokkeren.
    const cleaningServicesToSave = (editingProperty.cleaningServices || []).filter((s) => (s.name || '').trim());

    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/api/admin/properties/${editingProperty.id}`, {
        name: editingProperty.name,
        address: editingProperty.address,
        ownerId: editingProperty.ownerId,
        airbnbIcalUrl: editingProperty.airbnbIcalUrl,
        bookingIcalUrl: editingProperty.bookingIcalUrl,
        vrboIcalUrl: editingProperty.vrboIcalUrl,
        guestyIcalUrl: editingProperty.guestyIcalUrl,
        guestyListingId: editingProperty.guestyListingId,
        hospitableIcalUrl: editingProperty.hospitableIcalUrl,
        bookingSheetId: editingProperty.bookingSheetId,
        nukiSmartlockId: editingProperty.nukiSmartlockId,
        smartlockBrand: editingProperty.smartlockBrand,
        nukiApiToken: editingProperty.nukiApiToken,
        autoAccessCodeEmailsEnabled: editingProperty.autoAccessCodeEmailsEnabled === true,
        cleaningTier: editingProperty.cleaningTier || 'FULL',
        occasionalCleaningRanges: editingProperty.cleaningTier === 'PARTIAL' ? (editingProperty.occasionalCleaningRanges || []) : [],
        propertyType: editingProperty.propertyType,
        fixedCleaningPrice: editingProperty.fixedCleaningPrice,
        automationMonthlyPrice: editingProperty.automationMonthlyPrice,
        cleaningHours: editingProperty.cleaningHours,
        cleanerHourlyRate: editingProperty.cleanerHourlyRate,
        cleaningWindowStart: editingProperty.cleaningWindowStart,
        cleaningWindowEnd: editingProperty.cleaningWindowEnd,
        cleaningHoursNote: editingProperty.cleaningHoursNote,
        checkInTime: editingProperty.checkInTime || '',
        checkOutTime: editingProperty.checkOutTime || '',
        defaultCleanerCount: editingProperty.defaultCleanerCount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Toegangsbeheer/Automation staan per accommodatie aan; de abonnement-
      // vlaggen en aantallen van de eigenaar volgen daaruit.
      if (editingProperty.ownerId) {
        const others = properties.filter((p) => p.ownerId === editingProperty.ownerId && p.id !== editingProperty.id);
        const automationOn = editingProperty.automationEnabled === true;
        // Einddatum al voorbij = Toegangsbeheer is gestopt: deze woning telt dan
        // niet meer mee (tenzij Automation, dat is gratis en blijft werken).
        const todayStr = toDateInputValue(new Date());
        const accessEnded = !!editingProperty.ownerAccessEnd && editingProperty.ownerAccessEnd < todayStr;
        const accessOn = (editingProperty.accessManagementEnabled === true && !accessEnded) || automationOn;
        const accessIds = others.filter((p) => (p.accessManagementEnabled && !accessEnded) || p.automationEnabled).map((p) => p.id).concat(accessOn ? [editingProperty.id] : []);
        const automationIds = others.filter((p) => p.automationEnabled).map((p) => p.id).concat(automationOn ? [editingProperty.id] : []);
        // Begin-/einddatum horen bij de verhuurder; enkel meesturen als de
        // eigenaar niet gewijzigd werd tijdens het bewerken.
        // Staat de schakelaar uit, dan sturen we geen datums mee (de server stopt het abonnement
        // zelf); de ingevulde datums blijven in het formulier bewaard als je ze weer aanzet.
        const accessSwitchOn = editingProperty.accessManagementEnabled === true || editingProperty.automationEnabled === true;
        const accessDates = (editingProperty.ownerAccessOwnerId === editingProperty.ownerId && accessSwitchOn)
          ? { subscriptionStartedAt: editingProperty.ownerAccessStart || null, subscriptionEndsAt: editingProperty.ownerAccessEnd || null }
          : {};
        await axios.put(`${API_URL}/api/users/${editingProperty.ownerId}`, {
          hasSubscription: accessIds.length > 0,
          subscriptionPropertyCount: accessIds.length,
          hasAutomation: automationIds.length > 0,
          automationPropertyCount: automationIds.length,
          ...accessDates,
        }, { headers: { Authorization: `Bearer ${token}` } });
        await axios.put(`${API_URL}/api/users/${editingProperty.ownerId}/subscription-properties`, {
          accessManagementPropertyIds: accessIds,
          automationPropertyIds: automationIds,
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      await axios.put(`${API_URL}/api/admin/properties/${editingProperty.id}/default-cleaners`, {
        cleanerIds: editingProperty.defaultCleanerIds || []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // PUT .../cleaning-services weigert een lege array (die endpoint
      // bewaakt enkel dat je niet de LAATSTE overblijvende dienst van een
      // woning die er al diensten heeft kan wegsturen) - dus enkel aanroepen
      // als er effectief iets is om op te slaan. Een woning die nooit
      // diensten kreeg, blijft gewoon op 0 staan en valt verder terug op de
      // legacy vlakke velden.
      if (cleaningServicesToSave.length > 0) {
        await axios.put(`${API_URL}/api/admin/properties/${editingProperty.id}/cleaning-services`, {
          // Nieuwe, nog niet opgeslagen diensten hebben een lokaal "temp-"-id
          // (zie addCleaningService hieronder) - die niet meesturen, de backend
          // maakt er zelf een echt id voor aan.
          services: cleaningServicesToSave.map((s) => ({
            id: s.id && !String(s.id).startsWith('temp-') ? s.id : undefined,
            name: s.name,
            price: s.price === '' ? null : s.price,
            hours: s.hours === '' ? null : s.hours,
            hourlyRate: s.hourlyRate === '' ? null : s.hourlyRate,
            cleanerCount: s.cleanerCount === '' ? null : s.cleanerCount,
            maxCleanerCount: (s.maxCleanerCount === '' || s.maxCleanerCount === undefined) ? null : s.maxCleanerCount,
            windowStart: s.windowStart === '' ? null : s.windowStart,
            windowEnd: s.windowEnd === '' ? null : s.windowEnd,
          }))
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setMessage({ text: t('dashboard.propertyUpdated'), type: 'success' });
      setShowEditPropertyModal(false);
      setEditingProperty(null);
      fetchProperties();
      fetchUsersList();
      fetchUsers();
      fetchSubscriptionInvoices();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('dashboard.updateError'), type: 'error' });
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: t('admin.userDeleted', { name: userName }), type: 'success' });
      setDeleteConfirm(null);
      fetchUsers();
      fetchUsersList();
      fetchProperties();
      fetchUnscheduledBookings();
      fetchScheduledTasks();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('dashboard.deleteError'), type: 'error' });
    }
  };

  // Herstelt een account dat zichzelf via "Account verwijderen" (profielmenu
  // in de app) had gedeactiveerd - de admin kiest dit i.p.v. de aanvraag te
  // verwerken, bv. als de gebruiker van gedachten veranderde. Geen apart
  // bevestigingsmodaltje (in tegenstelling tot verwijderen): dit is niet
  // destructief, gewoon meteen uitvoeren.
  const handleReactivateUser = async (userId, userName) => {
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/api/users/${userId}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: t('admin.userReactivated', { name: userName }), type: 'success' });
      fetchUsers();
      fetchUsersList();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('dashboard.deleteError'), type: 'error' });
    }
  };

  const handleDeleteProperty = async (propertyId, propertyName) => {
    askConfirm(t('admin.confirmDeletePropertyText', { name: propertyName }), async () => {
      try {
        const token = getAuthToken();
        await axios.delete(`${API_URL}/api/admin/properties/${propertyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ text: t('admin.propertyDeleted', { name: propertyName }), type: 'success' });
        fetchProperties();
        fetchUnscheduledBookings();
        fetchScheduledTasks();
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        setMessage({ text: t('dashboard.deleteError'), type: 'error' });
      }
    });
  };

  const handleApplyLockDefaults = async (propertyId) => {
    setApplyingLockDefaultsId(propertyId);
    try {
      const token = getAuthToken();
      await axios.post(`${API_URL}/api/admin/properties/${propertyId}/smartlock-apply-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ text: t('admin.lockDefaultsApplied'), type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: t('admin.lockDefaultsError'), type: 'error' });
    } finally {
      setApplyingLockDefaultsId(null);
    }
  };

  // "Inloggen als" - opent een NIEUW tabblad ingelogd als deze gebruiker,
  // zonder de eigen (echte) sessie van de admin in dit tabblad te raken (zie
  // utils/authSession.js en pages/ImpersonateLanding.js). Het lege tabblad
  // wordt hier meteen, synchroon bij de klik, geopend - browsers blokkeren
  // een window.open() die pas ná een await gebeurt vaak als popup.
  //
  // In de native app (1 enkele WebView, geen "tabbladen") bestaat
  // window.open()/target=_blank niet - Capacitor probeert dat dan als een
  // externe URL door het besturingssysteem te laten openen, wat crasht
  // ("Failed to open URL... invalid input parameters"). Daarom in de native
  // app gewoon in dezelfde WebView doornavigeren; sessionStorage isoleert de
  // geïmpersoneerde sessie nog steeds van de echte (localStorage) admin-login,
  // en "terug naar admin" (zie Dashboard.js/CleanerDashboard.js) navigeert om
  // diezelfde reden ook al gewoon in dezelfde context terug.
  //
  // Let op: hier moet react-router's navigate() gebruikt worden, GEEN
  // window.location.href. "/impersonate?..." is een client-side route, geen
  // echt bestand - de native app serveert enkel de gebundelde bestanden
  // (index.html, static/...) vanuit een lokale schijfmap, zonder de
  // server-side "geef altijd index.html terug"-fallback die smartstays.be
  // (via Nginx) wél heeft. Een volledige page-load naar dat pad vindt dus
  // simpelweg niets en laadt stil niets in (vandaar "dit opent niets") -
  // terwijl navigate() gewoon binnen de al geladen React-app blijft.
  const handleImpersonate = async (targetUser) => {
    const isNative = localStorage.getItem('isNativeApp') === 'true';
    // Let op: GEEN 'noopener' hier - dat zou window.open() altijd null laten
    // teruggeven, waardoor we straks geen referentie meer hebben om dit
    // tabblad nog naar de echte bestemming te sturen (het bleef dan hangen
    // op about:blank terwijl de fallback hieronder een tweede, vaak door de
    // browser geblokkeerd tabblad probeerde te openen).
    const newTab = isNative ? null : window.open('', '_blank');
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API_URL}/api/admin/impersonate/${targetUser.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { token: impersonationToken, user: impersonatedUser } = response.data;
      const url = `/impersonate?token=${encodeURIComponent(impersonationToken)}&user=${encodeURIComponent(JSON.stringify(impersonatedUser))}`;
      if (isNative) {
        navigate(url);
      } else if (newTab) {
        newTab.location.href = url;
      } else {
        // Toch geblokkeerd (bv. strengere browserinstellingen) - probeer alsnog.
        window.open(url, '_blank', 'noopener');
      }
    } catch (error) {
      if (newTab) newTab.close();
      setMessage({ text: error.response?.data?.error || t('admin.impersonateError'), type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    }
  };

  const handleLogout = () => {
    // In principe komt een admin hier nooit via impersonatie (inloggen als
    // een andere ADMIN wordt server-side geblokkeerd), maar voor de
    // consistentie met Dashboard.js/CleanerDashboard.js toch dezelfde check.
    if (isImpersonating()) {
      stopImpersonating();
      window.location.href = '/admin';
      return;
    }

    const isNative = localStorage.getItem('isNativeApp') === 'true';
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Zorgt dat een volgende login (ook binnen hetzelfde tabblad/dezelfde
    // browsersessie) opnieuw op Boekhouding start i.p.v. de sessionStorage-
    // vlag hierboven te laten staan van vóór het uitloggen.
    sessionStorage.removeItem('adminTabInitialized');

    if (isNative) {
      window.location.href = '/splash';
    } else {
      window.location.href = '/';
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Haalt de te vergelijken waarde op voor een kolom in de Woningen-tabel.
  // Voor kolommen die enkel als "✓"/"-" getoond worden (Airbnb/Booking iCal)
  // sorteert "hoog naar laag" op gekoppeld/niet-gekoppeld. De Nuki-slot kolom
  // sorteert op het live batterijpercentage (indien al opgehaald); niet-
  // gekoppelde woningen komen altijd onderaan/bovenaan via -1.
  // Leesbare "Boekingsbron"-tekst voor de woningenlijst (Gegevens-tab) en de
  // bijhorende sortering hierboven. Toonde voorheen enkel "Guesty"/
  // "Hospitable" (of "-") - miste dus de "Rechtstreeks"-optie (los
  // ingevulde Airbnb-/Booking.com-iCal-links, zie syncSourceDirect bij
  // "Woning bewerken") die daardoor als "-" leek te staan, ook al was er wel
  // degelijk een bron gekoppeld. Een woning kan bij "Rechtstreeks" zowel een
  // Airbnb- als een Booking.com-link tegelijk hebben (2 losse platforms,
  // geen kanaalbeheerder) - toon dan beide, samengevoegd.
  const getBookingSourceLabel = (p) => {
    if (p.guestyIcalUrl || p.guestyListingId) return 'Guesty';
    if (p.hospitableIcalUrl) return 'Hospitable';
    if (p.bookingSheetId) return 'Google Sheets';
    const direct = [];
    if (p.airbnbIcalUrl) direct.push('Airbnb');
    if (p.bookingIcalUrl) direct.push('Booking.com');
    if (p.vrboIcalUrl) direct.push('Vrbo');
    return direct.length > 0 ? direct.join(' + ') : '-';
  };

  const getPropertySortValue = (p, key) => {
    switch (key) {
      case 'name': return (p.name || '').toLowerCase();
      case 'address': return (p.address || '').toLowerCase();
      case 'owner': return (p.owner_name || p.owner_email || '').toLowerCase();
      case 'bookingSource': return getBookingSourceLabel(p).toLowerCase();
      case 'nukiLock': {
        if (!p.nukiSmartlockId) return -1;
        const status = smartlockStatuses[p.id];
        if (status?.reachable && status.batteryCharge != null) return status.batteryCharge;
        return -1;
      }
      case 'cleaningTier': return p.cleaningTier || '';
      default: return '';
    }
  };

  // Drietrapswissel per klik: oplopend -> aflopend -> geen sortering
  // ("ontfilteren", terug naar de oorspronkelijke volgorde).
  const handlePropertySort = (key) => {
    setPropertySortConfig(prev => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'asc' };
    });
  };

  const getPropertySortIcon = (key) => {
    if (propertySortConfig.key !== key) {
      return (
        <span className="sort-icon">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 10L12 5L17 10M7 14L12 19L17 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      );
    }
    return (
      <span className="sort-icon">
        {propertySortConfig.direction === 'asc' ? (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 19L12 5M12 5L7 10M12 5L17 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5L12 19M12 19L7 14M12 19L17 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </span>
    );
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <span className="sort-icon">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 10L12 5L17 10M7 14L12 19L17 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      );
    }
    return (
      <span className="sort-icon">
        {sortConfig.direction === 'asc' ? (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 19L12 5M12 5L7 10M12 5L17 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5L12 19M12 19L7 14M12 19L17 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  };

  const openEditUserModal = (user) => {
    const ownedProps = properties.filter((p) => p.ownerId === user.id);
    setEditingUser({
      ...user,
      // Bestaande gebruikers met Automation/toegangsbeheer aan maar nog geen
      // ingevuld aantal (van vóór deze velden bestonden) krijgen hier alsnog
      // de standaardwaarde 1.
      subscriptionPropertyCount: user.hasSubscription ? (user.subscriptionPropertyCount ?? 1) : user.subscriptionPropertyCount,
      automationPropertyCount: user.hasAutomation ? (user.automationPropertyCount ?? 1) : user.automationPropertyCount,
      // Welke specifieke woningen van deze gebruiker nu al gekoppeld staan aan
      // het toegangsbeheer- resp. Automation-abonnement, zodat de dropdowns
      // hieronder meteen de huidige koppeling tonen i.p.v. altijd leeg te starten.
      accessManagementPropertyIds: ownedProps.filter((p) => p.accessManagementEnabled).map((p) => p.id),
      automationPropertyIds: ownedProps.filter((p) => p.automationEnabled).map((p) => p.id),
      cleanerPropertyIds: properties.filter((p) => (p.defaultCleaners || []).some((c) => c.cleanerId === user.id)).map((p) => p.id),
    });
    setEditingUserTab('details');
    setUserPropertySearch('');
    setShowEditUserModal(true);
  };

  // Toont, per abonnement-type, evenveel dropdowns als het ingestelde aantal
  // ("count"), zodat de admin elke abonnement-plek expliciet aan één van de
  // woningen van deze verhuurder kan koppelen (of leeg laten). Gedeeld tussen
  // toegangsbeheer en Automation hieronder.
  const renderSubscriptionPropertyPickers = ({ count, selectedIds, onChangeAt }) => {
    if (!editingUser) return null;
    const ownedProps = properties.filter((p) => p.ownerId === editingUser.id);
    if (ownedProps.length === 0) {
      return <p className="empty-state-text">{t('admin.subscriptionNoProperties')}</p>;
    }
    return (
      <div className="subscription-property-pickers">
        {Array.from({ length: count }).map((_, i) => (
          <div className="form-group" key={i}>
            <label>{t('admin.subscriptionPropertySlotLabel', { index: i + 1 })}</label>
            <select value={selectedIds[i] || ''} onChange={(e) => onChangeAt(i, e.target.value)}>
              <option value="">{t('admin.subscriptionPropertyNone')}</option>
              {ownedProps.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    );
  };

  const formatOwnerLabel = (name, email) => (
    name ? t('admin.ownerNameEmail', { name, email }) : (email || '')
  );

  // Toont (indien aanwezig) de notitie met gewenste schoonmaakuren en/of de
  // vaste voorkeursvolgorde van schoonmakers van een woning - hergebruikt
  // bij zowel "In te plannen" als "Ingepland" zodat de admin die meteen kan
  // raadplegen zonder apart de woning zelf te moeten openen. Puur
  // informatief, wijst niets automatisch toe.
  const renderDefaultCleanerInfo = (propertyId) => {
    const prop = properties.find((p) => p.id === propertyId);
    if (!prop) return null;
    const cleaners = prop.defaultCleaners || [];
    const hasWindow = prop.cleaningWindowStart || prop.cleaningWindowEnd;
    if (!hasWindow && !prop.cleaningHoursNote && cleaners.length === 0) return null;
    // Alles op 1 compacte regel i.p.v. meerdere gestapelde paragrafen - dit
    // hintje mag niet meer ruimte innemen dan de rest van de kaart.
    const parts = [];
    if (hasWindow) {
      parts.push(`${t('admin.cleaningWindowLabel')}: ${prop.cleaningWindowStart || '?'}-${prop.cleaningWindowEnd || '?'}`);
    }
    if (prop.cleaningHoursNote) {
      parts.push(`${t('admin.extraInfoLabel')}: ${prop.cleaningHoursNote}`);
    }
    if (cleaners.length > 0) {
      parts.push(`${t('admin.defaultCleanersLabel')}: ${cleaners.map((c) => `#${c.position} ${c.cleaner_name || c.cleaner_email}`).join(', ')}`);
    }
    return (
      <p className="default-cleaners-hint">{parts.join('  ·  ')}</p>
    );
  };

  // Rekent het tarief voor 1 ingeplande taak uit: vast bedrag per woning
  // (indien ingesteld), anders het standaardtarief van het pandtype. Zelfde
  // berekening als bij "Te factureren" - dit is wat de klant betaalt voor deze
  // schoonmaak, ongeacht hoeveel schoonmakers ze samen doen.
  const getScheduledTaskPrice = (task) => {
    const hasCustomPrice = task.fixed_cleaning_price !== null && task.fixed_cleaning_price !== undefined;
    return hasCustomPrice ? Number(task.fixed_cleaning_price) : (PROPERTY_TYPE_PRICES[task.property_type] ?? 0);
  };

  // "Duur" bij een ingeplande taak is het aantal uren dat de schoonmaker(s)
  // effectief moeten werken (worked_minutes - al gedeeld door het aantal
  // toegewezen schoonmakers), niet de volledige voorziene tijdsspanne.
  const formatWorkedHours = (minutes) => {
    if (minutes === null || minutes === undefined) return '';
    const total = Math.round(minutes);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')} ${t('cleaner.hoursUnit', { count: 2 })}`;
  };

  // Geeft enkel het uur ('HH:mm') van een kloktijd-correctie terug - de dag
  // is altijd gewoon de dag van de taak zelf, dus de admin hoeft die niet
  // apart in te stellen. Simpeler invoerveld (1 <input type="time"> i.p.v.
  // datum+uur door elkaar) en minder foutgevoelig dan het vorige
  // <input type="datetime-local">.
  const toTimeInputValue = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Combineert de dag van de taak (scheduledAt) met een los ingevuld uur
  // ('HH:mm') tot een volledige Date - zo hoeft de admin bij het rechtzetten
  // van een kloktijd nooit de datum zelf aan te passen.
  const combineTaskDateWithTime = (task, timeValue) => {
    if (!timeValue) return null;
    const [h, m] = timeValue.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    const d = new Date(task.scheduledAt);
    d.setHours(h, m, 0, 0);
    return d;
  };

  // Zet een ISO-datumstring om naar de waarde die <input type="datetime-local">
  // verwacht ('YYYY-MM-DDTHH:mm'), in de LOKALE tijdzone van de browser (geen
  // .toISOString(), dat zou naar UTC omzetten en zo het uur laten verschuiven).
  const toDateTimeLocalInputValue = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Geeft de huidige invoerwaarde van een in-/uitklok-correctieveld terug: de
  // tijdelijke conceptwaarde als de admin net iets gewijzigd heeft, anders
  // gewoon de al opgeslagen waarde van deze taak/schoonmaker als startpunt.
  const getClockTimeDraft = (task, slot, field) => {
    const key = `${task.id}_${slot}`;
    const draft = clockTimeDrafts[key];
    if (draft && field in draft) return draft[field];
    const sourceField = field === 'clockIn' ? `cleaner${slot}ClockInAt` : `cleaner${slot}ClockOutAt`;
    return toTimeInputValue(task[sourceField]);
  };

  const setClockTimeDraftField = (task, slot, field, value) => {
    const key = `${task.id}_${slot}`;
    setClockTimeDrafts((prev) => ({
      ...prev,
      [key]: {
        clockIn: field === 'clockIn' ? value : getClockTimeDraft(task, slot, 'clockIn'),
        clockOut: field === 'clockOut' ? value : getClockTimeDraft(task, slot, 'clockOut'),
      },
    }));
  };

  // Slaat de gecorrigeerde in-/uitkloktijd van 1 specifieke toegewezen
  // schoonmaker (hoofd of co-schoonmaker, aangeduid via "slot") op - geen
  // tijdsvenster-check zoals bij de schoonmaker zijn eigen in-/uitklokken, de
  // admin mag dit altijd rechtzetten (bv. vergeten in/uit te klokken). Belang-
  // rijk: bij een taak met meerdere schoonmakers moet dit voor ELKE toegewezen
  // schoonmaker apart gebeuren voor de taak automatisch op "voltooid" springt.
  //
  // "overrideField/overrideValue" laat toe om de allernieuwste invoer meteen
  // mee te geven i.p.v. te wachten tot de draft-state effectief bijgewerkt is
  // (React batcht state-updates, dus vlak na setClockTimeDraftField zou
  // getClockTimeDraft() hier anders nog de VORIGE waarde kunnen teruggeven).
  const handleSaveClockTimes = async (task, slot, overrideField, overrideValue) => {
    const key = `${task.id}_${slot}`;
    setSavingClockTimeKey(key);
    try {
      const token = getAuthToken();
      const clockInTime = overrideField === 'clockIn' ? overrideValue : getClockTimeDraft(task, slot, 'clockIn');
      const clockOutTime = overrideField === 'clockOut' ? overrideValue : getClockTimeDraft(task, slot, 'clockOut');
      const clockIn = combineTaskDateWithTime(task, clockInTime);
      let clockOut = combineTaskDateWithTime(task, clockOutTime);
      // Nachtwerk-fallback: als de uitkloktijd vóór de inkloktijd valt (bv.
      // in 23:50, uit 00:20), ging het over middernacht - 1 dag bijtellen
      // i.p.v. dit als een (ongeldige) negatieve duur te bewaren.
      if (clockIn && clockOut && clockOut < clockIn) {
        clockOut = new Date(clockOut.getTime() + 24 * 60 * 60 * 1000);
      }
      await axios.put(`${API_URL}/api/cleaning-tasks/${task.id}/clock-times`, {
        slot,
        clockIn: clockIn ? clockIn.toISOString() : null,
        clockOut: clockOut ? clockOut.toISOString() : null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClockTimeDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      fetchScheduledTasks();
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.clockTimesSaveError'), type: 'error' });
    } finally {
      setSavingClockTimeKey(null);
    }
  };

  // Wijzigt 1 kloktijd-veld EN slaat meteen automatisch op - geen aparte
  // "Opslaan"-knop meer nodig. Voorheen moest de admin expliciet op
  // "Opslaan" klikken; vergat die dat, dan leek de invoer nadien gewoon
  // "verdwenen" (ze stond wel nog in de tijdelijke conceptwaarde, maar was
  // nooit effectief naar de server verstuurd).
  const handleClockTimeFieldChange = (task, slot, field, value) => {
    setClockTimeDraftField(task, slot, field, value);
    handleSaveClockTimes(task, slot, field, value);
  };

  // In de praktijk klokt maar 1 schoonmaker effectief in/uit voor de hele
  // taak, ook als er meerdere (co-)schoonmakers toegewezen zijn - apart per
  // schoonmaker tijd laten invoeren heeft dan geen zin. Deze functie past 1
  // algemene correctie toe op ALLE toegewezen schoonmaker-plaatsen tegelijk,
  // zodat de "allClockedOut"-check in de backend (die per-plaats kijkt) toch
  // meteen klopt en de taak normaal automatisch op "voltooid" springt.
  const getAssignedClockSlots = (task) => ['', '2', '3'].filter((slot) =>
    slot === '' ? task.cleaner_name : task[`cleaner${slot}_name`]
  );

  // Wist in- en uitkloktijd van alle toegewezen schoonmakers in 1 keer
  // (een <input type="time"> laat zich op een telefoon niet betrouwbaar leegmaken).
  const handleResetClockTimes = async (task) => {
    const slots = getAssignedClockSlots(task);
    setSavingClockTimeKey(`${task.id}_reset`);
    try {
      const token = getAuthToken();
      for (const slot of slots) {
        await axios.put(`${API_URL}/api/cleaning-tasks/${task.id}/clock-times`, { slot, clockIn: null, clockOut: null }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setClockTimeDrafts((prev) => {
        const next = { ...prev };
        slots.forEach((slot) => { delete next[`${task.id}_${slot}`]; });
        return next;
      });
      fetchScheduledTasks();
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.clockTimesSaveError'), type: 'error' });
    } finally {
      setSavingClockTimeKey(null);
    }
  };

  const handleGeneralClockTimeFieldChange = (task, field, value) => {
    getAssignedClockSlots(task).forEach((slot) => {
      setClockTimeDraftField(task, slot, field, value);
      handleSaveClockTimes(task, slot, field, value);
    });
  };

  // "field" hieronder is telkens de rechtstreekse backend-kolomnaam (PUT
  // /api/cleaning-tasks/:id accepteert die 1-op-1), bv. 'overrideCleaningHours2'
  // voor de uren van de 2de schoonmaker op een taak, of 'overrideInvoicePrice'
  // (geen slot-suffix, want dat blijft 1 gedeelde waarde per taak - de klant
  // wordt niet per schoonmaker apart gefactureerd). Zie schema.prisma bij
  // CleaningSchedule voor het volledige overzicht van deze velden.
  const getOverrideDraft = (task, field) => {
    const key = `${task.id}_${field}`;
    const draft = taskOverrideDrafts[key];
    if (draft !== undefined) return draft;
    const raw = task[field];
    return (raw !== null && raw !== undefined) ? String(raw) : '';
  };

  const setOverrideDraftField = (task, field, value) => {
    const key = `${task.id}_${field}`;
    setTaskOverrideDrafts((prev) => ({ ...prev, [key]: value }));
  };

  // Slaat 1 handmatige correctie op. Leeg gelaten = terug op de normale
  // (gedeelde/berekende) standaardwaarde vallen (null), zelfde principe als
  // bij expectedCleanerCount in PUT /api/cleaning-tasks/:id.
  const handleSaveTaskOverride = async (task, field, forcedRaw) => {
    const key = `${task.id}_${field}`;
    const raw = forcedRaw !== undefined ? forcedRaw : getOverrideDraft(task, field);
    if (raw !== '' && (isNaN(Number(raw)) || Number(raw) < 0)) {
      setMessage({ text: t('admin.overrideInvalidValue'), type: 'error' });
      return;
    }
    setSavingOverrideKey(key);
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/api/cleaning-tasks/${task.id}`, {
        [field]: raw === '' ? null : Number(raw),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskOverrideDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setEditingOverrideKey(null);
      fetchScheduledTasks();
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.taskOverrideSaveError'), type: 'error' });
    } finally {
      setSavingOverrideKey(null);
    }
  };

  // Type schoonmaak (PropertyCleaningService) aanpassen vanuit Uitbetaling /
  // Te factureren. Bepaalt prijs en uren van de taak, dus enkel op expliciete
  // keuze van de admin - nooit automatisch.
  const handleChangeTaskService = async (task, serviceId) => {
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/api/cleaning-tasks/${task.id}`, {
        cleaningServiceId: serviceId === '' ? null : serviceId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchScheduledTasks();
    } catch (error) {
      setMessage({ text: error.response?.data?.error || t('admin.taskOverrideSaveError'), type: 'error' });
    }
  };

  const renderTaskServiceSelect = (task, disabled = false) => {
    const services = properties.find((p) => p.id === task.propertyId)?.cleaningServices || [];
    if (services.length === 0) return task.cleaning_service_name || '-';
    return (
      <select
        className="task-service-select"
        value={task.cleaning_service_id || ''}
        disabled={disabled}
        onChange={(e) => handleChangeTaskService(task, e.target.value)}
      >
        {!task.cleaning_service_id && <option value="">{t('admin.cleaningServiceNoneChosen')}</option>}
        {services.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
    );
  };

  // Herbruikbare cel voor de handmatig overschrijfbare velden (Uitbetaling:
  // uren/bedrag PER schoonmaker-slot, Te factureren: prijs per taak) - toont
  // normaal de berekende waarde (displayNode) met een potloodje ernaast, en
  // klapt bij een klik om naar een invoerveld + vink/kruisje. Een klein
  // potlood-badge blijft zichtbaar naast de weergavewaarde zodra er
  // effectief een override ingesteld staat, zodat meteen duidelijk is dat
  // dit geen berekend cijfer meer is.
  const renderOverridableCell = (task, field, displayNode, placeholder) => {
    const key = `${task.id}_${field}`;
    const isEditing = editingOverrideKey === key;
    const isSaving = savingOverrideKey === key;
    const hasOverride = task[field] !== null && task[field] !== undefined;
    if (isEditing) {
      return (
        <span className="task-override-edit">
          <input
            type="number"
            min="0"
            step="0.01"
            value={getOverrideDraft(task, field)}
            onChange={(e) => setOverrideDraftField(task, field, e.target.value)}
            placeholder={placeholder !== undefined ? String(placeholder) : ''}
            disabled={isSaving}
            autoFocus
          />
          <button type="button" className="task-override-save" onClick={() => handleSaveTaskOverride(task, field)} disabled={isSaving} title={t('common.save')} aria-label={t('common.save')}>
            {isSaving ? '…' : '✓'}
          </button>
          <button type="button" className="task-override-cancel" onClick={() => setEditingOverrideKey(null)} disabled={isSaving} title={t('common.cancel')} aria-label={t('common.cancel')}>
            ✕
          </button>
        </span>
      );
    }
    return (
      <span className="task-override-display">
        {displayNode}
        {hasOverride && (
          <button
            type="button"
            className="task-override-reset"
            title={t('admin.overrideReset')}
            aria-label={t('admin.overrideReset')}
            onClick={() => handleSaveTaskOverride(task, field, '')}
          >↺</button>
        )}
        <button
          type="button"
          onClick={() => setEditingOverrideKey(key)}
          className="action-icon-btn action-icon-edit action-icon-btn-sm"
          title={t('admin.editOverride')}
          aria-label={t('admin.editOverride')}
        >
          <EditIcon />
        </button>
      </span>
    );
  };

  // Zet een datum om naar een 'YYYY-MM-DD'-sleutel in de LOKALE tijdzone van
  // de browser (bewust geen .toISOString(), dat verschuift soms 1 dag door
  // UTC-omzetting) - zelfde manier waarop FullCalendar's eigen
  // "dateClick.dateStr" hieronder de dag van een cel identificeert.
  const toDateKey = (dateInput) => {
    const d = new Date(dateInput);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Kalender voor de vanuit "Gebruikers" aangeklikte schoonmaker - hergebruikt
  // "scheduledTasks" (al opgehaald voor de "Ingepland"-tab, dus geen extra
  // API-call nodig) en filtert op elke taak waar deze schoonmaker ergens in
  // zit (hoofd- of 2de/3de schoonmaker), gegroepeerd per dag. De dagcellen
  // tonen enkel het aantal (zie "dayCellContent" verderop); het overzicht
  // van de effectieve taken verschijnt pas na het aanklikken van een dag.
  const cleanerTasksByDate = useMemo(() => {
    if (!calendarViewCleaner) return {};
    const map = {};
    scheduledTasks
      .filter((task) => [task.cleanerId, task.cleanerId2, task.cleanerId3].includes(calendarViewCleaner.id))
      .forEach((task) => {
        const key = toDateKey(task.scheduledAt);
        if (!map[key]) map[key] = [];
        map[key].push(task);
      });
    return map;
  }, [scheduledTasks, calendarViewCleaner]);

  // Bedrijfsbrede "Kalender"-tab (activeTab === 'calendar'): combineert
  // "scheduledTasks" (alle taken, elke woning) EN "unscheduledBookings"
  // (boekingen die nog geen enkel schoonmaakmoment hebben) tot 1 overzicht
  // per dag, in 4 categorieën:
  // - "assigned" (groen): taak heeft een bevestigde schoonmaker (ASSIGNED/
  //   COMPLETED/INVOICED).
  // - "pending" (oranje): er is al een schoonmaakmoment aangemaakt maar nog
  //   niemand bevestigd (OPEN/PENDING_CONFIRMATION), of een eerder toegewezen
  //   schoonmaker heeft zich teruggetrokken (WITHDRAWN).
  // - "unscheduled" (rood): boeking heeft nog helemaal geen schoonmaakmoment.
  // - "pendingapproval" (paars): door de verhuurder zelf toegevoegde taak die
  //   nog op goedkeuring van de admin wacht (zelfde als de bovenste sectie
  //   van "In te plannen").
  // Gegroepeerd op "scheduledAt" (taken) resp. "checkOut" (boekingen), zelfde
  // datumvelden als de "Ingepland"/"In te plannen"-tabs zelf al gebruiken.
  // Bezetting van een schoonmaakmoment, zelfde kleurregels als de multikalender:
  // 'done' (groen) = voltooid/verstreken, 'unstaffed' (rood) = niemand
  // toegewezen, 'partial' (oranje) = minimum aantal schoonmakers niet bereikt,
  // 'staffed' (blauw) = minimum bereikt.
  const getCleaningStaffing = (task) => {
    const assigned = [task.cleanerId, task.cleanerId2, task.cleanerId3].filter(Boolean).length;
    const expected = Number(task.expected_cleaner_count) || 1;
    const end = new Date(task.cleaningEnd || task.endDate || task.scheduledAt);
    if (['COMPLETED', 'INVOICED'].includes(task.status) || end < new Date()) return 'done';
    if (task.status !== 'ASSIGNED' && assigned === 0) return 'unstaffed';
    return assigned < expected ? 'partial' : 'staffed';
  };

  const businessCalendarDayStats = useMemo(() => {
    const map = {};
    const ensure = (key) => {
      if (!map[key]) map[key] = { done: 0, unstaffed: 0, partial: 0, staffed: 0, unscheduled: 0, pendingApproval: 0, rejected: 0 };
      return map[key];
    };
    scheduledTasks.forEach((task) => {
      if (!task.scheduledAt) return;
      const key = toDateKey(task.scheduledAt);
      // Nog niet ingeplande dingen (wacht op goedkeuring, boeking zonder
      // schoonmaak) komen NIET in de kalender - die staan onder "In te plannen".
      if (['ASSIGNED', 'COMPLETED', 'INVOICED', 'OPEN', 'PENDING_CONFIRMATION', 'WITHDRAWN'].includes(task.status)) {
        ensure(key)[getCleaningStaffing(task)] += 1;
      }
    });
    unscheduledBookings.forEach((booking) => {
      if (!booking.checkOut) return;
      if (booking.cleaningRejected) ensure(toDateKey(booking.checkOut)).rejected += 1;
    });
    return map;
  }, [scheduledTasks, unscheduledBookings]);

  // Zelfde bron/groepering als "businessCalendarDayStats" hierboven, maar dan
  // de effectieve items zelf (i.p.v. enkel de aantallen) - voor het overzicht
  // dat verschijnt na het aanklikken van een dag in de "Kalender"-tab. Bevat
  // ook de toegewezen schoonmaker(s) + geplande uren (assigned/notaccepted),
  // en alles wat nodig is om rechtstreeks vanuit de kalender in te plannen/
  // goed te keuren (unscheduled/pendingapproval), zonder eerst naar
  // "In te plannen" te moeten schakelen.
  const businessCalendarItemsByDate = useMemo(() => {
    const map = {};
    const push = (key, item) => {
      if (!map[key]) map[key] = [];
      map[key].push(item);
    };
    scheduledTasks.forEach((task) => {
      if (!task.scheduledAt) return;
      // 'assigned' = groen (voltooid/verstreken), 'unstaffed' = rood,
      // 'partial' = oranje, 'staffed' = blauw (zie getCleaningStaffing).
      const category = ['ASSIGNED', 'COMPLETED', 'INVOICED', 'OPEN', 'PENDING_CONFIRMATION', 'WITHDRAWN'].includes(task.status)
        ? { done: 'assigned', unstaffed: 'unstaffed', partial: 'partial', staffed: 'staffed' }[getCleaningStaffing(task)]
        : null; // PENDING_APPROVAL (nog niet ingepland) hoort niet in de kalender
      if (!category) return;
      push(toDateKey(task.scheduledAt), {
        id: task.id,
        category,
        time: formatTimeOnly(task.scheduledAt),
        endTime: task.cleaningEnd ? formatTimeOnly(task.cleaningEnd) : null,
        propertyName: task.property_name,
        address: task.address,
        status: task.status,
        cleanerNames: sortedNames([task.cleaner_name, task.cleaner2_name, task.cleaner3_name]),
        clockInAt: task.cleanerClockInAt || null,
        clockOutAt: task.cleanerClockOutAt || null,
        ownerName: task.owner_name,
        notes: task.notes,
        isTask: true,
        start: task.scheduledAt,
        end: task.cleaningEnd || task.endDate || task.scheduledAt,
        propertyId: task.propertyId,
        cleaners: [
          { id: task.cleanerId, name: task.cleaner_name },
          { id: task.cleanerId2, name: task.cleaner2_name },
          { id: task.cleanerId3, name: task.cleaner3_name },
        ].filter((c) => c.id),
        expectedCount: task.expected_cleaner_count,
      });
    });
    unscheduledBookings.forEach((booking) => {
      // Boeking waar nog geen schoonmaak voor ingepland is: niet in de kalender.
      if (!booking.checkOut || !booking.cleaningRejected) return;
      push(toDateKey(booking.checkOut), {
        id: booking.id,
        category: 'rejected',
        time: formatTimeOnly(booking.checkOut),
        endTime: null,
        propertyName: booking.property_name,
        address: booking.address,
        status: null,
        cleanerNames: [],
        propertyId: booking.propertyId,
        checkOutIso: booking.checkOut,
      });
    });
    return map;
  }, [scheduledTasks, unscheduledBookings]);

  // Groepeert "Ingepland" per kalenderdag (chronologisch, oplopend) zodat de
  // admin het net als een boekhouding dag per dag kan raadplegen, inclusief
  // een dagtotaal - i.p.v. één lange, ongestructureerde lijst kaarten.
  const scheduledTasksByDay = useMemo(() => {
    const groups = {};
    filteredScheduledTasks.forEach((task) => {
      const d = new Date(task.scheduledAt);
      const key = isNaN(d) ? 'onbekend' : d.toISOString().slice(0, 10);
      if (!groups[key]) groups[key] = [];
      groups[key].push(task);
    });
    return Object.keys(groups)
      .sort()
      .map((key) => {
        const tasks = [...groups[key]].sort((a, b) => (a.property_name || '').localeCompare(b.property_name || '', 'nl', { sensitivity: 'base' }) || new Date(a.scheduledAt) - new Date(b.scheduledAt));
        return {
          key,
          date: key === 'onbekend' ? null : new Date(`${key}T00:00:00`),
          tasks,
          total: tasks.reduce((sum, task) => sum + getScheduledTaskPrice(task), 0),
        };
      });
  }, [filteredScheduledTasks]);

  // Voltooid-tab: voltooide/gefactureerde taken per dag, recentste dag eerst.
  const completedTasksByDayList = useMemo(() => {
    let list = scheduledTasks.filter((task) => task.status === 'COMPLETED' || task.status === 'INVOICED');
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter((task) =>
        task.property_name?.toLowerCase().includes(term) ||
        task.address?.toLowerCase().includes(term) ||
        task.guest_name?.toLowerCase().includes(term) ||
        task.cleaner_name?.toLowerCase().includes(term)
      );
    }
    const groups = {};
    list.forEach((task) => {
      const d = new Date(task.scheduledAt);
      const key = isNaN(d) ? 'onbekend' : d.toISOString().slice(0, 10);
      if (!groups[key]) groups[key] = [];
      groups[key].push(task);
    });
    return Object.keys(groups).sort().reverse().map((key) => {
      const tasks = [...groups[key]].sort((a, b) => (a.property_name || '').localeCompare(b.property_name || '', 'nl', { sensitivity: 'base' }) || new Date(a.scheduledAt) - new Date(b.scheduledAt));
      return {
        key,
        date: key === 'onbekend' ? null : new Date(`${key}T00:00:00`),
        tasks,
        total: tasks.reduce((sum, task) => sum + getScheduledTaskPrice(task), 0),
      };
    });
  }, [scheduledTasks, searchTerm]);

  // Standaardgedrag: elke taak staat dicht totdat de admin er zelf op klikt
  // - ongeacht status (voorheen stonden OPEN/PENDING_CONFIRMATION-taken
  // standaard al open, wat het overzicht ongevraagd liet openklappen).
  const isScheduledTaskExpanded = (task) => {
    if (task.id in scheduledTaskExpandOverrides) return scheduledTaskExpandOverrides[task.id];
    return false;
  };

  const toggleScheduledTaskExpanded = (task) => {
    setScheduledTaskExpandOverrides((prev) => ({ ...prev, [task.id]: !isScheduledTaskExpanded(task) }));
  };

  // 'YYYY-MM-DD' voor een <input type="date"> uit een tijdstempel van de server.
  const toDateInputValue = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('sv-SE'); // sv-SE = ISO-notatie (YYYY-MM-DD)
  };

  const openEditPropertyModal = (property) => {
    // Begin-/einddatum van het Toegangsbeheer-abonnement horen bij de
    // verhuurder (niet bij 1 woning) - hier getoond bij de Toegangsbeheer-tab.
    const ownerRecord = usersList.find((u) => u.id === property.ownerId) || users.find((u) => u.id === property.ownerId) || {};
    setEditingProperty({
      ...property,
      ownerAccessStart: toDateInputValue(ownerRecord.subscriptionStartedAt),
      ownerAccessEnd: toDateInputValue(ownerRecord.subscriptionEndsAt),
      ownerAccessOwnerId: property.ownerId,
      ownerHasAutomation: ownerRecord.hasAutomation === true,
      ...deriveSourceCheckboxes(property),
      // Vaste voorkeursvolgorde van schoonmakers (zie PropertyDefaultCleaner
      // in het backend-schema) - enkel de id's, al gesorteerd op positie
      // zoals meegeleverd door /api/admin/properties.
      defaultCleanerIds: (property.defaultCleaners || []).map((c) => c.cleanerId),
      // Schoonmaakdiensten (zie PropertyCleaningService) - lokale kopie zodat
      // we vrij kunnen bewerken; pas bij opslaan wordt de volledige lijst in
      // één keer naar /cleaning-services gestuurd. Geen automatische
      // "Standaard"-dienst meer als er nog geen zijn - gewoon een lege lijst,
      // de admin voegt zelf bewust diensten toe via "+ Dienst toevoegen".
      cleaningServices: (property.cleaningServices || []).map((s) => ({ ...s }))
    });
    setEditOwnerSearchTerm(formatOwnerLabel(property.owner_name, property.owner_email));
    setCleaningPdfError('');
    setPhotoError('');
    setDefaultCleanerSearch('');
    setEditPropertyTab('general');
    setShowEditPropertyModal(true);
  };

  // Voegt een schoonmaker toe achteraan de voorkeursvolgorde (laagste
  // voorrang), verwijdert of verwisselt er twee onderling van plek - werkt
  // volledig op de lokale "defaultCleanerIds"-array; pas bij opslaan
  // (handleEditProperty) wordt de volledige lijst in één keer naar de server
  // gestuurd.
  const addDefaultCleaner = (cleanerId) => {
    setEditingProperty((prev) => {
      const current = prev.defaultCleanerIds || [];
      if (current.includes(cleanerId)) return prev;
      return { ...prev, defaultCleanerIds: [...current, cleanerId] };
    });
    setDefaultCleanerSearch('');
  };

  const removeDefaultCleaner = (cleanerId) => {
    setEditingProperty((prev) => ({
      ...prev,
      defaultCleanerIds: (prev.defaultCleanerIds || []).filter((id) => id !== cleanerId)
    }));
  };

  const moveDefaultCleaner = (index, direction) => {
    setEditingProperty((prev) => {
      const list = [...(prev.defaultCleanerIds || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= list.length) return prev;
      [list[index], list[targetIndex]] = [list[targetIndex], list[index]];
      return { ...prev, defaultCleanerIds: list };
    });
  };

  // Schoonmaakdiensten beheren (PropertyCleaningService) - zelfde lokale-state
  // patroon als defaultCleanerIds hierboven: alles werkt op een lokale kopie,
  // pas bij opslaan (handleEditProperty) wordt de volledige lijst in één keer
  // naar de server gestuurd (delete-all-then-reinsert op de backend).
  const addCleaningService = () => {
    setEditingProperty((prev) => ({
      ...prev,
      cleaningServices: [...(prev.cleaningServices || []), { id: `temp-${Date.now()}`, name: '', price: '', hours: '', hourlyRate: '', cleanerCount: '', maxCleanerCount: '', windowStart: '', windowEnd: '' }]
    }));
  };

  const removeCleaningService = (id) => {
    setEditingProperty((prev) => ({
      ...prev,
      cleaningServices: (prev.cleaningServices || []).filter((s) => s.id !== id)
    }));
  };

  const updateCleaningServiceField = (id, field, value) => {
    setEditingProperty((prev) => ({
      ...prev,
      cleaningServices: (prev.cleaningServices || []).map((s) => (s.id === id ? { ...s, [field]: value } : s))
    }));
  };

  // Woningfoto uploaden/vervangen/verwijderen - zelfde patroon als de
  // schoonmaak-PDF hierboven, werkt op editingProperty.
  const handleUploadPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !editingProperty) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setPhotoError(t('admin.photoInvalidType'));
      return;
    }
    setPhotoUploading(true);
    setPhotoError('');
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/properties/${editingProperty.id}/photo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const updated = { ...editingProperty, photoUrl: res.data.photoUrl };
      setEditingProperty(updated);
      setProperties((prev) => prev.map((p) => (p.id === updated.id ? { ...p, photoUrl: updated.photoUrl } : p)));
      setMessage({ text: t('admin.photoUploaded'), type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setPhotoError(error.response?.data?.error || t('admin.photoUploadError'));
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!editingProperty) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/api/properties/${editingProperty.id}/photo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updated = { ...editingProperty, photoUrl: null };
      setEditingProperty(updated);
      setProperties((prev) => prev.map((p) => (p.id === updated.id ? { ...p, photoUrl: null } : p)));
      setMessage({ text: t('admin.photoDeleted'), type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setPhotoError(error.response?.data?.error || t('admin.photoDeleteError'));
    }
  };

  // Schoonmaakinstructies-PDF: geen apart knopje/modal meer, gewoon een sectie
  // onderaan het bestaande "woning bewerken"-formulier (bij de iCal-velden),
  // werkt rechtstreeks op de woning die daar al open staat (editingProperty).
  const handleUploadCleaningPdf = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !editingProperty) return;
    if (file.type !== 'application/pdf') {
      setCleaningPdfError(t('admin.cleaningPdfInvalidType'));
      return;
    }
    setCleaningPdfUploading(true);
    setCleaningPdfError('');
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/properties/${editingProperty.id}/cleaning-pdf`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const updated = { ...editingProperty, cleaningPdfOriginalName: res.data.cleaningPdfOriginalName, cleaningPdfUploadedAt: res.data.cleaningPdfUploadedAt };
      setEditingProperty(updated);
      setProperties((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
      setMessage({ text: t('admin.cleaningPdfUploaded'), type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setCleaningPdfError(error.response?.data?.error || t('admin.cleaningPdfUploadError'));
    } finally {
      setCleaningPdfUploading(false);
    }
  };

  const handleDeleteCleaningPdf = async () => {
    if (!editingProperty) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/api/properties/${editingProperty.id}/cleaning-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updated = { ...editingProperty, cleaningPdfOriginalName: null, cleaningPdfUploadedAt: null };
      setEditingProperty(updated);
      setProperties((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
      setMessage({ text: t('admin.cleaningPdfDeleted'), type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setCleaningPdfError(error.response?.data?.error || t('admin.cleaningPdfDeleteError'));
    }
  };

  const handleViewCleaningPdf = async () => {
    if (!editingProperty) return;
    try {
      await openCleaningPdf(editingProperty.id);
    } catch (error) {
      setCleaningPdfError(t('admin.cleaningPdfLoadError'));
    }
  };

  // Manueel door een verhuurder toegevoegde schoonmaaktaken die nog niet
  // goedgekeurd zijn - staan al gewoon tussen "scheduledTasks" (dezelfde
  // GET /api/cleaning-tasks-call haalt alle statussen op), maar moeten apart
  // getoond worden bij "In te plannen" (met een duidelijk "manueel
  // toegevoegd"-label + Goedkeuren/Weigeren) i.p.v. tussen de gewone
  // "Ingepland"-lijst te verschijnen - zie ook de uitsluiting van
  // "PENDING_APPROVAL" verderop bij het filteren van "Ingepland" zelf.
  const pendingApprovalTasks = scheduledTasks
    .filter((t) => t.status === 'PENDING_APPROVAL')
    .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));

  // Elke niet-ingeplande boeking apart tonen (niet meer gegroepeerd per
  // woning met enkel de dringendste zichtbaar + een teller voor de rest) -
  // zodat een woning met veel losse boekingen tegelijk (bv. via de Google
  // Sheets-sync) ze allemaal meteen onder elkaar ziet staan, elk met een
  // eigen datum/uur/service en een eigen "Goedkeuren"-knop. Gesorteerd op
  // uitchecktijd zodat het dringendste bovenaan staat.
  const bookingsToSchedule = [...filteredUnscheduledBookings]
    .filter((b) => !b.cleaningRejected)
    .sort((a, b) => new Date(a.checkOut) - new Date(b.checkOut));

  // Woningen van dezelfde eigenaar als de vanuit "Woningen" aangeklikte
  // woning - bepaalt welke rijen de multi-kalender toont afhankelijk van
  // calendarViewScope, en of de "alle woningen van deze klant"-optie
  // überhaupt zinvol is om te tonen (enkel als er >1 woning is).
  const calendarViewOwnerProperties = calendarViewProperty
    ? properties.filter((p) => p.ownerId === calendarViewProperty.ownerId)
    : [];
  const calendarViewProperties = !calendarViewProperty
    ? []
    : calendarViewScope === 'owner'
      ? calendarViewOwnerProperties
      : [calendarViewProperty];

  // Woningen van dezelfde eigenaar als de woning waarvoor je vanuit "In te
  // plannen" op "Inboeken"/"Open" klikte - de multi-kalender toont bij het
  // inboeken enkel de woningen van díe klant, niet alle woningen van alle
  // klanten door elkaar.
  const schedulingOwnerProperties = (() => {
    if (!schedulingProperty) return [];
    const fullProperty = properties.find((p) => p.id === schedulingProperty.propertyId);
    if (!fullProperty || !fullProperty.ownerId) return properties;
    return properties.filter((p) => p.ownerId === fullProperty.ownerId);
  })();

  // Woningen die momenteel zichtbaar zijn in de bedrijfsbrede multikalender
  // (activeTab === 'calendar', businessCalendarView === 'multi') - alle
  // woningen minus diegene die de admin er met 1 klik uit verwijderde (zie
  // businessMultiCalendarHiddenIds hierboven).
  const businessMultiCalendarVisibleProperties = properties.filter((p) => !businessMultiCalendarHiddenIds.has(p.id));

  // Of de multi-kalender momenteel getoond wordt (via "Inboeken", "Woningen"
  // of de nieuwe bedrijfsbrede multikalender) - in dat geval breekt de
  // kalender uit de normale 1200px-breedte van het beheerpaneel, zodat hij
  // even groot is als bij de klant (Dashboard.js's .dashboard-main-full
  // heeft ook geen max-breedte).
  const showingWideCalendar = (!!schedulingProperty && scheduleView === 'multi') || !!calendarViewProperty
    || (activeTab === 'calendar' && businessCalendarView === 'multi');

  if (!user) return <div aria-busy="true" />;

  const renderTaskDays = (days, completedMode) => (
            <div className={`scheduled-days ${completedMode ? 'scheduled-days-completed' : ''}`}>
                  <div className="scheduled-task-colheader">
                    <span>{t('admin.colTaskProperty')}</span>
                    <span>{t('admin.colTaskType')}</span>
                    <span>{t('admin.colStatus')}</span>
                    <span>{t('admin.colTaskNote')}</span>
                    <span>{t('admin.colTaskCleaners')}</span>
                    {completedMode && <span>{t('admin.colTaskClock')}</span>}
                    <span className="num">{t('admin.colTaskDuration')}</span>
                    <span className="num">{t('admin.colTaskPrice')}</span>
                    <span></span>
                  </div>
                  {days.map(day => (
                    <div key={day.key} className="scheduled-day-group">
                      <div className="scheduled-day-header">
                        <span className="scheduled-day-date">
                          {day.date ? day.date.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' }) : t('admin.unknownDate')}
                        </span>
                        <span className="scheduled-day-meta">
                          {day.tasks.length} {day.tasks.length === 1 ? t('admin.taskSingular') : t('admin.taskPlural')} · €{day.total}
                        </span>
                      </div>

                      <div className="scheduled-day-tasks">
                        {day.tasks.map(task => {
                          const reserves = task.reserves || [];
                          const startDate = new Date(task.scheduledAt);
                          const endDate = task.cleaningEnd ? new Date(task.cleaningEnd) : startDate;
                          const isPeriod = startDate.toDateString() !== endDate.toDateString();
                          const cleaningPrice = getScheduledTaskPrice(task);
                          const expanded = isScheduledTaskExpanded(task);
                          // Bezetting als kleur (zelfde kleuren als in de agenda): rood = nog
                          // niemand, oranje = wacht op bevestiging of minimum niet bereikt,
                          // blauw = minimum bereikt, groen = voltooid. Enkel gebruikt voor de
                          // kleurstreep in de telefoon-/appweergave (zie Admin.css).
                          const assignedCount = [task.cleanerId, task.cleanerId2, task.cleanerId3].filter(Boolean).length;
                          const minCleaners = Math.max(Number(task.expected_cleaner_count) || 1, 1);
                          const staffing = ['COMPLETED', 'INVOICED'].includes(task.status) ? 'done'
                            : task.status === 'PENDING_CONFIRMATION' ? 'partial'
                            : task.status === 'ASSIGNED' ? (assignedCount < minCleaners ? 'partial' : 'staffed')
                            : 'unstaffed';

                          return (
                            <div key={task.id} className={`scheduled-task-row st-${staffing} ${expanded ? 'expanded' : ''}`}>
                              <div className="scheduled-task-summary" onClick={() => toggleScheduledTaskExpanded(task)}>
                                <span className="scheduled-task-property">{task.property_name}</span>
                                <span className="sts-meta">
                                {/* Vaste kolombreedte en altijd gerenderd (ook leeg) voor zowel
                                    type als notitie, zodat alle rijen mooi op dezelfde breedte
                                    uitgelijnd staan i.p.v. los/random te staan afhankelijk van of
                                    een rij toevallig een type/notitie heeft. Platte tekst, geen
                                    gekleurd badgetje - dat trok te veel aandacht t.o.v. de rest
                                    van de rij. Bij een te lange tekst kapt de kolom af met "...",
                                    de title-tooltip toont dan alsnog de volledige tekst bij hover. */}
                                <span className="scheduled-task-service-hint" title={task.cleaning_service_name || ''}>
                                  {task.cleaning_service_name || ''}
                                </span>
                                <span className="scheduled-task-status-cell">
                                <span className={`status-badge ${(task.status === 'ASSIGNED' || task.status === 'COMPLETED' || task.status === 'INVOICED') ? 'status-active' : task.status === 'WITHDRAWN' ? 'status-withdrawn' : 'status-pending'}`}>
                                  {task.status === 'OPEN' && t('admin.statusOpenTask')}
                                  {task.status === 'PENDING_CONFIRMATION' && t('admin.statusWaitingConfirm')}
                                  {task.status === 'ASSIGNED' && t('admin.statusAssignedTask')}
                                  {task.status === 'WITHDRAWN' && t('admin.statusWithdrawnTask')}
                                  {task.status === 'COMPLETED' && t('admin.statusCompletedTask')}
                                  {task.status === 'INVOICED' && t('admin.statusInvoicedTask')}
                                  {!['OPEN', 'PENDING_CONFIRMATION', 'ASSIGNED', 'WITHDRAWN', 'COMPLETED', 'INVOICED'].includes(task.status) && task.status}
                                </span>
                                </span>
                                </span>
                                {(() => {
                                  // Vroeger (vóór de notitie-herziening bij "In te plannen")
                                  // werd de ALGEMENE "Extra info" van de woning (toegangscodes
                                  // e.d.) automatisch gekopieerd naar de notitie van elke nieuwe
                                  // taak - voor taken van toen staat dat dus nog letterlijk in
                                  // task.notes, wat die identiek maakt aan prop.cleaningHoursNote.
                                  // Dat is geen echte, taak-specifieke notitie (bv. "zonder
                                  // slaapgedeelte", "zelfde dag nieuwe gasten") maar toegangsinfo,
                                  // en hoort hier niet thuis - enkel tonen als het ervan AFWIJKT.
                                  const prop = properties.find((p) => p.id === task.propertyId);
                                  const isLeftoverAccessInfo = prop?.cleaningHoursNote
                                    && task.notes?.trim() === prop.cleaningHoursNote.trim();
                                  const noteText = (!task.notes || isLeftoverAccessInfo) ? '' : task.notes;
                                  return <span className="scheduled-task-note-hint" title={noteText}>{noteText}</span>;
                                })()}
                                {(
                                  <div className="scheduled-task-cleaners" onClick={(e) => e.stopPropagation()}>
                                    {['PENDING_CONFIRMATION', 'WITHDRAWN'].includes(task.status) ? null : <>
                                    {sortedNames([task.cleaner_name, task.cleaner2_name, task.cleaner3_name]).map((name, i) => (
                                      <span className="cleaner-chip" key={`${name}-${i}`}>
                                        {name}
                                      </span>
                                    ))}
                                    </>}
                                  </div>
                                )}
                                {completedMode && (
                                <span className="scheduled-task-clocktimes">
                                  {task.cleaner_name && (task.cleanerClockInAt || task.cleanerClockOutAt) ? (
                                    <span className="clocktime-display" title={t('admin.editClockTimes')}>
                                      {`${task.cleanerClockInAt ? formatTimeOnly(task.cleanerClockInAt) : '–'} - ${task.cleanerClockOutAt ? formatTimeOnly(task.cleanerClockOutAt) : '–'}`}
                                    </span>
                                  ) : (
                                    <span className="scheduled-task-clocktimes-empty">–</span>
                                  )}
                                </span>
                                )}
                                <span className={`scheduled-task-duration ${(completedMode && task.cleanerClockInAt && task.cleanerClockOutAt) || task.worked_minutes ? 'has-hours' : 'no-hours'}`}>
                                  {completedMode && task.cleanerClockInAt && task.cleanerClockOutAt
                                    ? formatWorkedHours(Math.round((new Date(task.cleanerClockOutAt) - new Date(task.cleanerClockInAt)) / 60000))
                                    : (task.worked_minutes ? formatWorkedHours(task.worked_minutes) : '–')}
                                </span>
                                <span className="scheduled-task-price">€{cleaningPrice}</span>
                                <span className="scheduled-task-actions">
                                <button
                                  onClick={(e) => { e.stopPropagation(); openEditTaskModal(task); }}
                                  className="action-icon-btn action-icon-edit"
                                  title={t('common.edit')}
                                  aria-label={t('common.edit')}
                                >
                                  <EditIcon />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                                  className="action-icon-btn action-icon-delete"
                                  title={t('common.delete')}
                                  aria-label={t('common.delete')}
                                >
                                  <TrashIcon />
                                </button>
                                </span>
                              </div>

                              {expanded && (
                                <div className="scheduled-task-details">
                                  <div className="scheduled-task-details-main">
                                    <div className="task-info">
                                      <p><strong>{t('common.address')}:</strong> {task.address || t('cleaner.noAddress')}</p>
                                      {task.guest_name && (
                                        <p><strong>{t('admin.guestLabel')}:</strong> {task.guest_name}{task.guest_email ? ` (${task.guest_email})` : ''}</p>
                                      )}
                                      {task.booking_check_in && task.booking_check_out ? (
                                        <p>
                                          <strong>{t('admin.bookingLabel')}:</strong> {new Date(task.booking_check_in).toLocaleDateString('nl-NL')} - {new Date(task.booking_check_out).toLocaleDateString('nl-NL')}
                                          {' · '}{formatTimeOnly(task.scheduledAt)} - {formatTimeOnly(task.cleaningEnd)}
                                        </p>
                                      ) : (
                                        <p>
                                          <strong>{t('cleaner.cleaningPeriod')}:</strong> {isPeriod ? (
                                            <>{startDate.toLocaleDateString('nl-NL')} {formatTimeOnly(task.scheduledAt)} - {endDate.toLocaleDateString('nl-NL')} {formatTimeOnly(task.cleaningEnd)}</>
                                          ) : (
                                            <>{startDate.toLocaleDateString('nl-NL')}, {formatTimeOnly(task.scheduledAt)} - {formatTimeOnly(task.cleaningEnd)}</>
                                          )}
                                        </p>
                                      )}
                                      <p><strong>{t('cleaner.duration')}:</strong> {formatWorkedHours(task.worked_minutes)}</p>
                                      {task.notes && <p><strong>{t('cleaner.notes')}:</strong> {task.notes}</p>}
                                      {renderDefaultCleanerInfo(task.propertyId)}
                                    </div>
                                  </div>

                                  <div className="scheduled-task-details-side">
                                    {task.status === 'OPEN' && (
                                      <div className="assign-section">
                                        <input
                                          type="text"
                                          placeholder={t('admin.searchCleanerPlaceholder')}
                                          value={manualAssignSearch[task.id] || ''}
                                          onChange={(e) => setManualAssignSearch((prev) => ({ ...prev, [task.id]: e.target.value }))}
                                          className="search-select-input"
                                        />
                                        {manualAssignSearch[task.id] && (
                                          <div className="select-dropdown">
                                            {(() => {
                                              const term = manualAssignSearch[task.id].toLowerCase();
                                              const matches = users.filter(u =>
                                                u.role === 'SCHOONMAKER' && u.isActive &&
                                                (u.name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term))
                                              );
                                              if (matches.length === 0) {
                                                return <div className="dropdown-empty">{t('admin.noCleanersFound')}</div>;
                                              }
                                              return matches.map(c => (
                                                <div key={c.id} className="dropdown-item" onClick={() => handleManualAssign(task.id, c.id)}>
                                                  {formatOwnerLabel(c.name || t('admin.noName'), c.email)}
                                                </div>
                                              ));
                                            })()}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {(task.status === 'PENDING_CONFIRMATION' || task.status === 'WITHDRAWN') && (
                                      <div className="candidate-box">
                                        <div className="candidate-row">
                                          <Avatar url={task.cleaner_photo_url} name={task.cleaner_name || '?'} size={32} />
                                          <span className="candidate-name">{task.cleaner_name}</span>
                                          <div className="task-actions-row">
                                            <button onClick={() => handleConfirmCandidate(task)} className="action-btn confirm-btn">{t('admin.confirm')}</button>
                                            <button onClick={() => handleUnassignTask(task.id)} className="action-btn reject-btn">{t('admin.reject')}</button>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {['ASSIGNED', 'COMPLETED'].includes(task.status) && (
                                      <div className="task-cleaner-list">
                                        {[
                                          { id: task.cleanerId, name: task.cleaner_name, photo: task.cleaner_photo_url },
                                          { id: task.cleanerId2, name: task.cleaner2_name, photo: task.cleaner2_photo_url },
                                          { id: task.cleanerId3, name: task.cleaner3_name, photo: task.cleaner3_photo_url },
                                        ].filter((c) => c.id).sort((a, b) => compareNames(a.name, b.name)).map((c) => (
                                          <div key={c.id} className="task-cleaner-row">
                                            <Avatar url={c.photo} name={c.name || '?'} size={32} />
                                            <span className="task-cleaner-name">{c.name}</span>
                                            {task.status === 'ASSIGNED' && (
                                              <button
                                                type="button"
                                                className="action-btn reject-btn"
                                                onClick={() => handleRemoveCleanerFromTask(task, c.id)}
                                              >{t('common.delete')}</button>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {task.status === 'ASSIGNED' && (
                                      <div className="cleaner-info">
                                        {/* Verwijderen/intrekken gebeurt nu via het kruisje bij de
                                            naam in de samenvattingsrij hierboven - hier enkel nog 1
                                            algemene in-/uitkloktijd, rechtstreeks aanpasbaar (bv. als
                                            de schoonmaker vergat in/uit te klokken). In de praktijk
                                            klokt maar 1 schoonmaker effectief in/uit voor de hele
                                            taak, dus deze correctie wordt meteen op alle toegewezen
                                            schoonmaker-plaatsen tegelijk toegepast (zie
                                            handleGeneralClockTimeFieldChange hierboven en
                                            "/clock-times" in index.js). */}
                                        <div className="clock-time-edit-list">
                                          {(() => {
                                            const isSaving = !!savingClockTimeKey && savingClockTimeKey.startsWith(`${task.id}_`);
                                            return (
                                              <div className="clock-time-edit-row">
                                                <label className="clock-time-edit-field">
                                                  {t('admin.clockInLabel')}
                                                  <input
                                                    type="time"
                                                    value={getClockTimeDraft(task, '', 'clockIn')}
                                                    onChange={(e) => handleGeneralClockTimeFieldChange(task, 'clockIn', e.target.value)}
                                                    disabled={isSaving}
                                                  />
                                                </label>
                                                <label className="clock-time-edit-field">
                                                  {t('admin.clockOutLabel')}
                                                  <input
                                                    type="time"
                                                    value={getClockTimeDraft(task, '', 'clockOut')}
                                                    onChange={(e) => handleGeneralClockTimeFieldChange(task, 'clockOut', e.target.value)}
                                                    disabled={isSaving}
                                                  />
                                                </label>
                                                {isSaving && <span className="clocktime-saving-hint">{t('admin.savingClockTimes')}</span>}
                                                {(task.cleanerClockInAt || task.cleanerClockOutAt) && !isSaving && (
                                                  <button type="button" className="clocktime-reset-btn" onClick={() => handleResetClockTimes(task)}>
                                                    {t('admin.clockTimesReset')}
                                                  </button>
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                        {task.cleaner_count < 3 && (
                                          <div className="assign-section">
                                            <input
                                              type="text"
                                              placeholder={t('admin.addCoCleanerPlaceholder')}
                                              value={coCleanerSearch[task.id] || ''}
                                              onChange={(e) => setCoCleanerSearch((prev) => ({ ...prev, [task.id]: e.target.value }))}
                                              className="search-select-input"
                                            />
                                            {coCleanerSearch[task.id] && (
                                              <div className="select-dropdown">
                                                {(() => {
                                                  const term = coCleanerSearch[task.id].toLowerCase();
                                                  const takenIds = [task.cleanerId, task.cleanerId2, task.cleanerId3].filter(Boolean);
                                                  const matches = users.filter(u =>
                                                    u.role === 'SCHOONMAKER' && u.isActive && !takenIds.includes(u.id) &&
                                                    (u.name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term))
                                                  );
                                                  if (matches.length === 0) {
                                                    return <div className="dropdown-empty">{t('admin.noCleanersFound')}</div>;
                                                  }
                                                  return matches.map(c => (
                                                    <div key={c.id} className="dropdown-item" onClick={() => handleAddCoCleaner(task, c.id)}>
                                                      {formatOwnerLabel(c.name || t('admin.noName'), c.email)}
                                                    </div>
                                                  ));
                                                })()}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
  );

  const planningBadgeCount = unscheduledBookings.filter((b) => !b.cleaningRejected).length + pendingApprovalTasks.length;
  // Klik op een melding: ga naar het bijhorende tabblad (en sluit het meldingenscherm in de app).
  function handleAdminNotificationClick(n) {
    setNativeScreen(null);
    if (n.type === 'TASK_CLAIMED' || n.type === 'TASK_WITHDRAWN') {
      setActiveTab('scheduled');
    } else if (n.type === 'BOOKING_TO_SCHEDULE' || n.type === 'TASK_PENDING_APPROVAL') {
      setActiveTab('to-schedule');
    } else if (n.type === 'ACCOUNT_DELETION_REQUESTED') {
      setActiveTab('users');
    }
  }
  const goNativeTab = (tab) => { setNativeScreen(null); setActiveTab(tab); };
  const adminTabIconProps = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const nativeAdminTabs = [
    // Er is ALTIJD precies 1 tab blauw: staat het meldingen- of profielscherm open, dan
    // is enkel dát tabblad actief (de tabs van de pagina eronder zijn dan niet actief).
    { key: 'calendar', label: t('admin.tabCalendar'), active: !nativeScreen && activeTab === 'calendar', onClick: () => goNativeTab('calendar'),
      icon: <svg {...adminTabIconProps}><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 10H21M8 2V6M16 2V6" /></svg> },
    { key: 'stats', label: t('admin.tabDashboard'), active: !nativeScreen && activeTab === 'stats', onClick: () => goNativeTab('stats'),
      icon: <svg {...adminTabIconProps}><rect x="3" y="3" width="8" height="9" rx="2" /><rect x="13" y="3" width="8" height="5" rx="2" /><rect x="13" y="11" width="8" height="10" rx="2" /><rect x="3" y="15" width="8" height="6" rx="2" /></svg> },
    { key: 'planning', label: t('admin.tabPlanning'), badge: planningBadgeCount, active: !nativeScreen && ['to-schedule', 'scheduled', 'completed'].includes(activeTab),
      onClick: () => goNativeTab(['to-schedule', 'scheduled', 'completed'].includes(activeTab) ? activeTab : 'to-schedule'),
      icon: <svg {...adminTabIconProps}><rect x="5" y="4.5" width="14" height="16.5" rx="2" /><path d="M9 4.5V3.5h6v1" /><path d="M9 13.5l2 2 4-4.5" /></svg> },
    { key: 'accounting', label: t('admin.tabAccounting'), active: !nativeScreen && ['payout', 'invoicing'].includes(activeTab),
      onClick: () => goNativeTab(['payout', 'invoicing'].includes(activeTab) ? activeTab : 'invoicing'),
      icon: <svg {...adminTabIconProps}><path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" /><path d="M9 8h6M9 12h6" /></svg> },
    { key: 'data', label: t('admin.tabData'), active: !nativeScreen && ['users', 'properties', 'newsletter', 'demo'].includes(activeTab),
      onClick: () => goNativeTab(['users', 'properties', 'newsletter', 'demo'].includes(activeTab) ? activeTab : 'users'),
      icon: <svg {...adminTabIconProps}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" /><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18.3 14.3c2 .8 3.2 2.6 3.2 5.7" /></svg> },
    { key: 'notifications', label: t('notifications.title'), badge: nativeUnread, active: nativeScreen === 'notifications', onClick: () => setNativeScreen('notifications'),
      icon: <svg {...adminTabIconProps}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg> },
    { key: 'profile', label: t('profileMenu.title'), active: nativeScreen === 'profile', onClick: () => setNativeScreen('profile'),
      icon: <svg {...adminTabIconProps}><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg> },
  ];

  const searchPlaceholder = activeTab === 'users'
    ? t('admin.searchUsers')
    : activeTab === 'properties'
      ? t('admin.searchProperties')
      : activeTab === 'newsletter'
        ? t('admin.searchNewsletter')
        : t('admin.searchDefault');

  // Slot-status van een accommodatie als korte tekst/badge (batterij, onbereikbaar, ...).
  // Gebruikt in de accommodatielijst (tabel en uitklapkaartjes).
  const renderLockStatus = (p) => {
    const status = smartlockStatuses[p.id];
    if (!status || status.loading) return '...';
    if (status.error || (status.linked && status.configured && !status.reachable)) return t('admin.statusUnreachable');
    if (status.linked && !status.configured) return t('admin.statusNotActive');
    if (status.reachable && status.batteryCharge != null) {
      const batteryClass = status.batteryCharge <= 20 ? 'battery-value-critical' : status.batteryCharge <= 35 ? 'battery-value-warning' : '';
      return <span className={batteryClass}>{status.batteryCharge}%</span>;
    }
    return t('admin.statusUnknown');
  };

  // 1 maand van het maandoverzicht. Wordt 3x gerenderd (vorige/huidige/volgende maand) zodat de
  // buurmaanden tijdens het vegen aaneensluitend in beeld schuiven (zie useMonthSwipe).
  const renderBusinessMonth = (extra) => (
    <FullCalendar
      {...extra}
      plugins={[dayGridPlugin, interactionPlugin]}
      initialView="dayGridMonth"
      dayHeaderFormat={{ weekday: 'narrow' }}
      fixedWeekCount={true}
      showNonCurrentDates={false}
      headerToolbar={{ left: '', center: 'title', right: 'calendarMenuBtn' }}
      customButtons={{
        calendarMenuBtn: {
          text: '⋮',
          click: () => setShowBusinessCalMenu((prev) => !prev),
        },
      }}
      height="auto"
      locale={i18n.language?.startsWith('fr') ? frLocale : i18n.language?.startsWith('en') ? 'en' : nlLocale}
      firstDay={1}
      dateClick={(info) => setBusinessCalendarSelectedDate((prev) => (prev === info.dateStr ? null : info.dateStr))}
      dayCellClassNames={(args) => (toDateKey(args.date) === businessCalendarSelectedDate ? ['calendar-day-selected'] : [])}
      dayCellContent={(args) => {
        const stats = businessCalendarDayStats[toDateKey(args.date)];
        return (
          <div className="calendar-day-cell-content">
            <span className="calendar-day-number">{args.dayNumberText}</span>
            {stats && (
              <span className="calendar-day-badges">
                {stats.unstaffed > 0 && (
                  <span className="calendar-day-count unstaffed">{stats.unstaffed}</span>
                )}
                {stats.partial > 0 && (
                  <span className="calendar-day-count partial">{stats.partial}</span>
                )}
                {stats.staffed > 0 && (
                  <span className="calendar-day-count staffed">{stats.staffed}</span>
                )}
                {stats.done > 0 && (
                  <span className="calendar-day-count assigned">{stats.done}</span>
                )}
                {stats.unscheduled > 0 && (
                  <span className="calendar-day-count unscheduled">{stats.unscheduled}</span>
                )}
                {stats.pendingApproval > 0 && (
                  <span className="calendar-day-count pendingapproval">{stats.pendingApproval}</span>
                )}
                {stats.rejected > 0 && (
                  <span className="calendar-day-count rejected">{stats.rejected}</span>
                )}
              </span>
            )}
          </div>
        );
      }}
    />
  );

  return (
    <div className={`admin-container ${isNative ? 'admin-container-native' : ''}`}>
      <nav className="admin-nav">
        {/* Hamburgerknop: enkel zichtbaar op smallere schermen (zie media
            query in Admin.css) - vervangt daar de tekstlinks-rij, die anders
            lelijk naar een 2de regel wrapte. Zelfde patroon als
            Dashboard.js's dash-nav-toggle/dash-mobile-menu. */}
        {!isNative && (
        <button
          className={`admin-nav-toggle ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        )}

        {!isNative && (
        <div className="nav-brand">
          <SmartStaysLogo className="nav-logo" />
        </div>
        )}
        {!isNative && (
        <div className="admin-nav-links">
          <button className={`admin-nav-link admin-nav-link-plain ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => setActiveTab('calendar')}>
            {t('admin.tabCalendar')}
          </button>
          <button className={`admin-nav-link admin-nav-link-plain ${activeTab === 'stats' ? 'active' : ''}`} onClick={() => setActiveTab('stats')}>
            {t('admin.tabDashboard')}
          </button>
          <div className="admin-nav-dropdown">
            <button
              className={`admin-nav-link ${['to-schedule', 'scheduled', 'completed'].includes(activeTab) ? 'active' : ''}`}
              onClick={() => setActiveTab(['to-schedule', 'scheduled', 'completed'].includes(activeTab) ? activeTab : 'to-schedule')}
            >
              {t('admin.tabPlanning')}
              {(unscheduledBookings.filter((b) => !b.cleaningRejected).length + pendingApprovalTasks.length) > 0 && (
                <span className="admin-nav-badge">{unscheduledBookings.filter((b) => !b.cleaningRejected).length + pendingApprovalTasks.length}</span>
              )}
            </button>
            <div className="admin-nav-dropdown-menu">
              <button className={activeTab === 'to-schedule' ? 'active' : ''} onClick={() => setActiveTab('to-schedule')}>
                {t('admin.tabToSchedule')}{planningBadgeCount > 0 && <span className="admin-nav-badge admin-inline-badge">{planningBadgeCount}</span>}
              </button>
              <button className={activeTab === 'scheduled' ? 'active' : ''} onClick={() => setActiveTab('scheduled')}>
                {t('admin.tabScheduled')}
              </button>
              <button className={activeTab === 'completed' ? 'active' : ''} onClick={() => setActiveTab('completed')}>
                {t('admin.tabCompleted')}
              </button>
            </div>
          </div>
          <div className="admin-nav-dropdown">
            <button
              className={`admin-nav-link ${['payout', 'invoicing'].includes(activeTab) ? 'active' : ''}`}
              onClick={() => setActiveTab(['payout', 'invoicing'].includes(activeTab) ? activeTab : 'invoicing')}
            >
              {t('admin.tabAccounting')}
            </button>
            <div className="admin-nav-dropdown-menu">
              <button className={activeTab === 'invoicing' ? 'active' : ''} onClick={() => setActiveTab('invoicing')}>
                {t('admin.tabInvoicing')} {outstandingInvoiceGroupsCount > 0 && `(${outstandingInvoiceGroupsCount})`}
              </button>
              <button className={activeTab === 'payout' ? 'active' : ''} onClick={() => setActiveTab('payout')}>
                {t('admin.tabPayout')} {outstandingPayoutGroupsCount > 0 && `(${outstandingPayoutGroupsCount})`}
              </button>
            </div>
          </div>
          <div className="admin-nav-dropdown">
            <button
              className={`admin-nav-link ${['users', 'properties'].includes(activeTab) ? 'active' : ''}`}
              onClick={() => setActiveTab(['users', 'properties'].includes(activeTab) ? activeTab : 'users')}
            >
              {t('admin.tabData')}
            </button>
            <div className="admin-nav-dropdown-menu">
              <button className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>
                {t('admin.tabUsers')}
              </button>
              <button className={activeTab === 'properties' ? 'active' : ''} onClick={() => setActiveTab('properties')}>
                {t('admin.tabProperties')}
              </button>
            </div>
          </div>
          <button className={`admin-nav-link admin-nav-link-plain ${activeTab === 'newsletter' ? 'active' : ''}`} onClick={() => setActiveTab('newsletter')}>
            {t('admin.tabNewsletter')}
          </button>
          <button className={`admin-nav-link admin-nav-link-plain ${activeTab === 'demo' ? 'active' : ''}`} onClick={() => setActiveTab('demo')}>
            Demo
          </button>
        </div>
        )}
        {!isNative && (
        <div className="nav-user">
          <NotificationBell onNotificationClick={handleAdminNotificationClick} />
          <ProfileMenu user={user} onLogout={handleLogout} />
        </div>
        )}

        {/* Uitklapmenu enkel zichtbaar/bruikbaar op smallere schermen (zie
            media query) - vlakke lijst i.p.v. de hover-dropdowns hierboven,
            want die werken niet prettig met een touchscherm. */}
        {!isNative && mobileMenuOpen && (
          <div className="admin-mobile-menu">
            <button
              className={`admin-mobile-item ${activeTab === 'calendar' ? 'active' : ''}`}
              onClick={() => { setActiveTab('calendar'); setMobileMenuOpen(false); }}
            >
              {t('admin.tabCalendar')}
            </button>
            <button
              className={`admin-mobile-item ${activeTab === 'stats' ? 'active' : ''}`}
              onClick={() => { setActiveTab('stats'); setMobileMenuOpen(false); }}
            >
              {t('admin.tabDashboard')}
            </button>
            {/* Boekhouding/Gegevens: 1 knop per groep i.p.v. alle subtaken los te
                tonen - blijft op de huidige subtab staan als je al in die groep
                zit (zelfde "slim onthouden"-gedrag als de desktop-dropdown
                hierboven), anders default naar de 1ste subtab. Navigeren tussen
                de subtaken zelf gebeurt via de tab-knoppen boven de inhoud. */}
            <button
              className={`admin-mobile-item ${['to-schedule', 'scheduled', 'completed'].includes(activeTab) ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(['to-schedule', 'scheduled', 'completed'].includes(activeTab) ? activeTab : 'to-schedule');
                setMobileMenuOpen(false);
              }}
            >
              {t('admin.tabPlanning')}
              {planningBadgeCount > 0 && <span className="admin-nav-badge admin-inline-badge">{planningBadgeCount}</span>}
            </button>
            <button
              className={`admin-mobile-item ${['payout', 'invoicing'].includes(activeTab) ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(['payout', 'invoicing'].includes(activeTab) ? activeTab : 'invoicing');
                setMobileMenuOpen(false);
              }}
            >
              {t('admin.tabAccounting')}
            </button>
            <button
              className={`admin-mobile-item ${['users', 'properties'].includes(activeTab) ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(['users', 'properties'].includes(activeTab) ? activeTab : 'users');
                setMobileMenuOpen(false);
              }}
            >
              {t('admin.tabData')}
            </button>
            <button
              className={`admin-mobile-item ${activeTab === 'newsletter' ? 'active' : ''}`}
              onClick={() => { setActiveTab('newsletter'); setMobileMenuOpen(false); }}
            >
              {t('admin.tabNewsletter')}
            </button>
            <button
              className={`admin-mobile-item ${activeTab === 'demo' ? 'active' : ''}`}
              onClick={() => { setActiveTab('demo'); setMobileMenuOpen(false); }}
            >
              Demo
            </button>
          </div>
        )}
        {isNative && <NativeScreenTitle title={(nativeAdminTabs.find((x) => x.active) || {}).label} />}
      </nav>

      {isNative && nativeScreen === 'notifications' && (
        <div className="admin-content admin-content-native-screen">
          <NotificationBell screen onNotificationClick={handleAdminNotificationClick} />
        </div>
      )}
      {isNative && nativeScreen === 'profile' && (
        <div className="admin-content admin-content-native-screen">
          <ProfileMenu screen user={user} onLogout={handleLogout} />
        </div>
      )}

      <div
        className="admin-content"
        style={isNative && nativeScreen ? { display: 'none' } : (showingWideCalendar ? { maxWidth: 'none' } : undefined)}
      >
        <div className="admin-header">
          {['to-schedule', 'scheduled', 'completed'].includes(activeTab) && (
            <div className="tab-buttons planning-tabs">
              <button className={`tab-btn ${activeTab === 'to-schedule' ? 'active' : ''}`} onClick={() => setActiveTab('to-schedule')}>
                {t('admin.tabToSchedule')}{planningBadgeCount > 0 && <span className="admin-nav-badge admin-inline-badge">{planningBadgeCount}</span>}
              </button>
              <button className={`tab-btn ${activeTab === 'scheduled' ? 'active' : ''}`} onClick={() => setActiveTab('scheduled')}>
                {t('admin.tabScheduled')}
              </button>
              <button className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`} onClick={() => setActiveTab('completed')}>
                {t('admin.tabCompleted')}
              </button>
            </div>
          )}

          {['payout', 'invoicing'].includes(activeTab) && (
            <div className="tab-buttons">
              <button className={`tab-btn ${activeTab === 'invoicing' ? 'active' : ''}`} onClick={() => setActiveTab('invoicing')}>
                {t('admin.tabInvoicing')} {outstandingInvoiceGroupsCount > 0 && `(${outstandingInvoiceGroupsCount})`}
              </button>
              <button className={`tab-btn ${activeTab === 'payout' ? 'active' : ''}`} onClick={() => setActiveTab('payout')}>
                {t('admin.tabPayout')} {outstandingPayoutGroupsCount > 0 && `(${outstandingPayoutGroupsCount})`}
              </button>
            </div>
          )}

          {(['users', 'properties'].includes(activeTab) || (isNative && ['newsletter', 'demo'].includes(activeTab))) && (
            <div className="tab-buttons">
              <button className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
                {t('admin.tabUsers')}
              </button>
              <button className={`tab-btn ${activeTab === 'properties' ? 'active' : ''}`} onClick={() => setActiveTab('properties')}>
                {t('admin.tabProperties')}
              </button>
              {isNative && (
                <>
                  <button className={`tab-btn ${activeTab === 'newsletter' ? 'active' : ''}`} onClick={() => setActiveTab('newsletter')}>
                    {t('admin.tabNewsletter')}
                  </button>
                  <button className={`tab-btn ${activeTab === 'demo' ? 'active' : ''}`} onClick={() => setActiveTab('demo')}>
                    Demo
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {activeTab !== 'stats' && activeTab !== 'calendar' && activeTab !== 'demo' && (
        <div className="search-bar">
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="clear-search">✖</button>
          )}
        </div>
        )}

        {activeTab === 'calendar' && (
          <div className="cleaner-calendar-section business-calendar-section">
            <div className="section-header">
              <h2>{t('admin.tabCalendar')}</h2>
              <div className="nav-view-toggle schedule-view-toggle">
                <button
                  className={`nav-view-btn ${businessCalendarView === 'month' ? 'active' : ''}`}
                  onClick={() => setBusinessCalendarView('month')}
                >
                  {t('admin.monthOverviewView')}
                </button>
                <button
                  className={`nav-view-btn ${businessCalendarView === 'multi' ? 'active' : ''}`}
                  onClick={() => setBusinessCalendarView('multi')}
                >
                  {t('admin.tabMultiCalendar')}
                </button>
              </div>
            </div>

            {monthCleaningInfo && (
              <CleaningInfoModal
                locale={i18n.language?.startsWith('fr') ? 'fr-BE' : i18n.language?.startsWith('en') ? 'en-GB' : 'nl-NL'}
                info={{
                  id: monthCleaningInfo.id,
                  propertyName: monthCleaningInfo.propertyName,
                  start: monthCleaningInfo.start,
                  end: monthCleaningInfo.end,
                  status: monthCleaningInfo.status,
                  cleaners: monthCleaningInfo.cleaners,
                  expectedCount: monthCleaningInfo.expectedCount,
                }}
                onChanged={fetchScheduledTasks}
                onClose={() => setMonthCleaningInfo(null)}
                onDelete={(id) => { setMonthCleaningInfo(null); handleDeleteTask(id); }}
                onEdit={(id) => { setMonthCleaningInfo(null); handleEditCleaningFromTimeline(id); }}
              />
            )}

            {businessCalendarView === 'multi' && (
              <div className="multi-calendar-picker">
                <FilteredTimeline
                  properties={properties}
                  userRole="ADMIN"
                  onEditCleaning={handleEditCleaningFromTimeline}
                  storageKey="ss_admin_filter_calendar_v2"
                  showOwner
                />
              </div>
            )}

            {businessCalendarView === 'month' && (
              <>
            <div className="calendar-nav-row">
              <button type="button" className="calendar-nav-btn" onClick={() => businessSwipe.slide(-1)} aria-label={t('calendar.previous')}>&#8249;</button>
              <button type="button" className="calendar-nav-btn" onClick={() => businessSwipe.slide(1)} aria-label={t('calendar.next')}>&#8250;</button>
              <button type="button" className="calendar-nav-today" onClick={() => businessCalRef.current?.getApi().today()}>{t('calendar.today', 'Vandaag')}</button>
            </div>
            <div className="business-calendar-wrap">
              <div
                className="cleaner-calendar-card"
                ref={businessSwipe.cardRef}
                {...businessSwipe.handlers}
              >
                <div className="cleaner-calendar-track" ref={businessSwipe.trackRef}>
                  <div className="cleaner-calendar-pane cleaner-calendar-pane-prev" key={`p-${businessCalMonth.getFullYear()}-${businessCalMonth.getMonth()}`} aria-hidden="true">
                    {renderBusinessMonth({ initialDate: shiftMonth(businessCalMonth, -1) })}
                  </div>
                  <div className="cleaner-calendar-pane cleaner-calendar-pane-current">
                    {renderBusinessMonth({ ref: businessCalRef, initialDate: businessCalMonth, datesSet: handleBusinessCalDatesSet })}
                  </div>
                  <div className="cleaner-calendar-pane cleaner-calendar-pane-next" key={`n-${businessCalMonth.getFullYear()}-${businessCalMonth.getMonth()}`} aria-hidden="true">
                    {renderBusinessMonth({ initialDate: shiftMonth(businessCalMonth, 1) })}
                  </div>
                </div>
              </div>

              {showBusinessCalMenu && (
                <div className="calendar-menu-dropdown" ref={businessCalMenuRef}>
                  <button
                    type="button"
                    className="calendar-menu-dropdown-item"
                    onClick={() => { setShowBusinessCalMenu(false); handleAddToCalendar(); }}
                  >
                    {t('cleaner.calendarAddButton')}
                  </button>
                  <button
                    type="button"
                    className="calendar-menu-dropdown-item"
                    onClick={() => { setShowBusinessCalMenu(false); handleCopyCalendarLink(); }}
                  >
                    {t('cleaner.calendarCopyLink')}
                  </button>
                </div>
              )}
            </div>

            {showCalendarAddConfirm && calendarFeed && (
              <div className="modal-overlay" onClick={() => setShowCalendarAddConfirm(false)}>
                <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>{t('cleaner.calendarSubscribeTitle')}</h2>
                    <button className="modal-close" onClick={() => setShowCalendarAddConfirm(false)}>✖</button>
                  </div>
                  <div className="modal-body">
                    <p style={{ whiteSpace: 'pre-line' }}>{t('cleaner.calendarUpdateNote')}</p>
                  </div>
                  <div className="modal-buttons">
                    <button type="button" className="cancel-btn" onClick={() => setShowCalendarAddConfirm(false)}>
                      {t('common.cancel')}
                    </button>
                    <a
                      href={calendarFeed.webcalUrl}
                      className="submit-btn"
                      onClick={() => setShowCalendarAddConfirm(false)}
                    >
                      {t('cleaner.calendarContinue')}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {businessCalendarSelectedDate && (
              <div className="calendar-day-overview">
                <h4>
                  {new Date(businessCalendarSelectedDate).toLocaleDateString(i18n.language?.startsWith('fr') ? 'fr-BE' : i18n.language?.startsWith('en') ? 'en-GB' : 'nl-BE', {
                    weekday: 'long', day: 'numeric', month: 'long',
                  })}
                </h4>
                {(businessCalendarItemsByDate[businessCalendarSelectedDate] || []).length === 0 ? (
                  <p className="no-tasks">{t('admin.calendarNoItemsThisDay')}</p>
                ) : (
                  <div className="calendar-day-overview-list">
                    {(businessCalendarItemsByDate[businessCalendarSelectedDate] || [])
                      .slice()
                      .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                      .map((item) => (
                        <div
                          key={`${item.category}-${item.id}`}
                          className={`calendar-day-overview-item ${item.category} ${item.isTask ? 'clickable' : ''}`}
                          onClick={item.isTask ? () => setMonthCleaningInfo(item) : undefined}
                        >
                          <div className="calendar-day-overview-time">
                            {item.time}{item.endTime ? ` - ${item.endTime}` : ''}
                          </div>
                          <div className="calendar-day-overview-details">
                            <div className="calendar-day-overview-property">{item.propertyName}</div>
                            {item.address && <div className="calendar-day-overview-address">{item.address}</div>}
                            {item.cleanerNames.length > 0 && (
                              <div className="calendar-day-overview-cleaners">
                                {item.cleanerNames.join(', ')}
                                <span className="calendar-day-overview-clocktimes">
                                  {(item.clockInAt || item.clockOutAt)
                                    ? ` · ${item.clockInAt ? formatTimeOnly(item.clockInAt) : '–'} - ${item.clockOutAt ? formatTimeOnly(item.clockOutAt) : '–'}`
                                    : ''}
                                </span>
                              </div>
                            )}
                            {item.category === 'pendingapproval' && (
                              <>
                                {item.ownerName && (
                                  <div className="calendar-day-overview-address">{t('admin.addedByLandlord', { name: item.ownerName })}</div>
                                )}
                                {item.notes && (
                                  <div className="calendar-day-overview-address">{t('cleaner.notes')}: {item.notes}</div>
                                )}
                              </>
                            )}
                          </div>
                          {item.category === 'rejected' && (
                            <div className="calendar-day-overview-actions" onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="schedule-btn schedule-btn-calendar" onClick={() => handleRestoreCleaning(item.id)}>
                                {t('admin.restoreCleaning')}
                              </button>
                            </div>
                          )}
                          {item.category === 'unscheduled' && (
                            <div className="calendar-day-overview-actions" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="schedule-btn schedule-btn-calendar"
                                onClick={() => { openPropertySchedule({ propertyId: item.propertyId, property_name: item.propertyName }); setActiveTab('to-schedule'); }}
                              >
                                {t('admin.calendarButton')}
                              </button>
                              <button
                                type="button"
                                className="schedule-btn schedule-btn-approve"
                                disabled={approvingPropertyId === item.id}
                                onClick={() => handleApproveSchedule({ id: item.id, propertyId: item.propertyId, property_name: item.propertyName, checkOut: item.checkOutIso })}
                              >
                                {approvingPropertyId === item.id ? t('common.loading') : t('admin.approveButton')}
                              </button>
                              <button type="button" className="schedule-btn schedule-btn-reject" onClick={() => handleRejectCleaning(item.id)}>
                                {t('admin.rejectCleaningButton')}
                              </button>
                            </div>
                          )}
                          {item.category === 'pendingapproval' && (
                            <div className="calendar-day-overview-actions" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="schedule-btn schedule-btn-approve"
                                disabled={approvingTaskId === item.id}
                                onClick={() => handleApprovePendingTask(item.id)}
                              >
                                {approvingTaskId === item.id ? t('common.loading') : t('admin.approveButton')}
                              </button>
                              <button
                                type="button"
                                className="action-icon-btn action-icon-delete"
                                title={t('admin.rejectButton')}
                                aria-label={t('admin.rejectButton')}
                                onClick={() => handleDeleteTask(item.id)}
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
              </>
            )}
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="stats-dashboard">
            <div className="month-selector stats-date-filter">
              <div className="date-filter-controls">
                <div className="nav-view-toggle date-filter-mode-toggle">
                  <button type="button" className={`nav-view-btn ${statsDateMode === 'all' ? 'active' : ''}`} onClick={() => setStatsDateMode('all')}>{t('admin.dateFilterAll')}</button>
                  <button type="button" className={`nav-view-btn ${statsDateMode === 'month' ? 'active' : ''}`} onClick={() => setStatsDateMode('month')}>{t('admin.dateFilterByMonth')}</button>
                  <button type="button" className={`nav-view-btn ${statsDateMode === 'range' ? 'active' : ''}`} onClick={() => setStatsDateMode('range')}>{t('admin.dateFilterByRange')}</button>
                </div>
                {statsDateMode === 'month' && (
                  <input type="month" value={statsMonth} onChange={(e) => e.target.value && setStatsMonth(e.target.value)} />
                )}
                {statsDateMode === 'range' && (
                  <div className="date-filter-range">
                    <input type="date" value={statsRangeStart} onChange={(e) => setStatsRangeStart(e.target.value)} />
                    <span>{t('admin.dateFilterRangeSeparator')}</span>
                    <input type="date" value={statsRangeEnd} onChange={(e) => setStatsRangeEnd(e.target.value)} />
                  </div>
                )}
              </div>
            </div>
            <div className="stats-section">
              <div className="section-header">
                <h2>{t('admin.statsRevenueTitle')}</h2>
              </div>

              <div className="revenue-charts-row">
                <div className="revenue-chart-card">
                  <h3>{t('admin.statsRevenueByServiceTitle')}</h3>
                  <DonutChart
                    segments={businessStats.serviceBreakdown.map((s) => ({ key: s.key, label: t(s.labelKey), value: s.value, color: SERVICE_COLORS[s.key] }))}
                    totalLabel={t('admin.statsRevenueTotal')}
                    formatValue={(v) => `€${v.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    emptyText={t('admin.statsNoRevenueYet')}
                  />
                </div>
                <div className="revenue-chart-card">
                  <h3>{t('admin.statsTopCustomersTitle')}</h3>
                  <DonutChart
                    segments={businessStats.topCustomers.map((c, i) => ({ key: c.ownerId, label: c.name || c.email || t('admin.statsUnknownCustomer'), value: c.total, color: goldenAngleColor(i) }))}
                    totalLabel={t('admin.statsRevenueTotal')}
                    formatValue={(v) => `€${v.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    emptyText={t('admin.statsNoRevenueYet')}
                  />
                </div>
                <div className="revenue-chart-card">
                  <h3>{t('admin.statsTopCustomersProfitTitle')}</h3>
                  <DonutChart
                    segments={businessStats.topCustomersProfit.map((c, i) => ({ key: c.ownerId, label: c.name || c.email || t('admin.statsUnknownCustomer'), value: c.profit, color: goldenAngleColor(i) }))}
                    totalLabel={t('admin.statsProfitLabel')}
                    formatValue={(v) => `€${v.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    emptyText={t('admin.statsNoRevenueYet')}
                  />
                </div>
              </div>

              <div className="stats-cards stats-cards-revenue">
                {statsDateMode === 'month' ? (
                <div className="stat-card stat-card-input">
                  <span className="stat-card-label">{t('admin.statsRevenueSetup')}</span>
                  <div className="stat-card-input-row">
                    <span className="stat-card-input-prefix">&euro;</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={setupRevenueInput}
                      onChange={(e) => setSetupRevenueInput(e.target.value)}
                    />
                    <button type="button" className="stat-card-input-save" onClick={handleSaveSetupRevenue} disabled={setupRevenueSaving}>
                      {setupRevenueSaving ? t('common.loading') : t('common.save')}
                    </button>
                  </div>
                </div>
                ) : (
                  <div className="stat-card">
                    <span className="stat-card-value">&euro;{businessStats.setupRevenue.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span className="stat-card-label">{t('admin.statsRevenueSetup')}</span>
                  </div>
                )}
                <div className="stat-card">
                  <span className="stat-card-value">&euro;{businessStats.accessRevenue.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="stat-card-label">{t('admin.statsRevenueSubscriptions')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">&euro;{businessStats.cleaningRevenue.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="stat-card-label">{t('admin.statsRevenueCleaning')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">&euro;{businessStats.automationRevenue.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="stat-card-label">{t('admin.statsRevenueAutomation')}</span>
                </div>
                <div className="stat-card stat-card-highlight">
                  <span className="stat-card-value">&euro;{businessStats.totalRevenue.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="stat-card-label">{t('admin.statsRevenueTotal')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">&euro;{businessStats.cleanerCost.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="stat-card-label">{t('admin.statsCosts')}</span>
                </div>
                <div className={`stat-card ${businessStats.profit >= 0 ? 'stat-card-profit-positive' : 'stat-card-profit-negative'}`}>
                  <span className="stat-card-value">&euro;{businessStats.profit.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="stat-card-label">{t('admin.statsProfit')}</span>
                </div>
              </div>
            </div>

            <div className="stats-section">
              <h2>{t('admin.statsActivityTitle')}</h2>
              <div className="stats-cards">
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.tasksThisMonthCount}</span>
                  <span className="stat-card-label">{t('admin.statsTasksThisMonth')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.completedThisMonth}</span>
                  <span className="stat-card-label">{t('admin.statsCompletedThisMonth')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.todoThisMonth}</span>
                  <span className="stat-card-label">{t('admin.statsTodoThisMonth')}</span>
                </div>
                <div className="stat-card stat-card-highlight">
                  <span className="stat-card-value">&euro;{businessStats.cleaningRevenue.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="stat-card-label">{t('admin.statsRevenueCleaning')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.activeCleanersThisMonth}</span>
                  <span className="stat-card-label">{t('admin.statsActiveCleaners')}</span>
                </div>
              </div>
            </div>

            <div className="stats-section">
              <h2>{t('admin.statsUsersTitle')}</h2>
              <div className="stats-cards">
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.totalVerhuurders}</span>
                  <span className="stat-card-label">{t('admin.statsTotalVerhuurders')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.totalSchoonmakers}</span>
                  <span className="stat-card-label">{t('admin.statsTotalSchoonmakers')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.newUsersThisMonth}</span>
                  <span className="stat-card-label">{t('admin.statsNewUsersThisMonth')}</span>
                </div>
              </div>
            </div>

            <div className="stats-section">
              <h2>{t('admin.statsSubscriptionsTitle')}</h2>
              <div className="stats-cards">
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.accessManagedProperties}</span>
                  <span className="stat-card-label">{t('admin.statsAccessManagedProperties')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.automationCount}</span>
                  <span className="stat-card-label">{t('admin.statsAutomationSubscriptions')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.setupsThisMonth}</span>
                  <span className="stat-card-label">{t('admin.statsSetupsThisMonth')}</span>
                </div>
                <div className="stat-card stat-card-highlight">
                  <span className="stat-card-value">&euro;{businessStats.accessRevenue.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className="stat-card-label">{t('admin.statsEstimatedRevenue')}</span>
                </div>
              </div>
            </div>

            <div className="stats-section">
              <h2>{t('admin.statsPropertiesTitle')}</h2>
              <div className="stats-cards">
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.totalProperties}</span>
                  <span className="stat-card-label">{t('admin.statsTotalProperties')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.smartlockLinked}</span>
                  <span className="stat-card-label">{t('admin.statsSmartlockLinked')}</span>
                </div>
              </div>
              <div className="stats-breakdown">
                <h3>{t('admin.statsCleaningTierBreakdown')}</h3>
                {[
                  { key: 'FULL', label: t('admin.managedByUs'), count: businessStats.tierCounts.FULL },
                  { key: 'PARTIAL', label: t('admin.managedBySelf'), count: businessStats.tierCounts.PARTIAL },
                  { key: 'NONE', label: t('admin.managedNone'), count: businessStats.tierCounts.NONE },
                ].map((row) => (
                  <div className="stats-bar-row" key={row.key}>
                    <span className="stats-bar-label">{row.label}</span>
                    <div className="stats-bar-track">
                      <div
                        className={`stats-bar-fill stats-bar-fill-${row.key.toLowerCase()}`}
                        style={{ width: businessStats.totalProperties > 0 ? `${(row.count / businessStats.totalProperties) * 100}%` : '0%' }}
                      ></div>
                    </div>
                    <span className="stats-bar-count">{row.count}</span>
                  </div>
                ))}
              </div>
              <div className="stats-breakdown">
                <h3>{t('admin.statsPropertyTypeBreakdown')}</h3>
                {[
                  { key: 'hotelRoom', label: t('pricing.cleaningTable.hotelRoom'), count: businessStats.typeCounts.hotelRoom },
                  { key: 'type2', label: t('pricing.cleaningTable.type2'), count: businessStats.typeCounts.type2 },
                  { key: 'type3', label: t('pricing.cleaningTable.type3'), count: businessStats.typeCounts.type3 },
                  { key: 'type4', label: t('pricing.cleaningTable.type4'), count: businessStats.typeCounts.type4 },
                  { key: 'type5', label: t('pricing.cleaningTable.type5'), count: businessStats.typeCounts.type5 },
                  { key: 'unset', label: t('admin.statsPropertyTypeUnset'), count: businessStats.typeCounts.unset },
                ].filter((row) => row.count > 0).map((row) => (
                  <div className="stats-bar-row" key={row.key}>
                    <span className="stats-bar-label">{row.label}</span>
                    <div className="stats-bar-track">
                      <div
                        className="stats-bar-fill stats-bar-fill-neutral"
                        style={{ width: businessStats.totalProperties > 0 ? `${(row.count / businessStats.totalProperties) * 100}%` : '0%' }}
                      ></div>
                    </div>
                    <span className="stats-bar-count">{row.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="stats-section">
              <h2>{t('admin.statsNewsletterTitle')}</h2>
              <div className="stats-cards">
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.newsletterActive}</span>
                  <span className="stat-card-label">{t('admin.statsNewsletterTotal')}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-card-value">{businessStats.newSubscribersThisMonth}</span>
                  <span className="stat-card-label">{t('admin.statsNewSubscribersThisMonth')}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && calendarViewCleaner && (
          <>
            <div className="section-header">
              <button onClick={() => { setCalendarViewCleaner(null); setCalendarSelectedDate(null); }} className="back-link-btn">&larr; {t('admin.backToOverview')}</button>
              <h2>{t('admin.cleanerCalendarTitle', { name: calendarViewCleaner.name || calendarViewCleaner.email })}</h2>
            </div>
            <div className="cleaner-calendar-section">
              <div className="calendar-nav-row">
                <button type="button" className="calendar-nav-btn" onClick={() => cleanerCalRef.current?.getApi().prev()} aria-label="Vorige">&#8249;</button>
                <button type="button" className="calendar-nav-btn" onClick={() => cleanerCalRef.current?.getApi().next()} aria-label="Volgende">&#8250;</button>
                <button type="button" className="calendar-nav-today" onClick={() => cleanerCalRef.current?.getApi().today()}>{t('calendar.today', 'Vandaag')}</button>
              </div>
              <div className="cleaner-calendar-card">
              <FullCalendar
                ref={cleanerCalRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                dayHeaderFormat={{ weekday: 'narrow' }}
                fixedWeekCount={true}
                showNonCurrentDates={false}
                headerToolbar={{ left: '', center: 'title', right: '' }}
                height="auto"
                locale={i18n.language?.startsWith('fr') ? frLocale : i18n.language?.startsWith('en') ? 'en' : nlLocale}
                firstDay={1}
                dateClick={(info) => setCalendarSelectedDate((prev) => (prev === info.dateStr ? null : info.dateStr))}
                dayCellClassNames={(args) => (toDateKey(args.date) === calendarSelectedDate ? ['calendar-day-selected'] : [])}
                dayCellContent={(args) => {
                  const dayTasks = cleanerTasksByDate[toDateKey(args.date)] || [];
                  const hasUnconfirmed = dayTasks.some((task) => !['ASSIGNED', 'COMPLETED', 'INVOICED'].includes(task.status));
                  return (
                    <div className="calendar-day-cell-content">
                      <span className="calendar-day-number">{args.dayNumberText}</span>
                      {dayTasks.length > 0 && (
                        <span className={`calendar-day-count ${hasUnconfirmed ? 'pending' : 'confirmed'}`}>
                          {dayTasks.length}
                        </span>
                      )}
                    </div>
                  );
                }}
              />
              </div>
              {calendarSelectedDate && (
                <div className="calendar-day-overview">
                  <h4>
                    {new Date(calendarSelectedDate).toLocaleDateString(i18n.language?.startsWith('fr') ? 'fr-BE' : i18n.language?.startsWith('en') ? 'en-GB' : 'nl-BE', {
                      weekday: 'long', day: 'numeric', month: 'long',
                    })}
                  </h4>
                  {(cleanerTasksByDate[calendarSelectedDate] || []).length === 0 ? (
                    <p className="no-tasks">{t('cleaner.calendarNoTasksThisDay')}</p>
                  ) : (
                    <div className="calendar-day-overview-list">
                      {(cleanerTasksByDate[calendarSelectedDate] || [])
                        .slice()
                        .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
                        .map((task) => {
                          const isConfirmed = ['ASSIGNED', 'COMPLETED', 'INVOICED'].includes(task.status);
                          return (
                            <div key={task.id} className={`calendar-day-overview-item ${isConfirmed ? 'confirmed' : 'pending'}`}>
                              <div className="calendar-day-overview-time">
                                {formatTimeOnly(task.scheduledAt)}{task.cleaningEnd ? ` - ${formatTimeOnly(task.cleaningEnd)}` : ''}
                              </div>
                              <div className="calendar-day-overview-details">
                                <div className="calendar-day-overview-property">{task.property_name}</div>
                                {task.address && <div className="calendar-day-overview-address">{task.address}</div>}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'users' && !calendarViewCleaner && (
          <>
            <div className="section-header">
              <h2>{t('admin.tabUsers')}</h2>
              <button onClick={() => setShowUserModal(true)} className="add-btn">+ {t('admin.newUser')}</button>
            </div>

            {/* Rol-filter: laat de admin snel enkel klanten, schoonmakers of
                admins tonen i.p.v. altijd de volledige lijst. */}
            <div className={`user-role-filter ${isNativeApp() ? 'user-role-filter-native' : ''}`}>
              <button className={`tab-btn ${userRoleFilter === 'ALL' ? 'active' : ''}`} onClick={() => setUserRoleFilter('ALL')}>
                {t('admin.roleFilterAll')}
              </button>
              <button className={`tab-btn ${userRoleFilter === 'VERHUURDER' ? 'active' : ''}`} onClick={() => setUserRoleFilter('VERHUURDER')}>
                {t('admin.roleOwner')}
              </button>
              <button className={`tab-btn ${userRoleFilter === 'SCHOONMAKER' ? 'active' : ''}`} onClick={() => setUserRoleFilter('SCHOONMAKER')}>
                {t('admin.roleCleaner')}
              </button>
              <button className={`tab-btn ${userRoleFilter === 'ADMIN' ? 'active' : ''}`} onClick={() => setUserRoleFilter('ADMIN')}>
                {t('admin.roleAdmin')}
              </button>
            </div>

            {/* Filter (en in de app ook sortering) op de aanmaakdatum van het account. */}
            <div className={`user-created-toolbar ${isNativeApp() ? 'user-created-toolbar-native' : ''}`}>
              <label className="user-created-field">
                <span>{t('admin.createdFilterLabel')}</span>
                <select value={userCreatedFilter} onChange={(e) => setUserCreatedFilter(e.target.value)}>
                  <option value="ALL">{t('admin.createdAll')}</option>
                  <option value="7">{t('admin.created7')}</option>
                  <option value="30">{t('admin.created30')}</option>
                  <option value="90">{t('admin.created90')}</option>
                  <option value="365">{t('admin.created365')}</option>
                  <option value="CUSTOM">{t('admin.createdCustom')}</option>
                </select>
              </label>
              {userCreatedFilter === 'CUSTOM' && (
                <>
                  <label className="user-created-field">
                    <span>{t('admin.createdFrom')}</span>
                    <input type="date" value={userCreatedFrom} max={userCreatedTo || undefined} onChange={(e) => setUserCreatedFrom(e.target.value)} />
                  </label>
                  <label className="user-created-field">
                    <span>{t('admin.createdTo')}</span>
                    <input type="date" value={userCreatedTo} min={userCreatedFrom || undefined} onChange={(e) => setUserCreatedTo(e.target.value)} />
                  </label>
                </>
              )}
              {isNativeApp() && (
                <label className="user-created-field">
                  <span>{t('admin.sortLabel')}</span>
                  <select
                    value={`${sortConfig.key}:${sortConfig.direction}`}
                    onChange={(e) => { const [key, direction] = e.target.value.split(':'); setSortConfig({ key, direction }); }}
                  >
                    <option value="createdAt:desc">{t('admin.sortNewest')}</option>
                    <option value="createdAt:asc">{t('admin.sortOldest')}</option>
                    <option value="name:asc">{t('admin.sortNameAsc')}</option>
                    <option value="name:desc">{t('admin.sortNameDesc')}</option>
                  </select>
                </label>
              )}
              <span className="user-created-count">{t('admin.usersCount', { count: filteredUsers.length })}</span>
            </div>

            {isNativeApp() ? (
              <div className="user-card-list">
                {filteredUsers.map((u) => {
                  const expanded = expandedUserId === u.id;
                  const ownerHasProperties = properties.some((p) => p.ownerId === u.id);
                  return (
                    <div key={u.id} className={`user-card ${expanded ? 'expanded' : ''}`}>
                      <div
                        className="user-card-head"
                        role="button"
                        tabIndex={0}
                        aria-expanded={expanded}
                        onClick={() => setExpandedUserId(expanded ? null : u.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setExpandedUserId(expanded ? null : u.id); }}
                      >
                        <span className="user-card-avatar"><Avatar url={u.photoUrl} name={u.name || u.email} size={34} /></span>
                        <span className="user-card-main">
                          <span className="user-card-name">{u.name || '-'}</span>
                          <span className={`role-badge role-${u.role.toLowerCase()}`}>
                            {u.role === 'SCHOONMAKER' ? t('admin.roleCleaner') : u.role === 'VERHUURDER' ? t('admin.roleOwner') : t('admin.roleAdmin')}
                          </span>
                        </span>
                        <span className="user-card-actions" onClick={(e) => e.stopPropagation()}>
                          {u.role === 'VERHUURDER' && (
                            <button
                              onClick={() => {
                                const ownerProperties = properties.filter((p) => p.ownerId === u.id);
                                if (ownerProperties.length === 0) return;
                                setCalendarViewProperty(ownerProperties[0]);
                                setCalendarViewScope('owner');
                                setActiveTab('properties');
                              }}
                              className="action-icon-btn action-icon-calendar"
                              disabled={!ownerHasProperties}
                              aria-label={t('admin.openOwnerCalendar')}
                            >
                              <CalendarIcon />
                            </button>
                          )}
                          {u.role === 'SCHOONMAKER' && (
                            <button
                              onClick={() => { setCalendarViewCleaner(u); setCalendarSelectedDate(null); }}
                              className="action-icon-btn action-icon-calendar"
                              aria-label={t('admin.openCleanerCalendar')}
                            >
                              <CalendarIcon />
                            </button>
                          )}
                          <button onClick={() => openEditUserModal(u)} className="action-icon-btn action-icon-edit" aria-label={t('common.edit')}>
                            <EditIcon />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ id: u.id, name: u.name || u.email, type: 'user' })}
                            className="action-icon-btn action-icon-delete"
                            disabled={u.id === user.id}
                            aria-label={t('common.delete')}
                          >
                            <TrashIcon />
                          </button>
                          {u.deletionRequestedAt && (
                            <button
                              onClick={() => handleReactivateUser(u.id, u.name || u.email)}
                              className="action-icon-btn action-icon-restore"
                              aria-label={t('admin.reactivateUser')}
                            >
                              <RestoreIcon />
                            </button>
                          )}
                        </span>
                        <svg className="user-card-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                      </div>
                      {expanded && (
                        <dl className="user-card-details">
                          <div><dt>{t('admin.colId')}</dt><dd className="user-card-id">{u.id}</dd></div>
                          <div><dt>{t('common.email')}</dt><dd>{u.email}</dd></div>
                          <div><dt>{t('admin.createdOn')}</dt><dd>{formatUserCreated(u.createdAt)}</dd></div>
                          <div>
                            <dt>{t('admin.colStatus')}</dt>
                            <dd>
                              <span className={`status-badge ${u.isActive ? 'status-active' : 'status-pending'}`}>
                                {u.isActive ? t('admin.statusActiveUser') : t('admin.statusPendingActivation')}
                              </span>
                              {u.deletionRequestedAt && (
                                <span className="status-badge status-deletion-requested">{t('admin.statusDeletionRequested')}</span>
                              )}
                            </dd>
                          </div>
                          {u.role !== 'ADMIN' && (
                            <button type="button" className="user-card-impersonate" onClick={() => handleImpersonate(u)}>
                              {t('admin.impersonateButton')}
                            </button>
                          )}
                        </dl>
                      )}
                    </div>
                  );
                })}
                {filteredUsers.length === 0 && <div className="no-results">{t('admin.noUsersFound')}</div>}
              </div>
            ) : (
            <div className="users-table users-table-fixed users-table-users">
              <table>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('id')} className="sortable">{t('admin.colId')} {getSortIcon('id')}</th>
                    <th onClick={() => handleSort('name')} className="sortable">{t('common.name')} {getSortIcon('name')}</th>
                    <th onClick={() => handleSort('email')} className="sortable">{t('common.email')} {getSortIcon('email')}</th>
                    <th onClick={() => handleSort('role')} className="sortable">{t('admin.colRole')} {getSortIcon('role')}</th>
                    <th onClick={() => handleSort('isActive')} className="sortable">{t('admin.colStatus')} {getSortIcon('isActive')}</th>
                    <th onClick={() => handleSort('createdAt')} className="sortable">{t('admin.colCreated')} {getSortIcon('createdAt')}</th>
                    <th className="col-actions">{t('admin.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <tr key={u.id}>
                      <td className="id-cell">{u.id}</td>
                      <td>
                        <span className="user-name-with-avatar">
                          <Avatar url={u.photoUrl} name={u.name || u.email} size={30} />
                          {u.role !== 'ADMIN' ? (
                            <button
                              type="button"
                              className="user-name-link"
                              onClick={() => handleImpersonate(u)}
                              title={t('admin.impersonateTooltip')}
                            >
                              {u.name || '-'}
                            </button>
                          ) : (
                            u.name || '-'
                          )}
                        </span>
                      </td>
                      <td>{u.email}</td>
                      <td>
                        <span className={`role-badge role-${u.role.toLowerCase()}`}>
                          {u.role === 'SCHOONMAKER' ? t('admin.roleCleaner') : u.role === 'VERHUURDER' ? t('admin.roleOwner') : t('admin.roleAdmin')}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${u.isActive ? 'status-active' : 'status-pending'}`}>
                          {u.isActive ? t('admin.statusActiveUser') : t('admin.statusPendingActivation')}
                        </span>
                        {/* Los, tweede badgetje onder de gewone actief/pending-
                            status - een gebruiker kan actief zijn EN toch een
                            verwijdering aangevraagd hebben, dus dit vervangt de
                            bovenste badge niet, het vult aan. */}
                        {u.deletionRequestedAt && (
                          <span className="status-badge status-deletion-requested">
                            {t('admin.statusDeletionRequested')}
                          </span>
                        )}
                      </td>
                      <td className="created-cell">{formatUserCreated(u.createdAt)}</td>
                      <td className="col-actions">
                        {u.role === 'VERHUURDER' && (
                          <button
                            onClick={() => {
                              const ownerProperties = properties.filter((p) => p.ownerId === u.id);
                              if (ownerProperties.length === 0) return;
                              setCalendarViewProperty(ownerProperties[0]);
                              setCalendarViewScope('owner');
                              setActiveTab('properties');
                            }}
                            className="action-icon-btn action-icon-calendar"
                            disabled={properties.filter((p) => p.ownerId === u.id).length === 0}
                            title={t('admin.openOwnerCalendar')}
                            aria-label={t('admin.openOwnerCalendar')}
                          >
                            <CalendarIcon />
                          </button>
                        )}
                        {u.role === 'SCHOONMAKER' && (
                          <button
                            onClick={() => { setCalendarViewCleaner(u); setCalendarSelectedDate(null); }}
                            className="action-icon-btn action-icon-calendar"
                            title={t('admin.openCleanerCalendar')}
                            aria-label={t('admin.openCleanerCalendar')}
                          >
                            <CalendarIcon />
                          </button>
                        )}
                        {u.role !== 'VERHUURDER' && u.role !== 'SCHOONMAKER' && <span className="action-icon-slot" aria-hidden="true" />}
                        <button onClick={() => openEditUserModal(u)} className="action-icon-btn action-icon-edit" title={t('common.edit')} aria-label={t('common.edit')}>
                          <EditIcon />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ id: u.id, name: u.name || u.email, type: 'user' })}
                          className="action-icon-btn action-icon-delete"
                          disabled={u.id === user.id}
                          title={t('common.delete')}
                          aria-label={t('common.delete')}
                        >
                          <TrashIcon />
                        </button>
                        {/* Enkel zichtbaar als deze gebruiker zelf verwijdering
                            aanvroeg (zie ProfileMenu.js) - laat de admin die
                            aanvraag afwijzen en het account (isActive + login)
                            herstellen, als alternatief voor echt verwijderen. */}
                        {u.deletionRequestedAt ? (
                          <button
                            onClick={() => handleReactivateUser(u.id, u.name || u.email)}
                            className="action-icon-btn action-icon-restore"
                            title={t('admin.reactivateUser')}
                            aria-label={t('admin.reactivateUser')}
                          >
                            <RestoreIcon />
                          </button>
                        ) : <span className="action-icon-slot" aria-hidden="true" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredUsers.length === 0 && <div className="no-results">{t('admin.noUsersFound')}</div>}
            </div>
            )}
          </>
        )}

        {activeTab === 'properties' && calendarViewProperty && (
          <>
            <div className="section-header">
              <button onClick={() => setCalendarViewProperty(null)} className="back-link-btn">&larr; {t('admin.backToOverview')}</button>
            </div>
            <FilteredTimeline
              key={calendarViewProperty.id}
              properties={calendarViewOwnerProperties}
              userRole="ADMIN"
              onEditCleaning={handleEditCleaningFromTimeline}
              initialIds={[calendarViewProperty.id]}
            />
          </>
        )}

        {activeTab === 'properties' && !calendarViewProperty && (
          <>
            <div className="section-header">
              <h2>{t('admin.tabProperties')}</h2>
              <button onClick={() => setShowPropertyModal(true)} className="add-btn">+ {t('admin.newProperty')}</button>
            </div>

            {/* Filter (zoals de rolfilter bij gebruikers): alle accommodaties, of enkel met schoonmaak,
                automation of toegangsbeheer. */}
            <div className={`user-role-filter property-filter ${isNativeApp() ? 'user-role-filter-native' : ''}`}>
              <button className={`tab-btn ${propertyFilter === 'ALL' ? 'active' : ''}`} onClick={() => setPropertyFilter('ALL')}>
                {t('admin.roleFilterAll')}
              </button>
              <button className={`tab-btn ${propertyFilter === 'CLEANING' ? 'active' : ''}`} onClick={() => setPropertyFilter('CLEANING')}>
                {t('admin.tabCleaning')}
              </button>
              <button className={`tab-btn ${propertyFilter === 'AUTOMATION' ? 'active' : ''}`} onClick={() => setPropertyFilter('AUTOMATION')}>
                {t('admin.tabAutomation')}
              </button>
              <button className={`tab-btn ${propertyFilter === 'ACCESS' ? 'active' : ''}`} onClick={() => setPropertyFilter('ACCESS')}>
                {t('admin.tabAccessManagement')}
              </button>
            </div>

            {isNativeApp() ? (
              <div className="user-card-list property-card-list">
                {filteredProperties.map((p) => {
                  const expanded = expandedPropertyId === p.id;
                  const cleanersLabel = (p.defaultCleaners || []).map((c) => c.cleaner_name || c.cleaner_email).join(', ');
                  const brandLabel = { NUKI: 'Nuki', TEDEE: 'Tedee' }[p.smartlockBrand] || p.smartlockBrand;
                  return (
                    <div key={p.id} className={`user-card ${expanded ? 'expanded' : ''}`}>
                      <div
                        className="user-card-head"
                        role="button"
                        tabIndex={0}
                        aria-expanded={expanded}
                        onClick={() => setExpandedPropertyId(expanded ? null : p.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setExpandedPropertyId(expanded ? null : p.id); }}
                      >
                        {p.photoUrl ? (
                          <img src={`${API_URL}${p.photoUrl}`} alt="" className="property-card-photo" />
                        ) : (
                          <span className="property-card-photo property-card-photo-placeholder" aria-hidden="true">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 11L12 3L21 11M5 9.222V20H9.5V14H14.5V20H19V9.222" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            </svg>
                          </span>
                        )}
                        <span className="user-card-main">
                          <span className="user-card-name">{p.name}</span>
                          <span className="property-card-sub">{p.owner_name || p.owner_email || '-'}</span>
                        </span>
                        <span className="user-card-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => { setCalendarViewProperty(p); setCalendarViewScope('single'); }}
                            className="action-icon-btn action-icon-calendar"
                            aria-label={t('admin.openCalendar')}
                          >
                            <CalendarIcon />
                          </button>
                          <button onClick={() => openEditPropertyModal(p)} className="action-icon-btn action-icon-edit" aria-label={t('common.edit')}>
                            <EditIcon />
                          </button>
                          <button onClick={() => handleDeleteProperty(p.id, p.name)} className="action-icon-btn action-icon-delete" aria-label={t('common.delete')}>
                            <TrashIcon />
                          </button>
                        </span>
                        <svg className="user-card-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                      </div>
                      {expanded && (
                        <dl className="user-card-details property-card-details">
                          <div><dt>{t('common.address')}</dt><dd>{p.address || '-'}</dd></div>
                          <div><dt>{t('admin.colOwner')}</dt><dd>{p.owner_name || p.owner_email || '-'}</dd></div>
                          <div><dt>{t('admin.colBookingSource')}</dt><dd>{getBookingSourceLabel(p)}</dd></div>
                          <div>
                            <dt>{t('admin.colNukiLock')}</dt>
                            <dd>
                              {p.nukiSmartlockId ? (
                                <>{brandLabel || t('admin.smartlockBrandNone')} · {renderLockStatus(p)}</>
                              ) : p.smartlockBrand ? (
                                <>{brandLabel} · {t('admin.smartlockIdMissing')}</>
                              ) : '-'}
                            </dd>
                          </div>
                          <div>
                            <dt>{t('admin.colCleaning')}</dt>
                            <dd>
                              {p.cleaningTier === 'NONE'
                                ? t('admin.managedNone')
                                : p.cleaningTier === 'PARTIAL'
                                  ? t('admin.managedBySelf')
                                  : t('admin.managedByUs')}
                              {(p.cleaningWindowStart || p.cleaningWindowEnd) && (
                                <span className="property-card-subline">{p.cleaningWindowStart || '?'}-{p.cleaningWindowEnd || '?'}</span>
                              )}
                              {cleanersLabel && <span className="property-card-subline">{cleanersLabel}</span>}
                            </dd>
                          </div>
                          {p.nukiSmartlockId && (
                            <button
                              type="button"
                              className="user-card-impersonate"
                              disabled={applyingLockDefaultsId === p.id}
                              onClick={() => handleApplyLockDefaults(p.id)}
                            >
                              {t('admin.lockDefaultsButton')}
                            </button>
                          )}
                        </dl>
                      )}
                    </div>
                  );
                })}
                {filteredProperties.length === 0 && <div className="no-results">{t('admin.noPropertiesFound')}</div>}
              </div>
            ) : (
            <div className="users-table users-table-fixed users-table-properties">
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th onClick={() => handlePropertySort('name')} className="sortable">{t('common.name')} {getPropertySortIcon('name')}</th>
                    <th onClick={() => handlePropertySort('address')} className="sortable">{t('common.address')} {getPropertySortIcon('address')}</th>
                    <th onClick={() => handlePropertySort('owner')} className="sortable">{t('admin.colOwner')} {getPropertySortIcon('owner')}</th>
                    <th onClick={() => handlePropertySort('bookingSource')} className="sortable">{t('admin.colBookingSource')} {getPropertySortIcon('bookingSource')}</th>
                    <th onClick={() => handlePropertySort('nukiLock')} className="sortable">{t('admin.colNukiLock')} {getPropertySortIcon('nukiLock')}</th>
                    <th onClick={() => handlePropertySort('cleaningTier')} className="sortable">{t('admin.colCleaning')} {getPropertySortIcon('cleaningTier')}</th>
                    <th className="col-actions">{t('admin.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProperties.map(p => (
                    <tr key={p.id}>
                      <td>
                        {p.photoUrl ? (
                          <img src={`${API_URL}${p.photoUrl}`} alt="" className="property-photo-thumb-table" />
                        ) : (
                          <span className="property-photo-thumb-table property-photo-thumb-placeholder">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 11L12 3L21 11M5 9.222V20H9.5V14H14.5V20H19V9.222" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            </svg>
                          </span>
                        )}
                      </td>
                      <td>{p.name}</td>
                      <td>{p.address || '-'}</td>
                      <td>{p.owner_name || p.owner_email || '-'}</td>
                      <td>{getBookingSourceLabel(p)}</td>
                      <td>
                        {p.nukiSmartlockId ? (
                          <>
                            <div>{{ NUKI: 'Nuki', TEDEE: 'Tedee' }[p.smartlockBrand] || p.smartlockBrand || t('admin.smartlockBrandNone')}</div>
                            <div className="table-subtext">
                              {(() => {
                                const status = smartlockStatuses[p.id];
                                if (!status || status.loading) return '...';
                                if (status.error || (status.linked && status.configured && !status.reachable)) return t('admin.statusUnreachable');
                                if (status.linked && !status.configured) return t('admin.statusNotActive');
                                if (status.reachable && status.batteryCharge != null) {
                                  const batteryClass = status.batteryCharge <= 20 ? 'battery-value-critical' : status.batteryCharge <= 35 ? 'battery-value-warning' : '';
                                  return <span className={batteryClass}>{status.batteryCharge}%</span>;
                                }
                                return t('admin.statusUnknown');
                              })()}
                            </div>
                          </>
                        ) : p.smartlockBrand ? (
                          <>
                            <div>{{ NUKI: 'Nuki', TEDEE: 'Tedee' }[p.smartlockBrand] || p.smartlockBrand}</div>
                            <div className="table-subtext">{t('admin.smartlockIdMissing')}</div>
                          </>
                        ) : '-'}
                      </td>
                      <td>
                        <div>
                          {p.cleaningTier === 'NONE'
                            ? t('admin.managedNone')
                            : p.cleaningTier === 'PARTIAL'
                              ? t('admin.managedBySelf')
                              : t('admin.managedByUs')}
                        </div>
                        {(p.cleaningWindowStart || p.cleaningWindowEnd) && (
                          <div className="table-subtext">{p.cleaningWindowStart || '?'}-{p.cleaningWindowEnd || '?'}</div>
                        )}
                        {(p.defaultCleaners || []).length > 0 && (
                          <div
                            className="table-subtext table-subtext-truncate"
                            title={p.defaultCleaners.map((c) => c.cleaner_name || c.cleaner_email).join(', ')}
                          >
                            {p.defaultCleaners.map((c) => c.cleaner_name || c.cleaner_email).join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="col-actions">
                        <button
                          onClick={() => { setCalendarViewProperty(p); setCalendarViewScope('single'); }}
                          className="action-icon-btn action-icon-calendar"
                          title={t('admin.openCalendar')}
                          aria-label={t('admin.openCalendar')}
                        >
                          <CalendarIcon />
                        </button>
                        <button onClick={() => openEditPropertyModal(p)} className="action-icon-btn action-icon-edit" title={t('common.edit')} aria-label={t('common.edit')}>
                          <EditIcon />
                        </button>
                        <button onClick={() => handleDeleteProperty(p.id, p.name)} className="action-icon-btn action-icon-delete" title={t('common.delete')} aria-label={t('common.delete')}>
                          <TrashIcon />
                        </button>
                        {p.nukiSmartlockId ? (
                          <button
                            onClick={() => handleApplyLockDefaults(p.id)}
                            className="action-icon-btn action-icon-settings"
                            disabled={applyingLockDefaultsId === p.id}
                            title={t('admin.lockDefaultsButtonHint')}
                            aria-label={t('admin.lockDefaultsButton')}
                          >
                            <GearIcon />
                          </button>
                        ) : <span className="action-icon-slot" aria-hidden="true" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredProperties.length === 0 && <div className="no-results">{t('admin.noPropertiesFound')}</div>}
            </div>
            )}
          </>
        )}

        {activeTab === 'newsletter' && (
          <>
            <div className="section-header">
              <h2>{t('admin.tabNewsletter')}</h2>
            </div>

            <div className="mailing-groups-bar">
              <span className="mailing-groups-label">{t('admin.mailingGroupsLabel')}</span>
              <label className="mailing-group-chip">
                <input type="checkbox" checked={mailingGroups.newsletter} onChange={() => toggleMailingGroup('newsletter')} />
                {t('admin.mailingGroupNewsletter')}
              </label>
              <label className="mailing-group-chip">
                <input type="checkbox" checked={mailingGroups.cleaners} onChange={() => toggleMailingGroup('cleaners')} />
                {t('admin.mailingGroupCleaners')}
              </label>
              <label className="mailing-group-chip">
                <input type="checkbox" checked={mailingGroups.landlords} onChange={() => toggleMailingGroup('landlords')} />
                {t('admin.mailingGroupLandlords')}
              </label>
              <button onClick={handleExportMailing} className="add-btn mailing-export-btn">{t('admin.exportCsv')}</button>
            </div>

            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('common.email')}</th>
                    <th>{t('admin.colSubscribedAt')}</th>
                    <th>{t('admin.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNewsletterSubscribers.map(s => (
                    <tr key={s.id}>
                      <td>{s.email}</td>
                      <td>{formatDate(s.createdAt)}</td>
                      <td>
                        <span className={`status-badge ${s.unsubscribedAt ? 'status-pending' : 'status-active'}`}>
                          {s.unsubscribedAt ? t('admin.statusUnsubscribed') : t('admin.statusSubscribed')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredNewsletterSubscribers.length === 0 && <div className="no-results">{t('admin.noSubscribersFound')}</div>}
            </div>
          </>
        )}

        {activeTab === 'demo' && (
          <>
            <div className="section-header">
              <h2>Demo</h2>
            </div>

            <div className="demo-config-card">
              <div className="demo-config-row">
                <label className="demo-config-checkbox">
                  <input
                    type="checkbox"
                    checked={demoConfig.access}
                    onChange={(e) => setDemoConfig({ ...demoConfig, access: e.target.checked })}
                  />
                  Toegangsbeheer
                </label>
                <label className="demo-config-checkbox">
                  <input
                    type="checkbox"
                    checked={demoConfig.automation}
                    onChange={(e) => setDemoConfig({ ...demoConfig, automation: e.target.checked })}
                  />
                  Automation
                </label>
              </div>

              <div className="demo-config-row">
                <div className="form-group">
                  <label>Schoonmaak</label>
                  <select
                    value={demoConfig.tier}
                    onChange={(e) => setDemoConfig({ ...demoConfig, tier: e.target.value })}
                  >
                    <option value="FULL">Volledig</option>
                    <option value="PARTIAL">Occasioneel</option>
                    <option value="NONE">Niet</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Aantal accommodaties</label>
                  <input
                    type="number"
                    min="1"
                    max="6"
                    value={demoConfig.count}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Laat het veld tijdens het typen leeg/los staan i.p.v.
                      // meteen terug te springen naar "1" (parseInt("") is
                      // NaN) - anders kan je nooit een nieuw getal intypen
                      // zonder eerst alles te selecteren en te overtypen.
                      if (raw === '') {
                        setDemoConfig({ ...demoConfig, count: raw });
                        return;
                      }
                      const parsed = parseInt(raw, 10);
                      if (!isNaN(parsed)) {
                        setDemoConfig({ ...demoConfig, count: parsed });
                      }
                    }}
                    onBlur={(e) => {
                      const parsed = parseInt(e.target.value, 10);
                      setDemoConfig({ ...demoConfig, count: Math.min(6, Math.max(1, isNaN(parsed) ? 1 : parsed)) });
                    }}
                  />
                </div>
              </div>

              <div className="demo-config-row">
                <div className="form-group">
                  <label>Toestel</label>
                  <div className="nav-view-toggle">
                    <button
                      type="button"
                      className={`nav-view-btn ${demoConfig.device === 'desktop' ? 'active' : ''}`}
                      onClick={() => setDemoConfig({ ...demoConfig, device: 'desktop' })}
                    >
                      Desktop
                    </button>
                    <button
                      type="button"
                      className={`nav-view-btn ${demoConfig.device === 'mobile' ? 'active' : ''}`}
                      onClick={() => setDemoConfig({ ...demoConfig, device: 'mobile' })}
                    >
                      Mobiel
                    </button>
                  </div>
                </div>
              </div>
              <button
                className="add-btn demo-open-btn"
                onClick={() => {
                  const parsedCount = parseInt(demoConfig.count, 10);
                  const safeCount = Math.min(6, Math.max(1, isNaN(parsedCount) ? 1 : parsedCount));
                  const params = new URLSearchParams({
                    access: demoConfig.access ? '1' : '0',
                    automation: demoConfig.automation ? '1' : '0',
                    tier: demoConfig.tier,
                    count: String(safeCount),
                    device: demoConfig.device
                  });
                  const demoUrl = `/demo?${params.toString()}`;
                  if (localStorage.getItem('isNativeApp') === 'true') {
                    navigate(demoUrl);
                  } else {
                    window.open(demoUrl, '_blank', 'noopener');
                  }
                }}
              >
                Demo openen in nieuw tabblad
              </button>
            </div>
          </>
        )}

        {activeTab === 'to-schedule' && (
          <>
            {schedulingProperty ? (
              <>
                <div className="section-header">
                  <button onClick={closePropertySchedule} className="back-link-btn">&larr; {t('admin.backToOverview')}</button>
                  <div className="nav-view-toggle schedule-view-toggle">
                    <button
                      className={`nav-view-btn ${scheduleView === 'multi' ? 'active' : ''}`}
                      onClick={() => setScheduleView('multi')}
                    >
                      {t('admin.tabMultiCalendar')}
                    </button>
                    <button
                      className={`nav-view-btn ${scheduleView === 'month' ? 'active' : ''}`}
                      onClick={() => setScheduleView('month')}
                    >
                      {t('admin.monthCalendarView')}
                    </button>
                  </div>
                </div>
                {scheduleView === 'multi' ? (
                  <FilteredTimeline
                    key={schedulingProperty.propertyId}
                    properties={schedulingOwnerProperties}
                    userRole="ADMIN"
                    onEditCleaning={handleEditCleaningFromTimeline}
                    initialIds={[schedulingProperty.propertyId]}
                  />
                ) : (
                  <PropertyCalendar
                    propertyId={schedulingProperty.propertyId}
                    propertyName={schedulingProperty.property_name}
                    userRole="ADMIN"
                    cleaningHours={properties.find((p) => p.id === schedulingProperty.propertyId)?.cleaningHours}
                    defaultCleanerCount={properties.find((p) => p.id === schedulingProperty.propertyId)?.defaultCleanerCount}
                    cleaningServices={properties.find((p) => p.id === schedulingProperty.propertyId)?.cleaningServices}
                    onEditCleaning={handleEditCleaningFromTimeline}
                  />
                )}
              </>
            ) : (
              <>
                <div className="section-header">
                  <h2>{t('admin.tabToSchedule')}</h2>
                  <button onClick={() => setShowAddCleaningModal(true)} className="add-btn">+ {t('admin.addCleaningButton')}</button>
                </div>

                {pendingApprovalTasks.length > 0 && (
                  <div className="calendar-container pending-approval-section">
                    <h3 className="pending-approval-heading">
                      {t('admin.pendingApprovalTitle')} ({pendingApprovalTasks.length})
                    </h3>
                    <p className="info-text">{t('admin.pendingApprovalInfo')}</p>
                    <div className="schedule-cards-list">
                      {pendingApprovalTasks.map((task) => (
                        <div key={task.id} className="schedule-card pending-approval-card">
                          <div className="schedule-card-top">
                            <div className="schedule-card-title-row">
                              <h4>{task.property_name}</h4>
                              <span className="status-badge status-badge-pending-approval">
                                {t('admin.pendingApprovalBadge')}
                              </span>
                            </div>
                            <p className="schedule-card-meta">
                              {new Date(task.scheduledAt).toLocaleDateString('nl-NL')}
                              {' '}{formatTimeOnly(task.scheduledAt)} - {formatTimeOnly(task.cleaningEnd)}
                              {' · '}{task.address || t('cleaner.noAddress')}
                              {' · '}{t('admin.addedByLandlord', { name: task.owner_name || t('admin.unknown') })}
                            </p>
                            {task.notes && (
                              <p className="schedule-card-meta">{t('cleaner.notes')}: {task.notes}</p>
                            )}
                          </div>
                          <div className="schedule-card-buttons">
                            <button
                              onClick={() => handleApprovePendingTask(task.id)}
                              className="schedule-btn schedule-btn-approve"
                              disabled={approvingTaskId === task.id}
                            >
                              {approvingTaskId === task.id ? t('common.loading') : t('admin.approveButton')}
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task.id)}
                              className="action-icon-btn action-icon-delete"
                              title={t('admin.rejectButton')}
                              aria-label={t('admin.rejectButton')}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="calendar-container">
                  {bookingsToSchedule.length === 0 ? (
                    <div className="calendar-placeholder">
                      <div className="placeholder-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M8 2V6M16 2V6M3 10H21" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                      </div>
                      <h3>{t('admin.nothingToSchedule')}</h3>
                      <p>{t('admin.allBookingsScheduled')}</p>
                      <p className="placeholder-note">{t('admin.newBookingsAppearHere')}</p>
                    </div>
                  ) : (
                    <div className="schedule-cards-list">
                      {bookingsToSchedule.map(booking => {
                        // Schoonmaakdiensten van deze woning (PropertyCleaningService) -
                        // enkel een keuzemenu tonen als er meer dan 1 dienst is, anders
                        // is er toch niets te kiezen en blijft de kaart compact.
                        const propRecordForServices = properties.find((p) => p.id === booking.propertyId);
                        const availableServices = propRecordForServices?.cleaningServices || [];
                        const scheduleCardOpen = !isNative || openScheduleBookingId === booking.id;
                        return (
                        <div key={booking.id} className={`schedule-card ${isNative ? 'schedule-card-collapsible' : ''} ${scheduleCardOpen ? '' : 'is-collapsed'}`}>
                          <div
                            className="schedule-card-top"
                            role={isNative ? 'button' : undefined}
                            tabIndex={isNative ? 0 : undefined}
                            aria-expanded={isNative ? scheduleCardOpen : undefined}
                            onClick={isNative ? () => setOpenScheduleBookingId((prev) => (prev === booking.id ? null : booking.id)) : undefined}
                          >
                            {isNative && (
                              <svg className="schedule-card-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                            )}
                            <div className="schedule-card-date">
                              {/* Toont de volledige verblijfsperiode (inchek t.e.m. uitchek)
                                  i.p.v. enkel de uitcheck-dag, zodat meteen duidelijk is hoe
                                  lang de gast er verbleef - bv. "30 september om 15:00 -
                                  1 november om 11:00". */}
                              {isNative ? (
                                // Korte vorm in de app ("24 okt 11:00 → 27 okt 10:00"), zodat
                                // de periode op 1 regel past.
                                <>
                                  {booking.checkIn && (
                                    <>
                                      {new Date(booking.checkIn).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                                      {' '}{new Date(booking.checkIn).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                                      {' → '}
                                    </>
                                  )}
                                  {new Date(booking.checkOut).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                                  {' '}{new Date(booking.checkOut).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                                </>
                              ) : (
                                <>
                                  {booking.checkIn && (
                                    <>
                                      {new Date(booking.checkIn).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}
                                      {' '}{t('admin.at')} {new Date(booking.checkIn).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                                      {' - '}
                                    </>
                                  )}
                                  {new Date(booking.checkOut).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}
                                  {' '}{t('admin.at')} {new Date(booking.checkOut).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                                </>
                              )}
                            </div>
                            <div className="schedule-card-title-row">
                              <h4>{booking.property_name}</h4>
                            </div>
                            <p className="schedule-card-meta">
                              {booking.address || t('cleaner.noAddress')}
                              {' · '}{booking.owner_name || t('admin.unknown')}
                            </p>
                          </div>
                          <div className="schedule-card-actions">
                            <div className="schedule-card-times">
                            <div className="schedule-card-times-left">
                              <div className="form-group">
                                <label className="form-subgroup-label">{t('admin.cleaningDateLabel')}</label>
                                <input
                                  type="date"
                                  value={getApproveDate(booking.id, booking.checkOut)}
                                  onChange={(e) => setApproveDate(booking.id, e.target.value)}
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-subgroup-label">{t('admin.cleaningWindowStartLabel')}</label>
                                <input
                                  type="time"
                                  value={getApproveTime(booking.id, 'start', booking.propertyId)}
                                  onChange={(e) => setApproveTime(booking.id, 'start', e.target.value)}
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-subgroup-label">{t('admin.cleaningWindowEndLabel')}</label>
                                <input
                                  type="time"
                                  value={getApproveTime(booking.id, 'end', booking.propertyId)}
                                  onChange={(e) => setApproveTime(booking.id, 'end', e.target.value)}
                                />
                              </div>
                              {availableServices.length > 0 && (
                                <div className="form-group">
                                  <label className="form-subgroup-label">{t('admin.cleaningServiceLabel')}</label>
                                  <select
                                    className="schedule-service-select"
                                    value={getApproveAmount(booking.id, 'serviceId') || availableServices[0]?.id || ''}
                                    onChange={(e) => setApproveAmount(booking.id, 'serviceId', e.target.value)}
                                    required
                                  >
                                    {availableServices.map((s) => (
                                      <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                              {/* Geen Klantprijs/Loon schoonmaker-velden meer hier - dat
                                  komt al automatisch uit de gekozen Schoonmaakdienst
                                  hierboven, apart instellen was dubbel werk. Nog wel
                                  aanpasbaar nadien via "bewerk schoonmaak" bij Ingepland.
                                  Geen form-group-full meer: gewoon tussen Dag/Van/Tot/
                                  Schoonmaakdienst mee op dezelfde rij (daar is nog ruimte),
                                  smaller en hoger i.p.v. de volle breedte - zie
                                  .schedule-card-notes-field in Admin.css. Ook geen
                                  placeholder-tekst meer - gewoon leeg, geen voorbeeldtekst. */}
                              <div className="form-group schedule-card-notes-field">
                                <label className="form-subgroup-label">{t('cleaner.notes')}</label>
                                <textarea
                                  rows={3}
                                  value={getApproveAmount(booking.id, 'notes')}
                                  onChange={(e) => setApproveAmount(booking.id, 'notes', e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="schedule-card-buttons">
                              <button onClick={() => openPropertySchedule(booking)} className="schedule-btn schedule-btn-calendar">
                                {t('admin.calendarButton')}
                              </button>
                              <button
                                onClick={() => handleApproveSchedule(booking)}
                                className="schedule-btn schedule-btn-approve"
                                disabled={approvingPropertyId === booking.id}
                              >
                                {approvingPropertyId === booking.id ? t('common.loading') : t('admin.approveButton')}
                              </button>
                              <button type="button" className="schedule-btn schedule-btn-reject" onClick={() => handleRejectCleaning(booking.id)}>
                                {t('admin.rejectCleaningButton')}
                              </button>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}


        {activeTab === 'scheduled' && (
          <>
            <div className="section-header">
              <h2>{t('admin.tabScheduled')}</h2>
            </div>

            <div className="user-role-filter">
              <button className={`tab-btn ${scheduledStatusFilter === 'all' ? 'active' : ''}`} onClick={() => setScheduledStatusFilter('all')}>
                {t('admin.scheduledFilterAll')}
              </button>
              <button className={`tab-btn ${scheduledStatusFilter === 'open' ? 'active' : ''}`} onClick={() => setScheduledStatusFilter('open')}>
                {t('admin.scheduledFilterOpen')}
              </button>
              <button className={`tab-btn ${scheduledStatusFilter === 'assigned' ? 'active' : ''}`} onClick={() => setScheduledStatusFilter('assigned')}>
                {t('admin.scheduledFilterAssigned')}
              </button>
            </div>

            <div className="calendar-container">
              {filteredScheduledTasks.length === 0 ? (
                <div className="calendar-placeholder">
                  <div className="placeholder-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M8 2V6M16 2V6M3 10H21" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <h3>{t('admin.noneScheduled')}</h3>
                  <p>{t('admin.scheduleViaTab')}</p>
                </div>
              ) : (
                renderTaskDays(scheduledTasksByDay, false)
              )}
            </div>
          </>
        )}

        {activeTab === 'completed' && (
          <>
            <div className="section-header">
              <h2>{t('admin.tabCompleted')}</h2>
            </div>
            <div className="calendar-container">
              {completedTasksByDayList.length === 0 ? (
                <div className="calendar-placeholder">
                  <h3>{t('admin.noneCompleted')}</h3>
                </div>
              ) : (
                renderTaskDays(completedTasksByDayList, true)
              )}
            </div>
          </>
        )}

        {activeTab === 'payout' && (
          <>
            <div className="section-header">
              <h2>{t('admin.tabPayout')} <span className="section-total" title={t('admin.grandTotalPayout')}>({formatPayoutAmount(payoutGroups.reduce((sum, g) => sum + (Number(g.totalAmount) || 0), 0))})</span></h2>
            </div>

            <div className="month-selector">
              {/* "Openstaande" blijft, zolang er geen datumfilter actief is,
                  altijd ALLE nog niet uitbetaalde taken tonen (ongeacht
                  datum) - zodra hieronder een maand/periode gekozen wordt,
                  geldt dat ook mee terwijl "Openstaande" aan staat (dan zie
                  je dus enkel de nog openstaande taken BINNEN die maand/
                  periode). */}
              <label className="outstanding-toggle">
                <span>{t('admin.monthOutstanding')}</span>
                <span className={`outstanding-switch ${payoutOutstandingOnly ? 'on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={payoutOutstandingOnly}
                    onChange={(e) => setPayoutOutstandingOnly(e.target.checked)}
                  />
                  <span className="outstanding-slider" />
                </span>
              </label>
              <div className="date-filter-controls">
                <div className="nav-view-toggle date-filter-mode-toggle">
                  <button
                    type="button"
                    className={`nav-view-btn ${payoutDateMode === 'all' ? 'active' : ''}`}
                    onClick={() => setPayoutDateMode('all')}
                  >
                    {t('admin.dateFilterAll')}
                  </button>
                  <button
                    type="button"
                    className={`nav-view-btn ${payoutDateMode === 'month' ? 'active' : ''}`}
                    onClick={() => setPayoutDateMode('month')}
                  >
                    {t('admin.dateFilterByMonth')}
                  </button>
                  <button
                    type="button"
                    className={`nav-view-btn ${payoutDateMode === 'range' ? 'active' : ''}`}
                    onClick={() => setPayoutDateMode('range')}
                  >
                    {t('admin.dateFilterByRange')}
                  </button>
                </div>
                {payoutDateMode === 'month' && (
                  <input
                    type="month"
                    value={payoutSelectedMonth}
                    onChange={(e) => setPayoutSelectedMonth(e.target.value)}
                  />
                )}
                {payoutDateMode === 'range' && (
                  <div className="date-filter-range">
                    <input
                      type="date"
                      value={payoutRangeStart}
                      onChange={(e) => setPayoutRangeStart(e.target.value)}
                      placeholder={t('admin.dateFilterFrom')}
                    />
                    <span>{t('admin.dateFilterRangeSeparator')}</span>
                    <input
                      type="date"
                      value={payoutRangeEnd}
                      onChange={(e) => setPayoutRangeEnd(e.target.value)}
                      placeholder={t('admin.dateFilterTo')}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="calendar-container">
              {payoutGroups.length === 0 ? (
                <div className="calendar-placeholder">
                  <div className="placeholder-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M8 2V6M16 2V6M3 10H21" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <h3>{t('admin.nothingToInvoice')}</h3>
                  <p>{t('admin.tasksAppearWhenCompleted')}</p>
                </div>
              ) : (
                <div className="payout-groups">
                  {payoutGroups.map((group) => {
                    const hasOutstanding = group.tasks.some((task) => !isSlotPaid(task, getCleanerSlot(task, group.cleanerId)));
                    const isExpanded = !!expandedPayoutGroups[group.cleanerId];
                    return (
                      <div key={group.cleanerId} className={`payout-group ${hasOutstanding ? '' : 'payout-group-readonly'}`}>
                        <div className="payout-group-header payout-group-header-clickable" onClick={() => togglePayoutGroup(group.cleanerId)}>
                          <div className="payout-group-name-block">
                            <h3>{group.cleanerName || t('admin.unknown')}</h3>
                            {group.cleanerEmail && <p className="payout-group-email">{group.cleanerEmail}</p>}
                          </div>
                          <div className="payout-group-total payout-group-total-inline" onClick={(e) => e.stopPropagation()}>
                            <span className="payout-hours">{t('admin.totalHours', { hours: formatHM(group.totalHours) })}</span>
                            <span className="payout-amount payout-amount-total">{formatPayoutAmount(group.totalAmount)}</span>
                            {hasOutstanding ? (
                              <button
                                onClick={() => handlePayoutCleaner(group.cleanerId)}
                                className="action-btn confirm-btn payout-action-btn"
                                disabled={payoutSubmittingId === group.cleanerId}
                              >
                                {payoutSubmittingId === group.cleanerId ? t('common.loading') : t('admin.markPaidOut')}
                              </button>
                            ) : (
                              <span className="invoiced-badge">{t('admin.alreadyPaidOut')}</span>
                            )}
                          </div>
                          <button
                            type="button"
                            className={`payout-group-toggle ${isExpanded ? 'payout-group-toggle-open' : ''}`}
                            aria-label={isExpanded ? t('admin.collapseDetails') : t('admin.expandDetails')}
                          >
                            <ChevronDownIcon />
                          </button>
                        </div>
                        {isExpanded && (
                          <>
                            <div className="payout-task-table-wrap">
                              <table className="payout-task-table">
                                <thead>
                                  <tr>
                                    <th>{t('common.date')}</th>
                                    <th>{t('admin.tabProperties')}</th>
                                    <th>{t('admin.cleaningServiceLabel')}</th>
                                    <th>{t('admin.hoursLabel')}</th>
                                    <th>{t('admin.amountLabel')}</th>
                                    <th>{t('admin.clockedTimeShort')}</th>
                                    <th></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.tasks.map((task) => {
                                    const slotTimes = getCleanerSlotTimes(task, group.cleanerId);
                                    const slot = getCleanerSlot(task, group.cleanerId);
                                    const rowKey = `${task.id}_${group.cleanerId}`;
                                    const isEditingRow = payoutEditingRowKey === rowKey;
                                    const draftKey = `${task.id}_${slot}`;
                                    // Uren/bedrag zijn PER schoonmaker (slot) apart overschrijfbaar -
                                    // zelfde logica als in groupTasksByCleaner hierboven, maar dan
                                    // enkel voor DEZE ene rij/schoonmaker.
                                    const hoursOverrideField = `overrideCleaningHours${slot || ''}`;
                                    const amountOverrideField = `overridePayoutAmount${slot || ''}`;
                                    const hasHoursOverride = task[hoursOverrideField] !== null && task[hoursOverrideField] !== undefined;
                                    const hasSetHours = hasHoursOverride || (task.cleaning_hours !== null && task.cleaning_hours !== undefined);
                                    const defaultHoursShare = (Number(task.cleaning_hours) || 0) / getTaskCleanerCount(task);
                                    const rowHours = hasHoursOverride ? Number(task[hoursOverrideField]) : defaultHoursShare;
                                    // Bewust "defaultHoursShare" i.p.v. "rowHours" hier: uren- en
                                    // bedrag-override moeten volledig los van elkaar aan te passen
                                    // zijn - een uren-correctie mag het getoonde/berekende bedrag
                                    // niet automatisch mee veranderen (en omgekeerd).
                                    const rowAmount = (task[amountOverrideField] !== null && task[amountOverrideField] !== undefined)
                                      ? Number(task[amountOverrideField])
                                      : defaultHoursShare * (Number(task.cleaner_hourly_rate) || 0);
                                    return (
                                    <React.Fragment key={task.id}>
                                    <tr>
                                      <td data-label={t('common.date')}>{new Date(task.scheduledAt).toLocaleDateString('nl-NL')}</td>
                                      <td data-label={t('admin.tabProperties')}>{task.property_name}</td>
                                      <td data-label={t('admin.cleaningServiceLabel')}>{renderTaskServiceSelect(task, isSlotPaid(task, slot))}</td>
                                      <td data-label={t('admin.hoursLabel')}>{renderOverridableCell(task, hoursOverrideField, hasSetHours ? formatHoursShare(rowHours) : t('admin.hoursNotSet'), defaultHoursShare || undefined)}</td>
                                      <td data-label={t('admin.amountLabel')}>{renderOverridableCell(task, amountOverrideField, formatPayoutAmount(rowAmount))}</td>
                                      <td data-label={t('admin.clockedTimeShort')}>
                                        <span className="payout-clocktime-value">
                                          {slotTimes.in
                                            ? `${formatTimeOnly(slotTimes.in)} - ${slotTimes.out ? formatTimeOnly(slotTimes.out) : t('cleaner.clockedInOngoing')}`
                                            : '-'}
                                          {formatDurationHM(slotTimes.in, slotTimes.out) && (
                                            <span className="payout-hours-total-hint" title={t('admin.hoursTotalHint')}>
                                              {' '}({formatDurationHM(slotTimes.in, slotTimes.out)})
                                            </span>
                                          )}
                                        </span>
                                        {slot !== null && (
                                          <button
                                            type="button"
                                            onClick={() => setPayoutEditingRowKey(isEditingRow ? null : rowKey)}
                                            className={`action-icon-btn action-icon-edit action-icon-btn-sm ${isEditingRow ? 'action-icon-btn-active' : ''}`}
                                            title={t('admin.editClockTimes')}
                                            aria-label={t('admin.editClockTimes')}
                                          >
                                            <EditIcon />
                                          </button>
                                        )}
                                      </td>
                                      <td>
                                        {!isSlotPaid(task, slot) ? (
                                          <button onClick={() => handleUncompleteTask(task.id)} className="action-btn reject-btn payout-action-btn">{t('admin.revert')}</button>
                                        ) : (
                                          <span className="invoiced-badge">{t('admin.alreadyPaidOut')}</span>
                                        )}
                                      </td>
                                    </tr>
                                    {isEditingRow && slot !== null && (
                                      <tr className="payout-clocktime-edit-tr">
                                        <td colSpan={7}>
                                          <div className="clock-time-edit-row">
                                            <span className="clock-time-edit-name">{group.cleanerName}</span>
                                            <label className="clock-time-edit-field">
                                              {t('admin.clockInLabel')}
                                              <input
                                                type="time"
                                                value={getClockTimeDraft(task, slot, 'clockIn')}
                                                onChange={(e) => handleClockTimeFieldChange(task, slot, 'clockIn', e.target.value)}
                                                disabled={savingClockTimeKey === draftKey}
                                              />
                                            </label>
                                            <label className="clock-time-edit-field">
                                              {t('admin.clockOutLabel')}
                                              <input
                                                type="time"
                                                value={getClockTimeDraft(task, slot, 'clockOut')}
                                                onChange={(e) => handleClockTimeFieldChange(task, slot, 'clockOut', e.target.value)}
                                                disabled={savingClockTimeKey === draftKey}
                                              />
                                            </label>
                                            {savingClockTimeKey === draftKey && <span className="clocktime-saving-hint">{t('admin.savingClockTimes')}</span>}
                                            <button
                                              type="button"
                                              onClick={() => setPayoutEditingRowKey(null)}
                                              className="clocktime-done-btn"
                                            >
                                              {t('common.save')}
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                    </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'invoicing' && (
          <>
            <div className="section-header">
              <h2>{t('admin.tabInvoicing')} <span className="section-total" title={t('admin.grandTotalInvoice')}>(€{(invoiceGroups.reduce((sum, g) => sum + (Number(g.total) || 0), 0) + visibleSubscriptionInvoices.reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0)).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</span></h2>
            </div>

            <div className="month-selector">
              {/* Zelfde principe als bij Uitbetaling hierboven. */}
              <label className="outstanding-toggle">
                <span>{t('admin.monthOutstanding')}</span>
                <span className={`outstanding-switch ${invoiceOutstandingOnly ? 'on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={invoiceOutstandingOnly}
                    onChange={(e) => setInvoiceOutstandingOnly(e.target.checked)}
                  />
                  <span className="outstanding-slider" />
                </span>
              </label>
              <div className="date-filter-controls">
                <div className="nav-view-toggle date-filter-mode-toggle">
                  <button
                    type="button"
                    className={`nav-view-btn ${invoiceDateMode === 'all' ? 'active' : ''}`}
                    onClick={() => setInvoiceDateMode('all')}
                  >
                    {t('admin.dateFilterAll')}
                  </button>
                  <button
                    type="button"
                    className={`nav-view-btn ${invoiceDateMode === 'month' ? 'active' : ''}`}
                    onClick={() => setInvoiceDateMode('month')}
                  >
                    {t('admin.dateFilterByMonth')}
                  </button>
                  <button
                    type="button"
                    className={`nav-view-btn ${invoiceDateMode === 'range' ? 'active' : ''}`}
                    onClick={() => setInvoiceDateMode('range')}
                  >
                    {t('admin.dateFilterByRange')}
                  </button>
                </div>
                {invoiceDateMode === 'month' && (
                  <input
                    type="month"
                    value={invoiceSelectedMonth}
                    onChange={(e) => setInvoiceSelectedMonth(e.target.value)}
                  />
                )}
                {invoiceDateMode === 'range' && (
                  <div className="date-filter-range">
                    <input
                      type="date"
                      value={invoiceRangeStart}
                      onChange={(e) => setInvoiceRangeStart(e.target.value)}
                      placeholder={t('admin.dateFilterFrom')}
                    />
                    <span>{t('admin.dateFilterRangeSeparator')}</span>
                    <input
                      type="date"
                      value={invoiceRangeEnd}
                      onChange={(e) => setInvoiceRangeEnd(e.target.value)}
                      placeholder={t('admin.dateFilterTo')}
                    />
                  </div>
                )}
              </div>
            </div>

            {visibleSubscriptionInvoices.length > 0 && (
              <div className="subscription-invoices">
                <h3 className="subscription-invoices-title">{t('admin.subscriptionInvoicesTitle')}</h3>
                <p className="subscription-invoices-intro">{t('admin.subscriptionInvoicesIntro')}</p>
                <div className="payout-groups">
                  {visibleSubscriptionInvoices.map((inv) => {
                    const fmtDay = (key) => new Date(`${key}T00:00:00`).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' });
                    const busy = subscriptionInvoiceBusyId === inv.id;
                    return (
                      <div key={inv.id} className={`payout-group subscription-invoice ${inv.status === 'PAID' ? 'payout-group-readonly' : ''}`}>
                        <div className="payout-group-header subscription-invoice-header">
                          <div className="payout-group-name-block">
                            <h3>{inv.ownerName || t('admin.unknown')}</h3>
                            {inv.ownerEmail && <p className="payout-group-email">{inv.ownerEmail}</p>}
                            <p className="subscription-invoice-period">
                              {inv.kind === 'PRORATA' ? t('admin.subscriptionKindProrata') : t('admin.subscriptionKindQuarter')}
                              {' · '}{fmtDay(inv.periodStart)} - {fmtDay(inv.periodEnd)}
                              {' · '}{t('admin.subscriptionPropertiesCount', { count: inv.propertyCount })}
                            </p>
                          </div>
                          <div className="payout-group-total payout-group-total-inline">
                            <span className="payout-hours">€{Number(inv.amount).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            {inv.status === 'TO_INVOICE' && (
                              <button
                                type="button"
                                className="action-btn confirm-btn payout-action-btn"
                                disabled={busy}
                                onClick={() => handleSubscriptionInvoiceAction(inv, 'invoiced')}
                              >
                                {busy ? t('common.loading') : t('admin.markInvoiced')}
                              </button>
                            )}
                            {inv.status === 'INVOICED' && (
                              <>
                                <span className="subscription-invoice-status subscription-invoice-status-open">{t('admin.subscriptionAwaitingPayment')}</span>
                                <button
                                  type="button"
                                  className="action-btn confirm-btn payout-action-btn"
                                  disabled={busy}
                                  onClick={() => handleSubscriptionInvoiceAction(inv, 'paid')}
                                >
                                  {busy ? t('common.loading') : t('admin.markPaid')}
                                </button>
                              </>
                            )}
                            {inv.status === 'PAID' && (
                              <span className="subscription-invoice-status subscription-invoice-status-paid">
                                {t('admin.subscriptionPaidOn', { date: new Date(inv.paidAt).toLocaleDateString('nl-NL') })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Geen lege witte kaart tonen als er enkel abonnementsfacturen zijn. */}
            <div className="calendar-container" style={invoiceGroups.length === 0 && visibleSubscriptionInvoices.length > 0 ? { display: 'none' } : undefined}>
              {invoiceGroups.length === 0 ? (visibleSubscriptionInvoices.length > 0 ? null : (
                <div className="calendar-placeholder">
                  <div className="placeholder-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M8 2V6M16 2V6M3 10H21" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <h3>{t('admin.nothingToInvoice')}</h3>
                  <p>{t('admin.tasksAppearWhenCompleted')}</p>
                </div>
              )) : (
                <div className="payout-groups">
                  {invoiceGroups.map((group) => {
                    const hasOutstanding = group.tasks.some((task) => !task.customerInvoicedAt);
                    const isExpanded = !!expandedInvoiceGroups[group.ownerId];
                    return (
                      <div key={group.ownerId} className={`payout-group ${hasOutstanding ? '' : 'payout-group-readonly'}`}>
                        <div className="payout-group-header payout-group-header-clickable" onClick={() => toggleInvoiceGroup(group.ownerId)}>
                          <div className="payout-group-name-block">
                            <h3>{group.ownerName || t('admin.unknown')}</h3>
                            {group.ownerEmail && <p className="payout-group-email">{group.ownerEmail}</p>}
                          </div>
                          <div className="payout-group-total payout-group-total-inline" onClick={(e) => e.stopPropagation()}>
                            <span className="payout-hours">{t('admin.totalToInvoice', { amount: group.total.toLocaleString('nl-BE') })}</span>
                            {hasOutstanding ? (
                              <button
                                onClick={() => handleInvoiceOwner(group.ownerId, group.ownerName)}
                                className="action-btn confirm-btn payout-action-btn"
                                disabled={invoiceOwnerSubmittingId === group.ownerId}
                              >
                                {invoiceOwnerSubmittingId === group.ownerId ? t('common.loading') : t('admin.markInvoiced')}
                              </button>
                            ) : (
                              <span className="invoiced-badge">
                                {t('admin.alreadyInvoicedOn', { date: new Date(group.invoicedAt).toLocaleDateString('nl-NL') })}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            className={`payout-group-toggle ${isExpanded ? 'payout-group-toggle-open' : ''}`}
                            aria-label={isExpanded ? t('admin.collapseDetails') : t('admin.expandDetails')}
                          >
                            <ChevronDownIcon />
                          </button>
                        </div>
                        {isExpanded && (
                          <>
                            <div className="payout-task-table-wrap">
                              <table className="payout-task-table">
                                <thead>
                                  <tr>
                                    <th>{t('common.date')}</th>
                                    <th>{t('admin.tabProperties')}</th>
                                    <th>{t('admin.propertyTypeLabel')}</th>
                                    <th>{t('admin.cleaningServiceLabel')}</th>
                                    <th>{t('common.price')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.tasks.map((task) => (
                                    <tr key={task.id}>
                                      <td data-label={t('common.date')}>{new Date(task.scheduledAt).toLocaleDateString('nl-NL')}</td>
                                      <td data-label={t('admin.tabProperties')}>{task.property_name}</td>
                                      <td data-label={t('admin.propertyTypeLabel')}>{task.property_type ? t(`pricing.cleaningTable.${task.property_type}`) : '-'}</td>
                                      <td data-label={t('admin.cleaningServiceLabel')}>{renderTaskServiceSelect(task, !!task.customerInvoicedAt)}</td>
                                      <td data-label={t('common.price')}>{renderOverridableCell(task, 'overrideInvoicePrice', <>&euro;{task.invoicePrice}</>, task.invoicePrice)}</td>
                                    </tr>
                                  ))}
                                  <tr className="payout-total-row">
                                    <td colSpan={4}>{t('common.total')}</td>
                                    <td>€{group.total}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modals - hetzelfde als eerder */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin.inviteUserTitle')}</h2>
              <button className="modal-close" onClick={() => setShowUserModal(false)}>✖</button>
            </div>
            <form onSubmit={handleAddUser} noValidate onInput={clearFieldInvalid}>
              <div className="form-group">
                <label>{t('common.email')} *</label>
                <input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: toEmailInput(e.target.value)})} {...emailInputProps} required placeholder="naam@email.com" />
              </div>
              <div className="form-group">
                <label>{t('admin.roleLabel')} *</label>
                <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})}>
                  <option value="VERHUURDER">{t('admin.roleOwner')}</option>
                  <option value="SCHOONMAKER">{t('admin.roleCleaner')}</option>
                  <option value="ADMIN">{t('admin.roleAdmin')}</option>
                </select>
              </div>
              <div className="modal-buttons">
                <button type="button" onClick={() => setShowUserModal(false)} className="cancel-btn">{t('common.cancel')}</button>
                <button type="submit" className="submit-btn">{t('admin.sendInvite')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditUserModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowEditUserModal(false)}>
          <div className={`modal user-modal ${editingUser.role === 'SCHOONMAKER' ? 'user-modal-tabbed' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin.editUserTitle')}</h2>
              <button className="modal-close" onClick={() => setShowEditUserModal(false)}>✖</button>
            </div>
            <form onSubmit={handleEditUser} noValidate onInput={clearFieldInvalid}>
              {editingUser.role === 'SCHOONMAKER' && (
                <div className="nav-view-toggle schedule-view-toggle modal-tab-bar">
                  <button type="button" className={`nav-view-btn ${editingUserTab === 'details' ? 'active' : ''}`} onClick={() => setEditingUserTab('details')}>{t('admin.tabGeneral')}</button>
                  <button type="button" className={`nav-view-btn ${editingUserTab === 'properties' ? 'active' : ''}`} onClick={() => setEditingUserTab('properties')}>{t('admin.tabAccommodations')}</button>
                </div>
              )}
              {editingUser.role === 'SCHOONMAKER' && editingUserTab === 'properties' && (
                <div className="form-group form-group-full user-tab-body fixed">
                  <label>{t('admin.accommodationsLabel')} ({(editingUser.cleanerPropertyIds || []).length})</label>
                  <div className="property-cleaners-list">
                    {(editingUser.cleanerPropertyIds || [])
                      .map((pid) => properties.find((p) => p.id === pid))
                      .filter(Boolean)
                      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl', { sensitivity: 'base' }))
                      .map((prop) => (
                        <div key={prop.id} className="property-cleaner-card">
                          {prop.photoUrl
                            ? <img className="property-cleaner-photo" src={`${API_URL}${prop.photoUrl}`} alt="" />
                            : <div className="property-cleaner-photo property-cleaner-photo-empty">🏠</div>}
                          <div className="property-cleaner-info">
                            <span className="property-cleaner-name">{prop.name}</span>
                          </div>
                          <button type="button" onClick={() => setEditingUser({ ...editingUser, cleanerPropertyIds: (editingUser.cleanerPropertyIds || []).filter((id) => id !== prop.id) })} className="action-icon-btn action-icon-delete" title={t('common.delete')} aria-label={t('common.delete')}>
                            <TrashIcon />
                          </button>
                        </div>
                      ))}
                  </div>
                  <div className="assign-section">
                    <input
                      type="text"
                      placeholder={t('admin.addAccommodationPlaceholder')}
                      value={userPropertySearch}
                      onChange={(e) => setUserPropertySearch(e.target.value)}
                      className="search-select-input"
                    />
                    {userPropertySearch && (
                      <div className="select-dropdown">
                        {(() => {
                          const term = userPropertySearch.toLowerCase();
                          const taken = editingUser.cleanerPropertyIds || [];
                          const matches = properties.filter((p) => !taken.includes(p.id) && (p.name || '').toLowerCase().includes(term));
                          if (matches.length === 0) return <div className="dropdown-empty">{t('admin.noAccommodationsFound')}</div>;
                          return matches.map((p) => (
                            <div key={p.id} className="dropdown-item" onClick={() => { setEditingUser({ ...editingUser, cleanerPropertyIds: [...taken, p.id] }); setUserPropertySearch(''); }}>
                              {p.name}
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {!(editingUser.role === 'SCHOONMAKER' && editingUserTab === 'properties') && (<div className={`user-tab-body ${editingUser.role === 'SCHOONMAKER' ? 'fixed' : ''}`}>
              <div className="form-group">
                <label>{t('common.name')}</label>
                <input type="text" value={editingUser.name || ''} onChange={(e) => setEditingUser({...editingUser, name: e.target.value})} placeholder={t('common.name')} />
              </div>
              <div className="form-group">
                <label>{t('common.email')} *</label>
                <input type="email" value={editingUser.email} onChange={(e) => setEditingUser({...editingUser, email: toEmailInput(e.target.value)})} {...emailInputProps} required />
              </div>
              <div className="form-group">
                <label>{t('admin.roleLabel')} *</label>
                <select value={editingUser.role} onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}>
                  <option value="VERHUURDER">{t('admin.roleOwner')}</option>
                  <option value="SCHOONMAKER">{t('admin.roleCleaner')}</option>
                  <option value="ADMIN">{t('admin.roleAdmin')}</option>
                </select>
              </div>
              {(editingUser.role === 'VERHUURDER' || editingUser.role === 'SCHOONMAKER') && (
                <>
                  <div className="form-row">
                    <div className="form-group form-group-wide">
                      <label>{t('activate.straat')}</label>
                      <input
                        type="text"
                        value={editingUser.straat || ''}
                        onChange={(e) => setEditingUser({...editingUser, straat: e.target.value})}
                        placeholder={editingUser.isActive ? t('admin.notFilledInYet') : t('admin.filledInAtActivation')}
                      />
                    </div>
                    <div className="form-group form-group-narrow">
                      <label>{t('activate.huisnummer')}</label>
                      <input
                        type="text"
                        value={editingUser.huisnummer || ''}
                        onChange={(e) => setEditingUser({...editingUser, huisnummer: e.target.value})}
                        placeholder={editingUser.isActive ? t('admin.notFilledInYet') : t('admin.filledInAtActivation')}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group form-group-narrow">
                      <label>{t('activate.postcode')}</label>
                      <input
                        type="text"
                        value={editingUser.postcode || ''}
                        onChange={(e) => setEditingUser({...editingUser, postcode: e.target.value})}
                        placeholder={editingUser.isActive ? t('admin.notFilledInYet') : t('admin.filledInAtActivation')}
                      />
                    </div>
                    <div className="form-group form-group-wide">
                      <label>{t('activate.stad')}</label>
                      <input
                        type="text"
                        value={editingUser.stad || ''}
                        onChange={(e) => setEditingUser({...editingUser, stad: e.target.value})}
                        placeholder={editingUser.isActive ? t('admin.notFilledInYet') : t('admin.filledInAtActivation')}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('activate.phone')}</label>
                    <input
                      type="tel"
                      value={editingUser.phone || ''}
                      onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                      placeholder={editingUser.isActive ? t('admin.notFilledInYet') : t('admin.filledInAtActivation')}
                    />
                  </div>
                </>
              )}
              </div>)}
              <div className="modal-buttons">
                <button type="button" onClick={() => setShowEditUserModal(false)} className="cancel-btn">{t('common.cancel')}</button>
                <button type="submit" className="submit-btn">{t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddCleaningModal && (
        <div className="modal-overlay" onClick={() => setShowAddCleaningModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin.addCleaningButton')}</h2>
              <button className="modal-close" onClick={() => setShowAddCleaningModal(false)}>✖</button>
            </div>
            <form onSubmit={handleAddCleaning} noValidate onInput={clearFieldInvalid}>
              <div className="form-group">
                <label>{t('calendar.selectProperty')} *</label>
                <select
                  value={newCleaning.propertyId}
                  onChange={(e) => setNewCleaning({ ...newCleaning, propertyId: e.target.value, cleaningServiceId: '' })}
                  required
                >
                  <option value="">{t('calendar.selectPropertyPlaceholder')}</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('common.date')} *</label>
                  <input type="date" value={newCleaning.date} onChange={(e) => setNewCleaning({ ...newCleaning, date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>{t('calendar.start')} *</label>
                  <input type="time" value={newCleaning.startTime} onChange={(e) => setNewCleaning({ ...newCleaning, startTime: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>{t('calendar.end')} *</label>
                  <input type="time" value={newCleaning.endTime} onChange={(e) => setNewCleaning({ ...newCleaning, endTime: e.target.value })} required />
                </div>
              </div>

              {(() => {
                const selectedProperty = properties.find((p) => p.id === newCleaning.propertyId);
                const availableServices = selectedProperty?.cleaningServices || [];
                return availableServices.length > 0 ? (
                  <div className="form-group">
                    <label>{t('admin.cleaningServiceLabel')}</label>
                    <select
                      value={newCleaning.cleaningServiceId || availableServices[0]?.id || ''}
                      onChange={(e) => setNewCleaning({ ...newCleaning, cleaningServiceId: e.target.value })}
                    >
                      {availableServices.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                ) : null;
              })()}

              <div className="form-group">
                <label>{t('cleaner.notes')}</label>
                <textarea value={newCleaning.notes} onChange={(e) => setNewCleaning({ ...newCleaning, notes: e.target.value })} />
              </div>

              <div className="form-group">
                <label>{t('admin.cleaningServiceCleanerCountPlaceholder')}</label>
                <input type="number" min="0" value={newCleaning.cleanerCount} onChange={(e) => setNewCleaning({ ...newCleaning, cleanerCount: e.target.value })} />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddCleaningModal(false)} className="cancel-btn">{t('common.cancel')}</button>
                <button type="submit" className="submit-btn" disabled={addingCleaning}>{addingCleaning ? t('common.loading') : t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPropertyModal && (
        <div className="modal-overlay" onClick={() => setShowPropertyModal(false)}>
          <div className="modal property-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin.addPropertyTitle')}</h2>
              <button className="modal-close" onClick={() => setShowPropertyModal(false)}>✖</button>
            </div>
            <form onSubmit={handleAddProperty} noValidate onInput={clearFieldInvalid}>
              <div className="form-group">
                <label>{t('common.name')} *</label>
                <input type="text" value={newProperty.name} onChange={(e) => setNewProperty({...newProperty, name: e.target.value})} required maxLength={60} placeholder={t('admin.propertyNamePlaceholder')} />
              </div>
              <div className="form-group form-group-full">
                <label>{t('common.address')}</label>
                <AddressFields key="new-property" value={newProperty.address} onChange={(address) => setNewProperty((prev) => ({ ...prev, address }))} t={t} />
              </div>
              <div className="form-group form-group-full">
                <label>{t('admin.ownerRequired')} *</label>
                <input type="text" placeholder={t('admin.searchOwnerPlaceholder')} value={userSearchTerm} onChange={(e) => setUserSearchTerm(e.target.value)} className="search-select-input" />

                {!newProperty.ownerId && userSearchTerm && (
                  <div className="select-dropdown">
                    {filteredUsersList.length === 0 && <div className="dropdown-empty">{t('admin.noOwnersFound')}</div>}
                    {filteredUsersList.map(u => (
                      <div key={u.id} className="dropdown-item" onClick={() => {
                        setNewProperty({...newProperty, ownerId: u.id});
                        setUserSearchTerm(`${u.name || u.email}`);
                      }}>
                        <div className="dropdown-name">{u.name || t('admin.noName')}</div>
                        <div className="dropdown-email">{u.email}</div>
                      </div>
                    ))}
                  </div>
                )}

                {!newProperty.ownerId && !userSearchTerm && (
                  <div className="select-dropdown">
                    <div className="dropdown-hint">{t('admin.typeToSearch')}</div>
                  </div>
                )}

                {newProperty.ownerId && (
                  <div className="selected-info">
                    <span>{t('admin.selectedOwner', { name: usersList.find(u => u.id === newProperty.ownerId)?.name || usersList.find(u => u.id === newProperty.ownerId)?.email })}</span>
                    <button type="button" className="deselect-btn" onClick={() => {
                      setNewProperty({...newProperty, ownerId: ''});
                      setUserSearchTerm('');
                    }}>{t('common.delete')}</button>
                  </div>
                )}
              </div>

              <div className="form-group form-group-full">
                <label>{t('admin.syncSourceLabel')}</label>
                <div className="sync-source-options">
                  <label className={`sync-source-option ${newProperty.useGuesty ? 'selected' : ''}`}>
                    <input type="checkbox" checked={newProperty.useGuesty} onChange={(e) => setNewProperty(withGuestyToggle(newProperty, e.target.checked))} />
                    {t('admin.syncSourceGuesty')}
                  </label>
                  <label className={`sync-source-option ${newProperty.useHospitable ? 'selected' : ''}`}>
                    <input type="checkbox" checked={newProperty.useHospitable} onChange={(e) => setNewProperty(withHospitableToggle(newProperty, e.target.checked))} />
                    {t('admin.syncSourceHospitable')}
                  </label>
                  <label className={`sync-source-option ${newProperty.useDirect ? 'selected' : ''}`}>
                    <input type="checkbox" checked={newProperty.useDirect} onChange={(e) => setNewProperty(withDirectToggle(newProperty, e.target.checked))} />
                    {t('admin.syncSourceDirect')}
                  </label>
                  <label className={`sync-source-option ${newProperty.useSheet ? 'selected' : ''}`}>
                    <input type="checkbox" checked={newProperty.useSheet} onChange={(e) => setNewProperty(withSheetToggle(newProperty, e.target.checked))} />
                    {t('admin.syncSourceSheet')}
                  </label>
                </div>
              </div>

              {newProperty.useGuesty && (
                <>
                  <div className="form-group form-group-full">
                    <label>{t('dashboard.guestyIcalLabel')}</label>
                    <input type="url" value={newProperty.guestyIcalUrl} onChange={(e) => setNewProperty({...newProperty, guestyIcalUrl: e.target.value})} placeholder="https://app.guesty.com/api/public/ical-calendar/..." />
                  </div>

                  <div className="form-group form-group-full">
                    <label>{t('admin.guestyListingIdLabel')}</label>
                    <input type="text" value={newProperty.guestyListingId} onChange={(e) => setNewProperty({...newProperty, guestyListingId: e.target.value})} placeholder={t('admin.guestyListingIdPlaceholder')} />
                  </div>
                </>
              )}

              {newProperty.useHospitable && (
                <div className="form-group form-group-full">
                  <label>{t('dashboard.hospitableIcalLabel')}</label>
                  <input type="url" value={newProperty.hospitableIcalUrl} onChange={(e) => setNewProperty({...newProperty, hospitableIcalUrl: e.target.value})} placeholder="https://api.hospitable.com/ical/..." />
                </div>
              )}

              {newProperty.useDirect && (
                <>
                  <div className="form-group form-group-full">
                    <label>{t('admin.airbnbIcalLabel')}</label>
                    <input type="url" value={newProperty.airbnbIcalUrl} onChange={(e) => setNewProperty({...newProperty, airbnbIcalUrl: e.target.value})} placeholder="https://www.airbnb.com/calendar/ical/..." />
                  </div>
                  <div className="form-group form-group-full">
                    <label>{t('admin.bookingIcalLabel')}</label>
                    <input type="url" value={newProperty.bookingIcalUrl} onChange={(e) => setNewProperty({...newProperty, bookingIcalUrl: e.target.value})} placeholder="https://admin.booking.com/hotel/hoteladmin/ical.html?..." />
                  </div>
                </>
              )}

              {newProperty.useSheet && (
                <div className="form-group form-group-full">
                  <label>{t('admin.sheetIdLabel')}</label>
                  <input type="text" value={newProperty.bookingSheetId} onChange={(e) => setNewProperty({...newProperty, bookingSheetId: e.target.value})} />
                </div>
              )}

              <div className="form-group form-group-full property-enable-toggle">
                <label>
                  <input type="checkbox" checked={(newProperty.accessManagementEnabled === true || newProperty.automationEnabled === true)} disabled={newProperty.automationEnabled === true} onChange={(e) => setNewProperty({...newProperty, accessManagementEnabled: e.target.checked, ...(e.target.checked ? {} : { automationEnabled: false })})} />
                  {t('admin.enableAccess')}
                </label>
              </div>
              {(newProperty.accessManagementEnabled === true || newProperty.automationEnabled === true) && (
              <>
              <div className="form-group">
                <label>{t('admin.smartlockBrandLabel')}</label>
                <select value={newProperty.smartlockBrand || ''} onChange={(e) => setNewProperty({...newProperty, smartlockBrand: e.target.value})}>
                  <option value="">{t('admin.smartlockBrandNone')}</option>
                  <option value="NUKI">Nuki</option>
                  <option value="TEDEE">Tedee</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('admin.nukiIdLabel')}</label>
                <input type="text" value={newProperty.nukiSmartlockId} onChange={(e) => setNewProperty({...newProperty, nukiSmartlockId: e.target.value})} placeholder={t('admin.nukiIdPlaceholder')} />
              </div>

              <div className="form-group">
                <label>{t('admin.nukiApiTokenLabel')}</label>
                <input type="text" value={newProperty.nukiApiToken || ''} onChange={(e) => setNewProperty({...newProperty, nukiApiToken: e.target.value})} placeholder={t('admin.nukiApiTokenPlaceholder')} />
              </div>

              </>
              )}
              <div className="form-group form-group-full property-enable-toggle">
                <label>
                  <input type="checkbox" checked={(newProperty.cleaningTier || 'FULL') !== 'NONE'} disabled={false} onChange={(e) => setNewProperty({...newProperty, cleaningTier: e.target.checked ? 'FULL' : 'NONE'})} />
                  {t('admin.enableCleaning')}
                </label>
              </div>
              {(newProperty.cleaningTier || 'FULL') !== 'NONE' && (
              <>
              <div className="form-group">
                <label>{t('admin.cleaningManagedLabel')}</label>
                <select value={newProperty.cleaningTier} onChange={(e) => setNewProperty({...newProperty, cleaningTier: e.target.value})}>
                  <option value="FULL">{t('admin.tierFull')}</option>
                  <option value="PARTIAL">{t('admin.tierPartial')}</option>
                  <option value="NONE">{t('admin.tierNone')}</option>
                </select>
              </div>

              {newProperty.cleaningTier === 'PARTIAL' && (
                <div className="form-group">
                  <label>{t('admin.occasionalRangeLabel')}</label>
                  <OccasionalRangesEditor
                    ranges={newProperty.occasionalCleaningRanges}
                    onChange={(ranges) => setNewProperty({...newProperty, occasionalCleaningRanges: ranges})}
                    t={t}
                  />
                </div>
              )}

              <div className="form-group">
                <label>{t('admin.propertyTypeLabel')}</label>
                <select value={newProperty.propertyType} onChange={(e) => setNewProperty({...newProperty, propertyType: e.target.value})}>
                  <option value="">{t('admin.propertyTypeNone')}</option>
                  <option value="hotelRoom">{t('pricing.cleaningTable.hotelRoom')}</option>
                  <option value="type2">{t('pricing.cleaningTable.type2')}</option>
                  <option value="type3">{t('pricing.cleaningTable.type3')}</option>
                  <option value="type4">{t('pricing.cleaningTable.type4')}</option>
                  <option value="type5">{t('pricing.cleaningTable.type5')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('admin.fixedPriceLabel')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newProperty.fixedCleaningPrice}
                  onChange={(e) => setNewProperty({...newProperty, fixedCleaningPrice: e.target.value})}
                  placeholder={t('admin.fixedPricePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('admin.cleaningHoursLabel')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  value={newProperty.cleaningHours}
                  onChange={(e) => setNewProperty({...newProperty, cleaningHours: e.target.value})}
                  placeholder={t('admin.cleaningHoursPlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('admin.cleanerHourlyRateLabel')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newProperty.cleanerHourlyRate}
                  onChange={(e) => setNewProperty({...newProperty, cleanerHourlyRate: e.target.value})}
                  placeholder={t('admin.cleanerHourlyRatePlaceholder')}
                />
              </div>

              <div className="form-group form-group-full">
                <label>{t('admin.cleaningWindowLabel')}</label>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-subgroup-label">{t('admin.cleaningWindowStartLabel')}</label>
                    <input
                      type="time"
                      value={newProperty.cleaningWindowStart}
                      onChange={(e) => setNewProperty({...newProperty, cleaningWindowStart: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-subgroup-label">{t('admin.cleaningWindowEndLabel')}</label>
                    <input
                      type="time"
                      value={newProperty.cleaningWindowEnd}
                      onChange={(e) => setNewProperty({...newProperty, cleaningWindowEnd: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group form-group-full">
                <label>{t('admin.extraInfoLabel')}</label>
                <textarea
                  rows={2}
                  value={newProperty.cleaningHoursNote}
                  onChange={(e) => setNewProperty({...newProperty, cleaningHoursNote: e.target.value})}
                  placeholder={t('admin.extraInfoPlaceholder')}
                />
              </div>

              </>
              )}
              <div className="form-group form-group-full property-enable-toggle">
                <label>
                  <input type="checkbox" checked={newProperty.automationEnabled === true} disabled={false} onChange={(e) => setNewProperty({...newProperty, automationEnabled: e.target.checked, ...(e.target.checked ? { accessManagementEnabled: true } : {})})} />
                  {t('admin.enableAutomation')}
                </label>
              </div>
              {newProperty.automationEnabled === true && (
              <>
              <div className="form-group">
                <label>{t('admin.automationMonthlyPriceLabel')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newProperty.automationMonthlyPrice}
                  onChange={(e) => setNewProperty({...newProperty, automationMonthlyPrice: e.target.value})}
                />
              </div>

              </>
              )}

              <div className="modal-buttons">
                <button type="button" onClick={() => setShowPropertyModal(false)} className="cancel-btn">{t('common.cancel')}</button>
                <button type="submit" className="submit-btn">{t('admin.addAndSync')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditPropertyModal && editingProperty && (
        <div className="modal-overlay" onClick={() => setShowEditPropertyModal(false)}>
          <div className="modal property-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('dashboard.editPropertyTitle')}</h2>
              <button className="modal-close" onClick={() => setShowEditPropertyModal(false)}>✖</button>
            </div>
            <div className="nav-view-toggle schedule-view-toggle modal-tab-bar">
              <button type="button" className={`nav-view-btn ${editPropertyTab === 'general' ? 'active' : ''}`} onClick={() => setEditPropertyTab('general')}>{t('admin.tabGeneral')}</button>
              <button type="button" className={`nav-view-btn ${editPropertyTab === 'access' ? 'active' : ''}`} onClick={() => setEditPropertyTab('access')}>{t('admin.tabAccessManagement')}</button>
              <button type="button" className={`nav-view-btn ${editPropertyTab === 'cleaning' ? 'active' : ''}`} onClick={() => setEditPropertyTab('cleaning')}>{t('admin.tabCleaning')}</button>
              <button type="button" className={`nav-view-btn ${editPropertyTab === 'automation' ? 'active' : ''}`} onClick={() => setEditPropertyTab('automation')}>{t('admin.tabAutomation')}</button>
            </div>
            <form id="edit-property-form" onSubmit={handleEditProperty} noValidate onInput={clearFieldInvalid}>
              {editPropertyTab === 'general' && (
              <>
              <div className="modal-section">
              <h3 className="modal-section-title">{t('admin.sectionBasics')}</h3>
              <div className="form-group">
                <label>{t('common.name')} *</label>
                <input type="text" value={editingProperty.name} onChange={(e) => setEditingProperty({...editingProperty, name: e.target.value})} required maxLength={60} />
              </div>
              <div className="form-group form-group-full">
                <label>{t('common.address')}</label>
                <AddressFields key={editingProperty.id} value={editingProperty.address || ''} onChange={(address) => setEditingProperty((prev) => ({ ...prev, address }))} t={t} />
              </div>
              <div className="form-group">
                <label>{t('admin.ownerRequired')} *</label>
                <input
                  type="text"
                  placeholder={t('admin.searchOwnerPlaceholder')}
                  value={editOwnerSearchTerm}
                  onChange={(e) => {
                    setEditOwnerSearchTerm(e.target.value);
                    // Zodra er opnieuw getypt wordt, is de vorige selectie niet
                    // meer geldig totdat er weer expliciet op een resultaat
                    // geklikt wordt - anders zou de oude ownerId onopgemerkt
                    // blijven staan terwijl de tekst iets anders toont.
                    setEditingProperty((prev) => ({ ...prev, ownerId: '' }));
                  }}
                  className="search-select-input"
                />

                {!editingProperty.ownerId && editOwnerSearchTerm && (
                  <div className="select-dropdown">
                    {usersList.filter(u =>
                      u.name?.toLowerCase().includes(editOwnerSearchTerm.toLowerCase()) ||
                      u.email?.toLowerCase().includes(editOwnerSearchTerm.toLowerCase())
                    ).length === 0 && <div className="dropdown-empty">{t('admin.noOwnersFound')}</div>}
                    {usersList.filter(u =>
                      u.name?.toLowerCase().includes(editOwnerSearchTerm.toLowerCase()) ||
                      u.email?.toLowerCase().includes(editOwnerSearchTerm.toLowerCase())
                    ).map(u => (
                      <div key={u.id} className="dropdown-item" onClick={() => {
                        setEditingProperty((prev) => ({ ...prev, ownerId: u.id }));
                        setEditOwnerSearchTerm(formatOwnerLabel(u.name, u.email));
                      }}>
                        {formatOwnerLabel(u.name || t('admin.noName'), u.email)}
                      </div>
                    ))}
                  </div>
                )}

                {!editingProperty.ownerId && !editOwnerSearchTerm && (
                  <div className="select-dropdown">
                    <div className="dropdown-hint">{t('admin.typeToSearch')}</div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>{t('admin.propertyTypeLabel')}</label>
                <select value={editingProperty.propertyType || ''} onChange={(e) => setEditingProperty({...editingProperty, propertyType: e.target.value})}>
                  <option value="">{t('admin.propertyTypeNone')}</option>
                  <option value="hotelRoom">{t('pricing.cleaningTable.hotelRoom')}</option>
                  <option value="type2">{t('pricing.cleaningTable.type2')}</option>
                  <option value="type3">{t('pricing.cleaningTable.type3')}</option>
                  <option value="type4">{t('pricing.cleaningTable.type4')}</option>
                  <option value="type5">{t('pricing.cleaningTable.type5')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('admin.checkInTimeLabel')}</label>
                <input type="time" value={editingProperty.checkInTime || ''} onChange={(e) => setEditingProperty({...editingProperty, checkInTime: e.target.value})} />
              </div>
              <div className="form-group">
                <label>{t('admin.checkOutTimeLabel')}</label>
                <input type="time" value={editingProperty.checkOutTime || ''} onChange={(e) => setEditingProperty({...editingProperty, checkOutTime: e.target.value})} />
              </div>
              <p className="form-hint form-group-full">{t('admin.checkTimesHint')}</p>

              </div>

              <div className="modal-section">
              <h3 className="modal-section-title">{t('admin.sectionMedia')}</h3>
              <div className="form-group form-group-full">
                <label>{t('admin.photoLabel')}</label>

                {photoError && (
                  <div className="checklist-modal-error">{photoError}</div>
                )}

                {editingProperty.photoUrl ? (
                  <div className="property-photo-current">
                    <img src={`${API_URL}${editingProperty.photoUrl}`} alt="" className="property-photo-preview" />
                    <button type="button" className="action-btn" onClick={handleDeletePhoto}>
                      {t('common.delete')}
                    </button>
                  </div>
                ) : (
                  <p className="empty-state-text">{t('admin.photoEmpty')}</p>
                )}

                <label className="submit-btn" style={{ display: 'inline-block', width: 'auto', cursor: photoUploading ? 'wait' : 'pointer', opacity: photoUploading ? 0.7 : 1 }}>
                  {photoUploading ? t('admin.photoUploading') : (editingProperty.photoUrl ? t('admin.photoReplace') : t('admin.photoUpload'))}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleUploadPhoto}
                    disabled={photoUploading}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
              </div>

              <div className="modal-section">
              <h3 className="modal-section-title">{t('admin.sectionBookings')}</h3>
              <div className="form-group form-group-full">
                <label>{t('admin.syncSourceLabel')}</label>
                <div className="sync-source-options">
                  <label className={`sync-source-option ${editingProperty.useGuesty ? 'selected' : ''}`}>
                    <input type="checkbox" checked={!!editingProperty.useGuesty} onChange={(e) => setEditingProperty(withGuestyToggle(editingProperty, e.target.checked))} />
                    {t('admin.syncSourceGuesty')}
                  </label>
                  <label className={`sync-source-option ${editingProperty.useHospitable ? 'selected' : ''}`}>
                    <input type="checkbox" checked={!!editingProperty.useHospitable} onChange={(e) => setEditingProperty(withHospitableToggle(editingProperty, e.target.checked))} />
                    {t('admin.syncSourceHospitable')}
                  </label>
                  <label className={`sync-source-option ${editingProperty.useDirect ? 'selected' : ''}`}>
                    <input type="checkbox" checked={!!editingProperty.useDirect} onChange={(e) => setEditingProperty(withDirectToggle(editingProperty, e.target.checked))} />
                    {t('admin.syncSourceDirect')}
                  </label>
                  <label className={`sync-source-option ${editingProperty.useSheet ? 'selected' : ''}`}>
                    <input type="checkbox" checked={!!editingProperty.useSheet} onChange={(e) => setEditingProperty(withSheetToggle(editingProperty, e.target.checked))} />
                    {t('admin.syncSourceSheet')}
                  </label>
                </div>
                {editingProperty.vrboIcalUrl && (
                  <p className="info-text ical-legacy-note">{t('admin.syncSourceLegacyNote')}</p>
                )}
              </div>

              {editingProperty.useGuesty && (
                <>
                  <div className="form-group form-group-full">
                    <label>{t('dashboard.guestyIcalLabel')}</label>
                    <input type="url" value={editingProperty.guestyIcalUrl || ''} onChange={(e) => setEditingProperty({...editingProperty, guestyIcalUrl: e.target.value})} placeholder="https://app.guesty.com/api/public/ical-calendar/..." />
                  </div>

                  <div className="form-group form-group-full">
                    <label>{t('admin.guestyListingIdLabel')}</label>
                    <input type="text" value={editingProperty.guestyListingId || ''} onChange={(e) => setEditingProperty({...editingProperty, guestyListingId: e.target.value})} placeholder={t('admin.guestyListingIdPlaceholder')} />
                  </div>
                </>
              )}

              {editingProperty.useHospitable && (
                <div className="form-group form-group-full">
                  <label>{t('dashboard.hospitableIcalLabel')}</label>
                  <input type="url" value={editingProperty.hospitableIcalUrl || ''} onChange={(e) => setEditingProperty({...editingProperty, hospitableIcalUrl: e.target.value})} placeholder="https://api.hospitable.com/ical/..." />
                </div>
              )}

              {editingProperty.useDirect && (
                <>
                  <div className="form-group form-group-full">
                    <label>{t('admin.airbnbIcalLabel')}</label>
                    <input type="url" value={editingProperty.airbnbIcalUrl || ''} onChange={(e) => setEditingProperty({...editingProperty, airbnbIcalUrl: e.target.value})} placeholder="https://www.airbnb.com/calendar/ical/..." />
                  </div>
                  <div className="form-group form-group-full">
                    <label>{t('admin.bookingIcalLabel')}</label>
                    <input type="url" value={editingProperty.bookingIcalUrl || ''} onChange={(e) => setEditingProperty({...editingProperty, bookingIcalUrl: e.target.value})} placeholder="https://admin.booking.com/hotel/hoteladmin/ical.html?..." />
                  </div>
                </>
              )}

              {editingProperty.useSheet && (
                <div className="form-group form-group-full">
                  <label>{t('admin.sheetIdLabel')}</label>
                  <input type="text" value={editingProperty.bookingSheetId || ''} onChange={(e) => setEditingProperty({...editingProperty, bookingSheetId: e.target.value})} />
                </div>
              )}
              </div>

              <div className="modal-section">
              <h3 className="modal-section-title">{t('admin.extraInfoLabel')}</h3>
              <div className="form-group form-group-full">
                <label>{t('admin.extraInfoLabel')}</label>
                <textarea
                  rows={2}
                  value={editingProperty.cleaningHoursNote || ''}
                  onChange={(e) => setEditingProperty({...editingProperty, cleaningHoursNote: e.target.value})}
                  placeholder={t('admin.extraInfoPlaceholder')}
                />
              </div>
              </div>
              </>
              )}

              {editPropertyTab === 'access' && (
              <>
              <div className="form-group form-group-checkbox property-enable-toggle">
                <label>
                  <input type="checkbox" checked={editingProperty.accessManagementEnabled === true || editingProperty.automationEnabled === true} disabled={editingProperty.automationEnabled === true} onChange={(e) => setEditingProperty({...editingProperty, accessManagementEnabled: e.target.checked, ...(e.target.checked ? {} : { automationEnabled: false })})} />
                  {t('admin.enableAccess')}
                </label>
              </div>
              {(editingProperty.accessManagementEnabled === true || editingProperty.automationEnabled === true) && (
              <div className="modal-section access-subscription-section">
                <h3 className="modal-section-title">{t('admin.accessSubscriptionTitle')}</h3>
                {editingProperty.ownerHasAutomation ? (
                  <p className="access-subscription-hint">{t('admin.accessFreeWithAutomation')}</p>
                ) : (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>{t('admin.accessStartDate')}</label>
                        <input
                          type="date"
                          value={editingProperty.ownerAccessStart || ''}
                          onChange={(e) => setEditingProperty({
                            ...editingProperty,
                            ownerAccessStart: e.target.value,
                          })}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('admin.accessEndDate')}</label>
                        <input
                          type="date"
                          value={editingProperty.ownerAccessEnd || ''}
                          min={editingProperty.ownerAccessStart || undefined}
                          onChange={(e) => setEditingProperty({ ...editingProperty, ownerAccessEnd: e.target.value })}
                        />
                      </div>
                    </div>
                    <p className="access-subscription-hint">{t('admin.accessDatesHint')}</p>
                  </>
                )}
              </div>
              )}
              {(editingProperty.accessManagementEnabled === true || editingProperty.automationEnabled === true) && (
              <div className="modal-section">
              <h3 className="modal-section-title">{t('admin.sectionSmartlock')}</h3>
              <div className="form-group">
                <label>{t('admin.smartlockBrandLabel')}</label>
                <select value={editingProperty.smartlockBrand || ''} onChange={(e) => setEditingProperty({...editingProperty, smartlockBrand: e.target.value})}>
                  <option value="">{t('admin.smartlockBrandNone')}</option>
                  <option value="NUKI">Nuki</option>
                  <option value="TEDEE">Tedee</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('admin.nukiIdLabel')}</label>
                <input type="text" value={editingProperty.nukiSmartlockId || ''} onChange={(e) => setEditingProperty({...editingProperty, nukiSmartlockId: e.target.value})} placeholder={t('admin.nukiIdPlaceholder')} />
              </div>

              <div className="form-group">
                <label>{t('admin.nukiApiTokenLabel')}</label>
                <input type="text" value={editingProperty.nukiApiToken || ''} onChange={(e) => setEditingProperty({...editingProperty, nukiApiToken: e.target.value})} placeholder={t('admin.nukiApiTokenPlaceholder')} />
              </div>

              <div className="form-group form-group-full">
                <label className="toggle-switch-row">
                  <span className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={editingProperty.autoAccessCodeEmailsEnabled === true}
                      onChange={(e) => setEditingProperty({...editingProperty, autoAccessCodeEmailsEnabled: e.target.checked})}
                    />
                    <span className="toggle-switch-slider"></span>
                  </span>
                  {t('admin.autoAccessCodeEmailsLabel')}
                </label>
              </div>

              </div>
              )}
              </>
              )}

              {editPropertyTab === 'cleaning' && (
              <>
              <div className="form-group form-group-checkbox property-enable-toggle">
                <label>
                  <input type="checkbox" checked={(editingProperty.cleaningTier || 'FULL') !== 'NONE'} disabled={false} onChange={(e) => setEditingProperty({...editingProperty, cleaningTier: e.target.checked ? 'FULL' : 'NONE'})} />
                  {t('admin.enableCleaning')}
                </label>
              </div>
              {(editingProperty.cleaningTier || 'FULL') !== 'NONE' && (
              <>
              <div className="modal-section">
              <h3 className="modal-section-title">{t('admin.sectionCleaningInstructions')}</h3>
              <div className="form-group form-group-full">
                <label>{t('admin.cleaningPdfLabel')}</label>

                {cleaningPdfError && (
                  <div className="checklist-modal-error">{cleaningPdfError}</div>
                )}

                <div className="cleaning-pdf-row">
                {editingProperty.cleaningPdfOriginalName ? (
                  <div className="cleaning-pdf-current">
                    <span className="cleaning-pdf-filename">📄 {editingProperty.cleaningPdfOriginalName}</span>
                    <div className="checklist-manage-item-actions">
                      <button type="button" className="action-btn" style={{ color: 'var(--primary)' }} onClick={handleViewCleaningPdf}>
                        {t('admin.cleaningPdfView')}
                      </button>
                      <button type="button" className="action-btn" onClick={handleDeleteCleaningPdf}>
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="empty-state-text">{t('admin.cleaningPdfEmpty')}</p>
                )}

                <label className="submit-btn" style={{ display: 'inline-block', width: 'auto', margin: 0, whiteSpace: 'nowrap', cursor: cleaningPdfUploading ? 'wait' : 'pointer', opacity: cleaningPdfUploading ? 0.7 : 1 }}>
                  {cleaningPdfUploading ? t('admin.cleaningPdfUploading') : (editingProperty.cleaningPdfOriginalName ? t('admin.cleaningPdfReplace') : t('admin.cleaningPdfUpload'))}
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleUploadCleaningPdf}
                    disabled={cleaningPdfUploading}
                    style={{ display: 'none' }}
                  />
                </label>
                </div>
              </div>
              </div>

              <div className="modal-section">
              <h3 className="modal-section-title">{t('admin.sectionCleaning')}</h3>
              <div className="form-group">
                <label>{t('admin.cleaningManagedLabel')}</label>
                <select value={editingProperty.cleaningTier || 'FULL'} onChange={(e) => setEditingProperty({...editingProperty, cleaningTier: e.target.value})}>
                  <option value="FULL">{t('admin.tierFull')}</option>
                  <option value="PARTIAL">{t('admin.tierPartial')}</option>
                  <option value="NONE">{t('admin.tierNone')}</option>
                </select>
              </div>

              {editingProperty.cleaningTier === 'PARTIAL' && (
                <div className="form-group">
                  <label>{t('admin.occasionalRangeLabel')}</label>
                  <OccasionalRangesEditor
                    ranges={editingProperty.occasionalCleaningRanges}
                    onChange={(ranges) => setEditingProperty({...editingProperty, occasionalCleaningRanges: ranges})}
                    t={t}
                  />
                </div>
              )}

              <div className="form-group form-group-full">
                <label>{t('admin.cleaningServicesLabel')}</label>
                {(editingProperty.cleaningServices || []).length > 0 && (
                  <div className="cleaning-service-row cleaning-service-header-row">
                    <span className="cleaning-service-name-input">{t('admin.cleaningServiceNamePlaceholder')}</span>
                    <span className="cleaning-service-number-input">{t('admin.cleaningServicePricePlaceholder')}</span>
                    <span className="cleaning-service-number-input">{t('admin.cleaningServiceHoursPlaceholder')}</span>
                    <span className="cleaning-service-number-input">{t('admin.cleaningServiceRatePlaceholder')}</span>
                    <span className="cleaning-service-number-input">{t('admin.cleaningServiceCleanerCountPlaceholder')}</span>
                    <span className="cleaning-service-number-input">{t('admin.cleaningServiceMaxCleanerCountPlaceholder')}</span>
                    <span className="cleaning-service-number-input">{t('admin.cleaningWindowStartLabel')}</span>
                    <span className="cleaning-service-number-input">{t('admin.cleaningWindowEndLabel')}</span>
                  </div>
                )}
                <div className="default-cleaners-list">
                  {(editingProperty.cleaningServices || []).map((service) => (
                    <div key={service.id} className="cleaning-service-row">
                      <label className="cleaning-service-field" data-label={t('admin.cleaningServiceNamePlaceholder')}>
<input
                        type="text"
                        value={service.name || ''}
                        onChange={(e) => updateCleaningServiceField(service.id, 'name', e.target.value)}
                        placeholder={t('admin.cleaningServiceNamePlaceholder')}
                        className="cleaning-service-name-input"
                      />
                      </label>
                      <label className="cleaning-service-field" data-label={t('admin.cleaningServicePricePlaceholder')}>
<input
                        type="number"
                        min="0"
                        value={service.price ?? ''}
                        onChange={(e) => updateCleaningServiceField(service.id, 'price', e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder={t('admin.cleaningServicePricePlaceholder')}
                        className="cleaning-service-number-input"
                      />
                      </label>
                      <label className="cleaning-service-field" data-label={t('admin.cleaningServiceHoursPlaceholder')}>
<input
                        type="number"
                        min="0"
                        step="0.25"
                        value={service.hours ?? ''}
                        onChange={(e) => updateCleaningServiceField(service.id, 'hours', e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder={t('admin.cleaningServiceHoursPlaceholder')}
                        className="cleaning-service-number-input"
                      />
                      </label>
                      <label className="cleaning-service-field" data-label={t('admin.cleaningServiceRatePlaceholder')}>
<input
                        type="number"
                        min="0"
                        step="0.5"
                        value={service.hourlyRate ?? ''}
                        onChange={(e) => updateCleaningServiceField(service.id, 'hourlyRate', e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder={t('admin.cleaningServiceRatePlaceholder')}
                        className="cleaning-service-number-input"
                      />
                      </label>
                      <label className="cleaning-service-field" data-label={t('admin.cleaningServiceCleanerCountPlaceholder')}>
<input
                        type="number"
                        min="1"
                        step="1"
                        value={service.cleanerCount ?? ''}
                        onChange={(e) => updateCleaningServiceField(service.id, 'cleanerCount', e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder={t('admin.cleaningServiceCleanerCountPlaceholder')}
                        title={t('admin.cleaningServiceMinCleanerCountHint')}
                        className="cleaning-service-number-input"
                      />
                      </label>
                      <label className="cleaning-service-field" data-label={t('admin.cleaningServiceMaxCleanerCountPlaceholder')}>
<input
                        type="number"
                        min="1"
                        max="3"
                        step="1"
                        value={service.maxCleanerCount ?? ''}
                        onChange={(e) => updateCleaningServiceField(service.id, 'maxCleanerCount', e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder={t('admin.cleaningServiceMaxCleanerCountPlaceholder')}
                        title={t('admin.cleaningServiceMaxCleanerCountHint')}
                        className="cleaning-service-number-input"
                      />
                      </label>
                      <label className="cleaning-service-field" data-label={t('admin.cleaningWindowStartLabel')}>
<input
                        type="time"
                        value={service.windowStart || ''}
                        onChange={(e) => updateCleaningServiceField(service.id, 'windowStart', e.target.value)}
                        className="cleaning-service-number-input"
                      />
                      </label>
                      <label className="cleaning-service-field" data-label={t('admin.cleaningWindowEndLabel')}>
<input
                        type="time"
                        value={service.windowEnd || ''}
                        onChange={(e) => updateCleaningServiceField(service.id, 'windowEnd', e.target.value)}
                        className="cleaning-service-number-input"
                      />
                      </label>
                      <button
                        type="button"
                        onClick={() => removeCleaningService(service.id)}
                        className="action-icon-btn action-icon-delete"
                        title={t('common.delete')}
                        aria-label={t('common.delete')}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addCleaningService} className="secondary-btn">
                  + {t('admin.addCleaningServiceButton')}
                </button>
              </div>

              <div className="form-group form-group-full">
                <label>{t('admin.cleanersLabel')}</label>
                <div className="property-cleaners-list">
                  {(editingProperty.defaultCleanerIds || []).length === 0 && (
                    <p className="info-text">{t('admin.cleanersNone')}</p>
                  )}
                  {[...(editingProperty.defaultCleanerIds || [])]
                    .map((cleanerId) => ({ id: cleanerId, user: users.find((u) => u.id === cleanerId) }))
                    .sort((x, y) => (x.user?.name || x.user?.email || '').localeCompare(y.user?.name || y.user?.email || '', 'nl', { sensitivity: 'base' }))
                    .map(({ id: cleanerId, user: cleaner }) => (
                      <div key={cleanerId} className="property-cleaner-card">
                        <Avatar url={cleaner?.photoUrl} name={cleaner?.name || cleaner?.email || '?'} size={36} />
                        <div className="property-cleaner-info">
                          <span className="property-cleaner-name">{cleaner ? (cleaner.name || t('admin.noName')) : cleanerId}</span>
                          {cleaner?.email && <span className="property-cleaner-email">{cleaner.email}</span>}
                        </div>
                        <button type="button" onClick={() => removeDefaultCleaner(cleanerId)} className="action-icon-btn action-icon-delete" title={t('common.delete')} aria-label={t('common.delete')}>
                          <TrashIcon />
                        </button>
                      </div>
                    ))}
                </div>
                <div className="assign-section">
                  <input
                    type="text"
                    placeholder={t('admin.addCleanerPlaceholder')}
                    value={defaultCleanerSearch}
                    onChange={(e) => setDefaultCleanerSearch(e.target.value)}
                    className="search-select-input"
                  />
                  {defaultCleanerSearch && (
                    <div className="select-dropdown">
                      {(() => {
                        const term = defaultCleanerSearch.toLowerCase();
                        const takenIds = editingProperty.defaultCleanerIds || [];
                        const matches = users.filter((u) =>
                          u.role === 'SCHOONMAKER' && u.isActive && !takenIds.includes(u.id) &&
                          (u.name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term))
                        );
                        if (matches.length === 0) {
                          return <div className="dropdown-empty">{t('admin.noCleanersFound')}</div>;
                        }
                        return matches.map((c) => (
                          <div key={c.id} className="dropdown-item property-cleaner-option" onClick={() => addDefaultCleaner(c.id)}>
                            <Avatar url={c.photoUrl} name={c.name || c.email} size={24} />
                            <span>{formatOwnerLabel(c.name || t('admin.noName'), c.email)}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              </div>

              </div>
              </>
              )}
              </>
              )}

              {editPropertyTab === 'automation' && (
              <>
              <div className="form-group form-group-checkbox property-enable-toggle">
                <label>
                  <input type="checkbox" checked={editingProperty.automationEnabled === true} disabled={false} onChange={(e) => setEditingProperty({...editingProperty, automationEnabled: e.target.checked, ...(e.target.checked ? { accessManagementEnabled: true } : {})})} />
                  {t('admin.enableAutomation')}
                </label>
              </div>
              {editingProperty.automationEnabled === true && (
              <div className="modal-section">
              <h3 className="modal-section-title">{t('admin.sectionAutomation')}</h3>
              </div>
              )}
              </>
              )}

            </form>
            <div className="modal-buttons property-modal-footer">
              <button type="button" onClick={() => setShowEditPropertyModal(false)} className="cancel-btn">{t('common.cancel')}</button>
              <button type="submit" form="edit-property-form" className="submit-btn">{t('admin.saveAndSync')}</button>
            </div>
          </div>
        </div>
      )}

      {showEditTaskModal && editingTask && (
        <div className="modal-overlay" onClick={() => setShowEditTaskModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin.taskEditTitle')}</h2>
              <button className="modal-close" onClick={() => setShowEditTaskModal(false)}>✖</button>
            </div>
            <form onSubmit={handleEditTask}>
              <div className="modal-section">
                <p className="info-text">{editingTask.property_name}</p>
                <div className="form-group">
                  <label>{t('admin.taskEditStart')}</label>
                  <input
                    type="datetime-local"
                    value={editingTask.startDateInput}
                    onChange={(e) => setEditingTask({ ...editingTask, startDateInput: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.taskEditEnd')}</label>
                  <input
                    type="datetime-local"
                    value={editingTask.endDateInput}
                    onChange={(e) => setEditingTask({ ...editingTask, endDateInput: e.target.value })}
                    required
                  />
                </div>
                {(() => {
                  // Bestaande (ongeplande) boekingen van dezelfde woning, plus -
                  // indien van toepassing - de boeking die nu al aan deze taak
                  // hangt (die staat zelf niet meer tussen de "ongeplande"
                  // boekingen, want die is nu net wél al ingepland). Zo kan de
                  // admin een losstaande schoonmaak (zonder boeking, bv.
                  // handmatig toegevoegd) alsnog aan een boeking koppelen, of
                  // net ontkoppelen/naar een andere boeking omzetten.
                  const samePropertyBookings = unscheduledBookings.filter((b) => b.propertyId === editingTask.propertyId);
                  const currentBookingStillUnscheduled = samePropertyBookings.some((b) => b.id === editingTask.bookingIdInput);
                  const currentBookingOption = (editingTask.bookingIdInput && !currentBookingStillUnscheduled && editingTask.booking_check_in)
                    ? { id: editingTask.bookingIdInput, checkIn: editingTask.booking_check_in, checkOut: editingTask.booking_check_out, guestName: editingTask.guest_name }
                    : null;
                  const bookingOptions = [...(currentBookingOption ? [currentBookingOption] : []), ...samePropertyBookings];
                  const formatBookingLabel = (b) => {
                    const range = `${new Date(b.checkIn).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} - ${new Date(b.checkOut).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}`;
                    return b.guestName ? `${range} (${b.guestName})` : range;
                  };
                  return (
                    <div className="form-group">
                      <label>{t('admin.taskLinkBookingLabel')}</label>
                      <select
                        value={editingTask.bookingIdInput}
                        onChange={(e) => setEditingTask({ ...editingTask, bookingIdInput: e.target.value })}
                      >
                        <option value="">{t('admin.taskLinkBookingNone')}</option>
                        {bookingOptions.map((b) => (
                          <option key={b.id} value={b.id}>{formatBookingLabel(b)}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}
                {(() => {
                  const taskPropRecord = properties.find((p) => p.id === editingTask.propertyId);
                  const taskServices = taskPropRecord?.cleaningServices || [];
                  if (taskServices.length === 0) return null;
                  return (
                    <div className="form-group">
                      <label>{t('admin.cleaningServiceLabel')}</label>
                      <select
                        value={editingTask.cleaningServiceIdInput}
                        onChange={(e) => setEditingTask({ ...editingTask, cleaningServiceIdInput: e.target.value })}
                      >
                        {editingTask.cleaningServiceIdInput === '' && (
                          <option value="">{t('admin.cleaningServiceNoneChosen')}</option>
                        )}
                        {taskServices.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}
                <div className="form-group">
                  <label>{t('admin.invoicePriceLabel')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingTask.invoicePriceInput !== '' ? editingTask.invoicePriceInput : getTaskEditDefaults(editingTask).price}
                    onChange={(e) => setEditingTask({ ...editingTask, invoicePriceInput: e.target.value })}
                    placeholder={t('admin.overrideDefaultPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.payoutAmountLabel')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingTask.payoutAmountInput !== '' ? editingTask.payoutAmountInput : getTaskEditDefaults(editingTask).payout}
                    onChange={(e) => setEditingTask({ ...editingTask, payoutAmountInput: e.target.value })}
                    placeholder={t('admin.overrideDefaultPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.taskEditExpectedCleaners')}</label>
                  <input
                    type="number"
                    min="1"
                    value={editingTask.expectedCleanerCountInput !== '' ? editingTask.expectedCleanerCountInput : getTaskEditDefaults(editingTask).count}
                    onChange={(e) => setEditingTask({ ...editingTask, expectedCleanerCountInput: e.target.value })}
                    placeholder={t('admin.taskEditExpectedCleanersPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('cleaner.notes')}</label>
                  <textarea
                    value={editingTask.notesInput}
                    onChange={(e) => setEditingTask({ ...editingTask, notesInput: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>
              <div className="modal-buttons">
                <button type="button" onClick={() => setShowEditTaskModal(false)} className="cancel-btn">{t('common.cancel')}</button>
                <button type="submit" className="submit-btn">{t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin.confirmDeleteTitle')}</h2>
            </div>
            <p>{t('admin.confirmDeleteText', { name: deleteConfirm.name })}</p>
            <p className="delete-warning">{t('admin.cannotUndo')}</p>
            <div className="modal-buttons">
              <button onClick={() => setDeleteConfirm(null)} className="cancel-btn">{t('common.cancel')}</button>
              <button onClick={() => deleteConfirm.type === 'user' ? handleDeleteUser(deleteConfirm.id, deleteConfirm.name) : handleDeleteProperty(deleteConfirm.id, deleteConfirm.name)} className="delete-confirm-btn">{t('common.delete')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Generiek bevestigingsmodaltje i.p.v. window.confirm() - askConfirm()
          hierboven vult confirmState.message/onConfirm; de knop voert
          onConfirm uit en sluit het modaltje. */}
      {confirmState && (
        <div className="modal-overlay" onClick={() => setConfirmState(null)}>
          <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('common.confirm')}</h2>
              <button className="modal-close" onClick={() => setConfirmState(null)}>✖</button>
            </div>
            <div className="modal-body">
              <p style={{ whiteSpace: 'pre-line' }}>{confirmState.message}</p>
            </div>
            <div className="modal-buttons">
              <button type="button" className="cancel-btn" onClick={() => setConfirmState(null)}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="submit-btn"
                onClick={() => { const { onConfirm } = confirmState; setConfirmState(null); onConfirm(); }}
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isNative && createPortal(
        <nav className="native-tabbar admin-native-tabbar">
          {nativeAdminTabs.map((tab, tabIndex) => (
            <button
              key={tab.key}
              type="button"
              className={`native-tab ${tab.active && nativeAdminTabs.findIndex((x) => x.active) === tabIndex ? 'active' : ''}`}
              aria-label={tab.label}
              onClick={tab.onClick}
            >
              {tab.icon}
              {tab.badge > 0 && <span className="native-tab-badge">{tab.badge > 99 ? '99+' : tab.badge}</span>}
            </button>
          ))}
        </nav>,
        document.body
      )}

    </div>
  );
}

export default Admin;
